import React, { useEffect, useMemo, useState } from "react";
import { stalenessTitle, type FiProfileStaleness } from "@lib/fiProfileStaleness";
import Layout from "@components/Layout";
import { API_BASE_URL } from "@lib/config";
import { parsePeakFreshness, peakFreshnessView, type PeakFreshness } from "@lib/peakFreshness";
import { HelionButton } from "@components/ui/HelionButton";
import { InfoHint, chipClass, bucketLabel, type ChipTone } from "@components/cockpit";
import { DeltaRow, type DeltaFormat } from "@components/cockpit/delta";
import { PeriodReturnTile } from "@components/cockpit/PeriodReturnTile";
import { flowAwareChangePct } from "@lib/navPerformance";
import type { NavReconciliation } from "@lib/navTypes";
import { fmtLocalDateTime } from "@lib/dateUtils";
import { useUser } from "../hooks/useUser";
import { useSignalR } from "../hooks/useSignalR";
import { RiskMethodologyModal } from "@components/RiskMethodologyModal";
// Sticky section nav — shared with the Signals dashboard so the two pages cannot drift.
import { QuickNav } from "@components/cockpit/QuickNav";
// Exit / Harvest Watchlist logic + shared numeric formatting (see lib/exitWatchlist.ts).
import {
  isNum,
  fmtUSD,
  fmtPct,
  fmtNum,
  asNum,
  buildWatchlist,
  explainFlags,
  explainNonTriggers,
  fmtStrike,
  hasFullContractIdentity,
  watchlistContractLabel,
  type DecisionThresholds,
  type RiskHomeRunPolicy,
  type WatchlistRow,
} from "@lib/exitWatchlist";
import { BookExposureGreeks } from "@components/BookExposureGreeks";
import { isMarketOpenAt } from "@lib/marketSessionClock";
import { summarizeCarry, type CarryInfo } from "@lib/marketCarry";
// ONE derivation of harvest-lockout capital, shared with Signals and the Screener so the
// three surfaces cannot quote different withheld amounts. See lib/harvestLockout.ts.
import {
  deriveHarvestLockout,
  netDeployable,
  deployabilityHeadline,
  fmtUnlockDate,
  type HarvestLockout,
  type DeployableFigure,
} from "@lib/harvestLockout";

// Day-over-day risk deltas (GET /api/risk/snapshot_deltas). Additive telemetry:
// compares the two most recent persisted daily risk snapshots (each captured 15:45 ET,
// pre-close, while the option book still quotes — not a settled close).
type SnapshotDeltasResponse = {
  current: Record<string, number | string | boolean | null> | null;
  prior: Record<string, number | string | boolean | null> | null;
  deltas: Record<string, number> | null;
  watchlist_delta?: {
    newly_flagged?: Array<Record<string, unknown>>;
    no_longer_flagged?: Array<Record<string, unknown>>;
  };
  current_as_of?: string | null;
  prior_as_of?: string | null;
  sessions_between?: number | null;
  stale?: boolean;
  /** Set when the window straddles the switch to market-carry pricing (2026-09-17). */
  basis_change?: { first_session?: string; fields?: string[]; note?: string } | null;
  reason?: string;
};

type PortfolioGreeks = {
  delta: number;
  gamma: number;
  vega: number;
  theta?: number;
  theta_per_day?: number;
  vanna: number;
  vomma: number;
};

type GreeksByExp = {
  expiration: string;
  delta: number;
  gamma: number;
  vega: number;
  theta?: number;
  theta_per_day?: number;
  vanna: number;
  vomma: number;
};

// Emitted by risk_greeks. `degraded` is true when any greek above rests on a
// FABRICATED implied vol (no observed IV existed, so a placeholder 20% was used) or
// when a held contract was dropped for missing inputs. 20% is well below real
// long-dated SPX IV, so fabricated contracts UNDERSTATE vega and stress loss -- the
// error runs in the reassuring direction, which is exactly why it must be surfaced.
type GreeksDataHealth = {
  degraded?: boolean | null;
  contracts_total?: number | null;
  contracts_priced?: number | null;
  iv_fabricated_count?: number | null;
  iv_fabricated_conids?: number[] | null;
  dropped_no_inputs_count?: number | null;
  dropped_conids?: number[] | null;
  fallback_iv_used?: number | null;
  note?: string | null;
};

type GreeksResponse = {
  as_of?: string;
  rate_used?: number | null;
  /** Rate + dividend source behind every greek (backend strategy/market_carry.py). */
  carry?: CarryInfo | null;
  portfolio?: PortfolioGreeks;
  by_expiration?: GreeksByExp[];
  cache?: RiskCacheMeta | null;
  data_health?: GreeksDataHealth | null;
  status?: string;
  error?: string;
};

// Long-end vol & vega cockpit + daily P&L attribution (GET /api/risk/long_end_vol).
// Read-only risk visibility: surfaces the long-dated vol the book actually lives on
// (vega-weighted ATM IV) vs spot VIX vs VIX3M, net vega as $/vol-point, and the
// close-to-now delta/theta/vega decomposition. All fields nullable / degrade gracefully.
type LongEndVolDayOverDay = {
  prior_as_of?: string | null;
  calendar_days?: number | null;
  spot_vix_change?: number | null;
  long_end_change?: number | null;
  divergence_flag?:
    | "spot_up_long_down"
    | "spot_down_long_up"
    | "aligned"
    | "quiet"
    | null;
  implied_vega_pnl?: number | null;
};
type LongEndVolCockpit = {
  spot_vix?: number | null;
  vix3m?: number | null;
  term_structure_ratio?: number | null;
  term_structure_label?:
    | "deep_contango"
    | "contango"
    | "flat"
    | "backwardation"
    | null;
  book_iv?: number | null;
  long_end_basis?: "book_iv" | "vix3m" | null;
  book_iv_minus_spot_vix?: number | null;
  book_iv_minus_vix3m?: number | null;
  net_vega_per_pt?: number | null;
  net_delta?: number | null;
  net_theta_per_day?: number | null;
  spot_level?: number | null;
  day_over_day?: LongEndVolDayOverDay | null;
};
type LongEndVolAttribution = {
  prior_as_of?: string | null;
  long_end_basis?: "book_iv" | "vix3m" | null;
  delta_pnl?: number | null;
  theta_pnl?: number | null;
  theta_basis?: "prior" | "live" | null;
  vega_pnl?: number | null;
  estimated_total?: number | null;
  actual_day_pnl?: number | null;
  residual?: number | null;
  missing_terms?: string[];
  spot_move?: number | null;
  long_end_move?: number | null;
  note?: string;
};
type LongEndVolResponse = {
  as_of?: string;
  rate_used?: number | null;
  carry?: CarryInfo | null;
  cockpit?: LongEndVolCockpit | null;
  attribution?: LongEndVolAttribution | null;
  errors?: string[];
  note?: string;
  status?: string;
  error?: string;
};

type GridAxis = {
  spot: number[];
  vol: number[];
};

type GridCell = {
  spot: number;
  vol: number;
  pnl_usd: number;
  pnl_pct?: number | null;
};

type ExpCells = {
  expiration: string;
  cells: GridCell[];
};

type StressContract = {
  contract_id: string;
  symbol?: string;
  expiration: string;
  spot: number;
  vol: number;
  pnl_usd: number;
};

type StressGridResponse = {
  as_of?: string;
  mode_used?: string;
  cache?: RiskCacheMeta | null;
  axis_mode?: "vix" | "iv";
  axis_label?: string;
  current_vix?: number | null;
  latest_vix_as_of?: string | null;
  horizon_months?: number;
  horizon_years?: number;
  horizon_label?: string;
  // Present only when the request carried rate_shock_bp (2026-10-22). Absent = rates unshocked.
  rate_shock?: {
    bp: number;
    label: string;
    applies_to?: string;
    div_yield?: string;
    net_carry_shift_bp?: number;
    note?: string;
  } | null;
  grid?: GridAxis;
  portfolio?: GridCell[];
  by_expiration?: ExpCells[];
  contracts?: StressContract[];
  // Named vol-shape scenarios priced beside the grid (backend 2026-10-27; CIO chose the 6/24
  // twist only). Reprice mode only; no grid cell changes.
  shape_scenarios?: Array<{
    key?: string;
    label?: string;
    error?: string;
    vix_from?: number;
    vix_to?: number;
    long_end_years?: number;
    long_end_shift_vol_pts?: number;
    pnl_usd?: number;
    pnl_pct_of_sleeve?: number | null;
    legs?: Record<string, { pnl_usd?: number; contracts?: number; pct_of_leg?: number | null }>;
  }>;
  status?: string;
  error?: string;
};

// The backend's allowed set (risk_stress_grid.ALLOWED_RATE_SHOCKS_BP); anything else is a 400.
type RateShockBp = -100 | -50 | 0 | 50 | 100 | 150;
const RATE_SHOCK_OPTIONS: RateShockBp[] = [-100, -50, 0, 50, 100, 150];
const rateShockLabel = (bp: number): string =>
  bp === 0 ? "Rates unchanged" : `Rates ${bp > 0 ? "+" : ""}${bp}bp`;

type KPIOverview = {
  total_nav?: number | null;
  leaps_market_value?: number | null;
  fixed_income_market_value?: number | null;
  leaps_funded_pct?: number | null;
  fixed_income_pct?: number | null;
  portfolio_drawdown_pct?: number | null;
  peak_nav?: number | null;
  peak_nav_as_of?: string | null;
  current_vix?: number | null;
  vix_zscore?: number | null;
  current_band?: string | null;
  current_phase?: string | null;
  effective_regime?: string | null;
  base_target_leaps_pct?: number | null;
  effective_target_leaps_pct?: number | null;
  target_leaps_pct?: number | null;
  max_leaps_pct?: number | null;
  target_source?: string | null;
  defense_overlay_active?: boolean | null;
  defense_overlay_label?: string | null;
  // "engine" = the rebalance engine was consulted. "fallback" = it was NOT, so the
  // defense legs below are UNKNOWN rather than off. (backend 835d1528)
  regime_context_source?: string | null;
  // Per-leg breakdown. The overlay has TWO legs and this tile used to report only the
  // VIX-z spike one, so it read "Inactive" while grind_defense — the only exposure
  // lever validated out-of-sample — was actively driving the target to its floor.
  defense_spike_active?: boolean | null;
  defense_grind_active?: boolean | null;
  defense_grind_applied?: boolean | null;
  defense_grind?: {
    enabled?: boolean | null;
    active?: boolean | null;
    applied?: boolean | null;
    target_pct?: number | null;
    pct_from_sma200?: number | null;
    days_below_200_of_window?: number | null;
    persist_days?: number | null;
    persist_window?: number | null;
    rearm_mode?: string | null;
  } | null;
  defense_confirmed?: boolean | null;
  block_reason?: string | null;
  latest_nav_as_of?: string | null;
  latest_vix_as_of?: string | null;
};

type ScenarioCellCoord = {
  spot_pct: number | null;
  vol_level: number | null; // VIX level (vix axis) or vol-point shift (iv axis)
  vol_from: number | null; // live VIX the shock is measured from; 0 on the iv axis
};

type KPIsResponse = {
  as_of?: string;
  axis_mode?: "vix" | "iv";
  cache?: RiskCacheMeta | null;
  axis_label?: string;
  summary?: KPIOverview | null;
  vega_cushion_usd?: number | null;
  right_tail_convexity?: {
    flat_vol: number | null;
    minus5_vol: number | null;
  } | null;
  // The grid row/column each scenario tile actually read (backend 2026-10-27).
  scenario_cells?: {
    axis_mode?: "vix" | "iv";
    vega_cushion?: ScenarioCellCoord | null;
    right_tail_flat?: ScenarioCellCoord | null;
    right_tail_minus5?: ScenarioCellCoord | null;
  } | null;
  worst_cell?: GridCell | null;
  worst_pct_of_sleeve?: number | null;
  worst_pct_of_nav?: number | null;
  dd_budget_pct?: number | null;
  budget_basis?: string | null;
  budget_basis_detail?: string | null;
  vs_budget_ratio?: number | null;

  expected_stress_drawdown_pct_nav?: number | null;
  expected_linear_drawdown_pct_nav?: number | null;
  p95_path_drawdown_pct_nav?: number | null;
  structural_risk_pct_nav?: number | null;
  convexity_benefit_pct_nav?: number | null;
  recovery_probability_6m?: number | null;
  recovery_probability_12m?: number | null;
  dynamic_vs_budget_ratio?: number | null;

  status?: string;
  error?: string;
};

type RiskPortfolioOverview = {
  portfolioNAV?: number | null;
  // (portfolio_nav * target_pct_effective) - leaps_nav, floored at 0. MARKET basis, and
  // GROSS of any harvest lockout — see the note at its render site.
  remainingNotionalToDeploy?: number | null;
  cashNAV?: number | null;
  fixedIncomeNAV?: number | null;
  currentNotional?: number | null;
  currentLeapsPct?: number | null;
  navUpdatedUTC?: string | null;
  navAsOfDate?: string | null;
};

type RiskRebalanceMeta = {
  funded_weight?: number | null; // TRUE funded / cost-basis LEAPS weight (the cap basis)
  funded_cost?: number | null;
  funded_cap_breached?: boolean | null;
  caps?: { funded_max?: number | null; growth_max?: number | null } | null;
  // Harvest-lockout telemetry the engine has always emitted and nothing ever rendered
  // (strategy/rebalance_logic.py:1069-1071). `buy_block_reason` carries the engine's own
  // sentence, which is reproduced VERBATIM — never re-worded in this file.
  home_run_redeployment?: unknown;
  home_run_locked_notional?: number | null;
  effective_buy_headroom_after_home_run_lock?: number | null;
  buy_block_reason?: string | null;
};

type RiskPortfolioViewResponse = {
  overview?: RiskPortfolioOverview | null;
  heldLeaps?: any[] | null;
  heldLeapsRaw?: any[] | null;
  rebalance?: RiskRebalanceMeta | null;
  homeRun?: { policy?: RiskHomeRunPolicy | null; summary?: unknown; activeLocks?: unknown } | null;
};

// NavReconciliation now lives in @lib/navTypes and is imported above. It was declared
// here until 2026-08-10, when the estimated period return began rendering on Risk,
// Portfolio and Signals: three private copies of a payload shape are survivable for a
// display NAV, but not for a return figure that must read identically on every screen.

type RiskLatestNavState = {
  total_nav?: number | null;
  cash_nav?: number | null;
  as_of?: string | null;
  source?: string | null;
  cash_override?: {
    enabled?: boolean | null;
    observed_cash?: number | null;
    reserved_cash?: number | null;
    deployable_cash?: number | null;
    deployed_capital?: number | null;
    deployment_pct?: number | null;
    mode?: string | null;
    reason?: string | null;
  } | null;
  reconciliation?: NavReconciliation | null;
};

type FiRatePosition = {
  ticker: string;
  name?: string | null;
  market_value: number | null;
  weight: number | null;
  effective_duration: number | null;
  sec_yield?: number | null;
  dv01_usd: number | null;
  covered: boolean;
  dmv_usd: Record<string, number | null>;
};

type FiRateResponse = {
  as_of?: string;
  profile_as_of?: string | null;
  profile_staleness?: FiProfileStaleness | null;
  profile_source?: string | null;
  method?: string | null;
  scope?: string | null;
  notes?: string | null;
  positions?: FiRatePosition[];
  sleeve?: {
    total_mv?: number | null;
    covered_mv?: number | null;
    covered_pct?: number | null;
    mv_weighted_effective_duration?: number | null;
    dv01_usd?: number | null;
    dmv_usd?: Record<string, number | null>;
  } | null;
  coverage?: {
    missing_tickers?: string[];
    all_covered?: boolean;
    note?: string | null;
  } | null;
  shocks_bp?: number[];
  status?: string;
  warning?: string;
  error?: string;
};

type SurfaceSummary = {
  surface_regime?: string;
  best_deployment_bucket?: string;
  deployment_stance?: string;
  sweet_spot_signal?: string;
  surface_alignment_score?: number;
  convexity_quality_score?: number;
  portfolio_primary_bucket?: string;
  mismatch_flag?: boolean;
  surface_distortion_score?: number;
  current_vix?: number | null;
  vix_zscore?: number | null;
  latest_vix_as_of?: string | null;
  tenor_bucket_counts?: Record<string, number>;
  tenor_atm_iv?: Record<string, number | null>;
};

type SurfaceExposureRow = {
  tenor_bucket?: string;
  label?: string;
  position_count?: number;
  market_value?: number | null;
  market_value_pct?: number | null;
  delta_exposure?: number | null;
  vega_exposure?: number | null;
  avg_surface_score?: number | null;
  is_best_bucket?: boolean;
  score?: number | null;
};

type SurfaceOverviewResponse = {
  as_of?: string;
  cache?: RiskCacheMeta | null;
  surface_summary?: SurfaceSummary;
  bucket_exposure?: SurfaceExposureRow[];
  value_label_exposure?: SurfaceExposureRow[];
  shape_label_exposure?: SurfaceExposureRow[];
  volatility_stabilization?: VolatilityStabilization;
  risk_flags?: string[];
  commentary?: string;
  status?: string;
  error?: string;
};

type VolatilityStabilization = {
  state?: "improving" | "deteriorating" | "unstable" | string;
  confidence?: "low" | "medium" | "high" | string;
  short_vix_avg?: number | null;
  medium_vix_avg?: number | null;
  short_z_avg?: number | null;
  medium_z_avg?: number | null;
  spread_pct?: number | null;
  z_spread?: number | null;
  unstable?: boolean;
  whipsaw_flag?: boolean;
  signal_flips_recent?: number | null;
  consecutive_improving_days?: number | null;
  consecutive_deteriorating_days?: number | null;
  short_window?: number | null;
  medium_window?: number | null;
  whipsaw_window?: number | null;
  current_vix?: number | null;
  current_vix_zscore?: number | null;
  latest_vix_as_of?: string | null;
  headline?: string | null;
  detail_text?: string | null;
  message?: string | null;
};

type HeatmapMetricMode = "usd" | "pct";

type RiskCacheMeta = {
  source?: string | null;
  stale?: boolean | null;
  as_of?: string | null;
  generated_at?: string | null;
};

type StatCardProps = {
  title: string;
  value: string;
  subtext?: React.ReactNode;
  toneClassName?: string;
  accentClassName?: string;
};

type TopRiskCard = {
  title: string;
  value: string;
  subtext?: string;
  tone: string;
};

type VolSurfaceTermKey = "1y" | "18m" | "2y" | "30m" | "3y";
type VolSurfaceSkewKey = "95" | "100" | "105";

type VolSurfaceCoverage = {
  term_structure_quality?: string;
  long_end_coverage_ok?: boolean;
  exact_or_solid_count?: number;
  non_null_count?: number;
  distinct_non_null_count?: number;
  distinct_dte_min_gap_days?: number;
};

type VolSurfacePointDetail = {
  iv?: number | null;
  count?: number | null;
  atm_band_used?: number | null;
  dte_target?: number | null;
  dte_tolerance?: number | null;
  median_dte_used?: number | null;
  median_moneyness_used?: number | null;
  method?: string | null;
  dte_distance_days?: number | null;
  quality?: string | null;
  distinct_cluster?: boolean | null;
  expiration_count?: number | null;
};

// Deep-tenor (4-5.5yr) extension — isolated, display-only. Built off the backend's
// separate get_atm_iv_curve, NOT the shared term structure, so it never affects the
// screener. The long end is thin, so each node carries a usable/quality flag.
type VolSurfaceDeepNode = {
  tenor_days?: number | null;
  label?: string | null;
  atm_iv?: number | null;
  median_dte?: number | null;
  count?: number | null;
  quality?: string | null;
  usable?: boolean | null;
  percentile?: number | null;
  history_count?: number | null;
  method?: string | null;
  dte_distance_days?: number | null;
};

type HistoryTenor = "2y" | "3y";
type HistoryStreak = {
  threshold_iv?: number | null;
  month_ends?: number | null;
  since_month?: string | null;
  sessions?: number | null;
  sessions_capped?: boolean | null;
  share_of_daily?: number | null;
};
type SurfaceHistoryViews = {
  available?: boolean;
  error?: string;
  series?: Array<{ date: string; sampling?: string; "2y"?: number | null; "3y"?: number | null; suspect?: string[] }>;
  bands?: Partial<Record<HistoryTenor, { p20?: number | null; p30?: number | null; p50?: number | null; p80?: number | null; n?: number }>>;
  latest?: Partial<Record<HistoryTenor, { iv?: number | null; percentile?: number | null }>>;
  streaks?: Partial<Record<HistoryTenor, { below_p30?: HistoryStreak; below_p50?: HistoryStreak }>>;
  shape?: Record<string, { value?: number | null; percentile?: number | null; n?: number }>;
  history?: { start?: string; end?: string; sessions?: number; months?: number; daily_since?: string | null };
};

type VolSurfaceResponse = {
  symbol?: string;
  cache?: RiskCacheMeta | null;
  as_of_trade_date?: string;
  spot_px?: number | null;
  row_count?: number;
  contract_count?: number;
  atm_term_structure?: Partial<Record<VolSurfaceTermKey, number | null>>;
  atm_term_structure_details?: Partial<Record<VolSurfaceTermKey, VolSurfacePointDetail>> & {
    _coverage?: VolSurfaceCoverage;
  };
  near_atm_skew?: Partial<Record<VolSurfaceSkewKey, number | null>>;
  near_atm_skew_details?: Partial<Record<VolSurfaceSkewKey, VolSurfacePointDetail>>;
  term_slopes?: Record<string, number | null>;
  term_structure_quality?: string;
  long_end_coverage_ok?: boolean;
  deep_term_structure?: VolSurfaceDeepNode[];
  deep_end_coverage_ok?: boolean;
  blob_path?: string;
  requested_date?: string;
  data_source?: {
    container?: string;
    root_prefix?: string;
    latest_trade_date_mode?: string;
  };
  selection_parameters?: Record<string, number | string | boolean | null>;
  // Suspect-node check (backend 2026-10-27): FLAG ONLY. A node whose total variance
  // kinks against its neighbours is listed here; percentiles and regime are unchanged.
  term_sanity?: {
    checked?: boolean;
    suspect_labels?: string[];
    front_suspect?: boolean;
    nodes?: Record<string, { status?: string; reason?: string | null; same_expiry_as?: string }>;
  } | null;
  // "How cheap, and for how long" (backend 2026-10-27, DISPLAY ONLY): 2y/3y history,
  // full-sample bands, below-p30/p50 streaks and slope/skew percentiles.
  history_views?: SurfaceHistoryViews | null;
  // 2y ATM implied vs trailing 1y / 2y realized SPX vol (backend 2026-10-27, DISPLAY ONLY).
  implied_vs_realized?: {
    available?: boolean;
    error?: string;
    tenor?: string;
    latest?: {
      as_of?: string;
      implied?: number | null;
      spx_close_through?: string | null;
      "1y"?: { realized?: number | null; spread?: number | null; spread_percentile?: number | null; spread_median?: number | null; n?: number };
      "2y"?: { realized?: number | null; spread?: number | null; spread_percentile?: number | null; spread_median?: number | null; n?: number };
    };
  } | null;
  history_sample_count?: number;
  term_percentiles?: Partial<Record<VolSurfaceTermKey, number | null>>;
  term_history_counts?: Partial<Record<VolSurfaceTermKey, number | null>>;
  skew_percentiles?: Partial<Record<VolSurfaceSkewKey, number | null>>;
  surface_regime?: string;
  history_window?: {
    start?: string | null;
    end?: string | null;
    frequency?: string | null;
  };
  status?: string;
  error?: string;
};

const VOL_TERM_ORDER: VolSurfaceTermKey[] = ["1y", "18m", "2y", "30m", "3y"];
const VOL_SKEW_ORDER: VolSurfaceSkewKey[] = ["95", "100", "105"];

const normalizeRiskLatestNav = (json: any): RiskLatestNavState | null => {
  if (!json || typeof json !== "object") return null;

  const navObj = json?.nav ?? json?.latest_nav?.nav ?? null;
  const cashOverride =
    json?.cashOverride ??
    json?.cash_override ??
    json?.latest_nav?.cashOverride ??
    json?.latest_nav?.cash_override ??
    null;

  return {
    total_nav:
      typeof navObj?.TotalNAV === "number"
        ? navObj.TotalNAV
        : typeof json?.total_nav === "number"
        ? json.total_nav
        : null,
    cash_nav:
      typeof navObj?.CashNAV === "number"
        ? navObj.CashNAV
        : typeof json?.cash_nav === "number"
        ? json.cash_nav
        : null,
    as_of:
      typeof json?.asOfUTC === "string" && json.asOfUTC
        ? json.asOfUTC
        : typeof navObj?.AsOfDate === "string" && navObj.AsOfDate
        ? navObj.AsOfDate
        : typeof json?.as_of === "string" && json.as_of
        ? json.as_of
        : null,
    source: typeof json?.source === "string" ? json.source : null,
    cash_override:
      cashOverride && typeof cashOverride === "object"
        ? {
            enabled:
              typeof cashOverride?.enabled === "boolean"
                ? cashOverride.enabled
                : null,
            observed_cash:
              typeof cashOverride?.observed_cash === "number"
                ? cashOverride.observed_cash
                : null,
            reserved_cash:
              typeof cashOverride?.reserved_cash === "number"
                ? cashOverride.reserved_cash
                : typeof cashOverride?.reservedCash === "number"
                ? cashOverride.reservedCash
                : null,
            deployable_cash:
              typeof cashOverride?.deployable_cash === "number"
                ? cashOverride.deployable_cash
                : null,
            deployed_capital:
              typeof cashOverride?.deployed_capital === "number"
                ? cashOverride.deployed_capital
                : null,
            deployment_pct:
              typeof cashOverride?.deployment_pct === "number"
                ? cashOverride.deployment_pct
                : null,
            mode: typeof cashOverride?.mode === "string" ? cashOverride.mode : null,
            reason: typeof cashOverride?.reason === "string" ? cashOverride.reason : null,
          }
        : null,
    reconciliation:
      json?.navReconciliation && typeof json.navReconciliation === "object"
        ? (json.navReconciliation as NavReconciliation)
        : json?.nav?.Reconciliation && typeof json.nav.Reconciliation === "object"
        ? (json.nav.Reconciliation as NavReconciliation)
        : null,
  };
};

// Signed USD (explicit + on gains) for P&L attribution terms.
function fmtSignedUSD(x?: number | null, maxFractionDigits = 0) {
  if (!isNum(x)) return "—";
  return `${x > 0 ? "+" : ""}${fmtUSD(x, maxFractionDigits)}`;
}

// Signed plain number (explicit + on positives) for vol-point moves.
function fmtSignedNum(x?: number | null, digits = 2) {
  if (!isNum(x)) return "—";
  return `${x > 0 ? "+" : ""}${x.toFixed(digits)}`;
}

function fmtDateTime(x?: string | null) {
  return fmtLocalDateTime(x);
}

function sourceLabel(source?: string | null) {
  const value = (source || "").toLowerCase();
  if (!value) return "Live";
  if (value === "blob_fallback") return "Snapshot Fallback";
  return titleize(value);
}

function sourceTone(source?: string | null, stale?: boolean | null) {
  if (stale) return "text-warn";
  const value = (source || "").toLowerCase();
  if (value === "blob_fallback") return "text-warn";
  return "text-up";
}

function freshnessLabel(cache?: RiskCacheMeta | null, asOf?: string | null) {
  const ts = cache?.generated_at || cache?.as_of || asOf || null;
  return ts ? fmtDateTime(ts) : "—";
}

// ---- Data-age / staleness helpers (data time, not compute time) ----

function parseTsMs(s?: string | null): number | null {
  if (!s) return null;
  // Bare date (YYYY-MM-DD): anchor at NOON UTC so timezone conversions (display +
  // trading-session math) stay on the correct calendar day instead of slipping to
  // the prior evening in a western timezone (midnight-UTC of 6/15 renders as
  // "6/14 6 PM" in Mountain Time). EOD feeds carry a date, not an intraday time.
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const [y, m, d] = s.split("-").map(Number);
    return Date.UTC(y, m - 1, d, 12, 0, 0);
  }
  const t = Date.parse(s);
  return Number.isFinite(t) ? t : null;
}

function humanizeAgeMs(ms: number): string {
  if (ms < 0) return "ahead of clock";
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 48) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

// ---- Trading-session-aware staleness (US Eastern, DST-correct) ----
// EOD feeds (VIX, ORATS vol surface, broker NAV) only update once per market
// session. On a Monday, Friday's close is the freshest data that exists — it is
// NOT stale, even though it is ~3 calendar days old. So we measure age in
// *trading sessions behind*, not clock hours, which kills the weekend/holiday
// "cry wolf" false alarms while still catching a genuinely missed session.

// Calendar parts for an epoch ms in US/Eastern (handles EST/EDT automatically).
function etParts(ms: number): { y: number; m: number; d: number; hour: number } {
  const fmt = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    hour12: false,
  });
  const get = (t: string) =>
    fmt.formatToParts(new Date(ms)).find((p) => p.type === t)?.value ?? "";
  return { y: Number(get("year")), m: Number(get("month")), d: Number(get("day")), hour: Number(get("hour")) };
}

// Whole-day serial number for an ET calendar date (days since 1970-01-01).
function etDateSerial(p: { y: number; m: number; d: number }): number {
  return Math.floor(Date.UTC(p.y, p.m - 1, p.d) / 86400000);
}

// US weekdays (Mon–Fri) strictly after the data's ET session date, up to today.
// Holidays are NOT modeled (no client-side calendar) — the per-feed grace below
// absorbs the occasional holiday so we under-alarm by a day rather than cry wolf.
// Friday data viewed Monday → 1; Friday data viewed Wednesday → 3.
function sessionsBehind(dataMs: number, nowMs: number): number {
  const dataSerial = etDateSerial(etParts(dataMs));
  const nowSerial = etDateSerial(etParts(nowMs));
  if (nowSerial <= dataSerial) return 0;
  let count = 0;
  for (let s = dataSerial + 1; s <= nowSerial; s++) {
    const weekday = (((s + 4) % 7) + 7) % 7; // serial 0 = 1970-01-01 = Thursday(4); 0=Sun..6=Sat
    if (weekday !== 0 && weekday !== 6) count++;
  }
  return count;
}

// Stale once the data is more than `graceSessions` trading sessions behind.
// grace accounts for normal publish lag (EOD feeds land the next morning) plus a
// holiday cushion. Tunable per feed.
function sessionStale(ms: number | null, graceSessions: number): boolean {
  if (ms === null) return true; // unknown age is treated as not-confirmed-fresh
  return sessionsBehind(ms, Date.now()) > graceSessions;
}

// EOD feeds report a bare date (YYYY-MM-DD); NAV reports a real intraday timestamp.
function isDateOnlyTs(s?: string | null): boolean {
  return typeof s === "string" && /^\d{4}-\d{2}-\d{2}$/.test(s);
}

// Calendar date (no time) of an epoch ms in UTC. Date feeds are anchored at noon
// UTC (see parseTsMs), so this yields the true session date, never a tz-shifted day.
function fmtUtcDate(ms: number): string {
  return new Date(ms).toLocaleDateString(undefined, {
    timeZone: "UTC",
    year: "numeric",
    month: "numeric",
    day: "numeric",
  });
}

// Session-aware age for EOD/date feeds (clock-hours is misleading for once-per-
// session data; Friday's close viewed Monday is "1 session ago", not "3d ago").
function sessionAgeLabel(ms: number): string {
  const n = sessionsBehind(ms, Date.now());
  if (n <= 0) return "current";
  return n === 1 ? "1 session ago" : `${n} sessions ago`;
}

function isWeekendNow(): boolean {
  const d = etParts(Date.now());
  const weekday = (((etDateSerial(d) + 4) % 7) + 7) % 7;
  return weekday === 0 || weekday === 6;
}

// Small inline glossary hint: native-tooltip "ⓘ" so terms are explained in place.
function postureTone(posture?: string | null) {
  const v = (posture || "").toLowerCase();
  if (v.includes("act")) return "text-down";
  if (v.includes("review")) return "text-warn";
  if (v.includes("monitor")) return "text-warn";
  return "text-up";
}

function titleize(x?: string | null) {
  if (!x) return "—";
  return x
    .replace(/_/g, " ")
    .split(" ")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function scoreTone(score?: number | null) {
  if (!isNum(score)) return "text-[var(--text-color)]";

  // Valuation semantics: low percentile / cheaper surface is good for option buyers.
  if (score <= 0.4) return "text-up";
  if (score <= 0.7) return "text-warn";
  return "text-down";
}

function qualityTone(score?: number | null) {
  if (!isNum(score)) return "text-[var(--text-color)]";

  // Positioning / quality semantics: higher alignment or quality is good.
  if (score >= 0.7) return "text-up";
  if (score >= 0.4) return "text-warn";
  return "text-down";
}

function percentileLabel(p?: number | null) {
  if (!isNum(p)) return "—";
  if (p < 40) return "Cheap";
  if (p < 70) return "Fair";
  return "Expensive";
}

// Exit-rank tier — mirrors the Portfolio page so the two surfaces read the same.
// Higher rank = more exit-worthy relative to the book (red), so semantics are inverted
// vs valuation percentiles above.
function exitRankTier(p?: number | null): { label: string; cls: string } {
  if (!isNum(p)) return { label: "—", cls: "text-[var(--text-color)]" };
  if ((p as number) >= 90) return { label: "High", cls: "text-down" };
  if ((p as number) >= 75) return { label: "Elevated", cls: "text-warn" };
  if ((p as number) >= 50) return { label: "Watch", cls: "text-warn" };
  return { label: "Low", cls: "text-up" };
}

/* One watchlist line: the summary row plus an expandable panel that shows the
 * contract in full and the arithmetic behind every trigger. Local expand state
 * lives here so the page-level component keeps none of it.
 */
/* Creates an exit TICKET from a flagged watchlist line. It does NOT trade: the
 * ticket lands in the Trade Desk queue where it still has to be dispositioned by
 * hand, and every order is placed manually in TWS. The confirmation gate below is
 * required by the house rule that no button creates a trade record in one click.
 *
 * Why a ticket rather than a ledger row: the ledger is written BY the disposition
 * step (pending_order_disposition), and a row created any other way would carry no
 * PendingOrderId, so broker fills could never attach to it and round-trip P&L would
 * break. Going through the ticket keeps the whole chain intact.
 */
const SendToDeskModal: React.FC<{
  row: WatchlistRow;
  onClose: () => void;
  onDone: (message: string, tone: "success" | "error") => void;
}> = ({ row, onClose, onDone }: {
  row: WatchlistRow;
  onClose: () => void;
  onDone: (message: string, tone: "success" | "error") => void;
}) => {
  const held = isNum(row.quantity) ? Math.abs(row.quantity as number) : null;
  // A trim is possible on ANY position of more than one contract. It used to be
  // offered only when the harvest POLICY had flagged the line, so a deliberate
  // partial on anything else was recorded as a SELL -- the verb for a close-out.
  // The quantity was always editable; only the label was wrong, and the ledger
  // reads very differently for "took some off" than for "closed the position".
  const canTrim = held !== null && held > 1;
  // The policy's own suggestion, when it made one, so a policy-driven harvest still
  // opens pre-filled with its recommended split.
  const policyTrim =
    isNum(row.homeRunTrimQty) && (row.homeRunTrimQty as number) > 0
      ? (row.homeRunTrimQty as number)
      : null;
  const [action, setAction] = useState<"SELL" | "TRIM">(policyTrim ? "TRIM" : "SELL");
  const [qty, setQty] = useState<string>(String(policyTrim ?? held ?? 1));
  const [reason, setReason] = useState<string>(
    row.triggers.length ? row.triggers.join("; ") : row.reason || "CIO review from the exit watchlist"
  );
  const [busy, setBusy] = useState(false);

  const qtyNum = Number(qty);
  const price = isNum(row.markPrice) ? (row.markPrice as number) : null;
  const mult = isNum(row.multiplier) ? (row.multiplier as number) : 100;
  const notional = isNum(price) && Number.isFinite(qtyNum) ? qtyNum * (price as number) * mult : null;
  const qtyBad =
    !Number.isFinite(qtyNum) || qtyNum <= 0 || (held !== null && qtyNum > held);
  // Taking the whole position off is a SELL, whatever it is called. Refusing the
  // mislabel keeps the ledger's verbs meaningful.
  const trimIsWholePosition = action === "TRIM" && held !== null && qtyNum >= held;
  const canSubmit =
    !busy && !qtyBad && !trimIsWholePosition && reason.trim().length > 0
    && isNum(notional) && (notional as number) > 0;

  const submit = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${API_BASE_URL}/create_order_ticket`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action,
          contractId: row.contractId,
          estimatedQty: Math.round(qtyNum),
          targetNotional: Number((notional as number).toFixed(2)),
          reason: reason.trim(),
          // D1 (2026-10-27): the CIO initiated this from the Risk watchlist. Without an origin the handler
          // defaulted to SCREENER, mislabelling every Risk-page ticket on the Trade Desk and in the ledger.
          origin: "CIO",
        }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json?.error || `HTTP ${res.status}`);
      onDone(
        `${watchlistContractLabel(row)} sent to the Trade Desk as a ${action} ticket for ${Math.round(qtyNum)}. Nothing has been traded — disposition it there once you have worked it in TWS.`,
        "success"
      );
      onClose();
    } catch (err: any) {
      onDone(err?.message || "Could not create the ticket.", "error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: "rgba(2, 8, 23, 0.62)" }}
      role="dialog"
      aria-modal="true"
      aria-label="Send to Trade Desk"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg rounded-xl p-5 text-[var(--text-color)]"
        style={{ backgroundColor: "var(--card-bg)", border: "1px solid var(--border-color)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="text-[10px] uppercase tracking-wide opacity-60">Send to Trade Desk</div>
        <h3 className="mt-1 text-[17px] font-semibold">{watchlistContractLabel(row)}</h3>
        <div className="text-[11px] opacity-60">conid {row.contractId}</div>

        <div className="mt-3 flex gap-2">
          {(canTrim ? (["TRIM", "SELL"] as const) : (["SELL"] as const)).map((a) => (
            <button
              key={a}
              type="button"
              onClick={() => {
                setAction(a);
                // On TRIM: the policy's split if it made one, else half the position
                // (the shape the policy itself uses), rounded down. Editable either way.
                setQty(String(
                  a === "TRIM"
                    ? (policyTrim ?? Math.max(1, Math.floor((held ?? 2) / 2)))
                    : held ?? 1
                ));
              }}
              className={`rounded-md px-3 py-1.5 text-[12px] font-semibold border ${
                action === a
                  ? "border-[rgb(var(--c-accent))] text-[rgb(var(--c-accent))]"
                  : "border-[var(--border-color)] opacity-70"
              }`}
            >
              {a === "TRIM" ? "Trim (partial)" : "Sell (full position)"}
            </button>
          ))}
        </div>

        {!canTrim ? (
          <div className="mt-2 text-[11px] text-warn leading-5">
            Only one contract held, so there is nothing to split — this is a hold-or-close
            decision, not a trim.
          </div>
        ) : action === "TRIM" && !policyTrim ? (
          <div className="mt-2 text-[11px] opacity-70 leading-5">
            Your call, not the policy's — the harvest policy has not flagged this line. It is
            recorded as a TRIM so the ledger shows part of the position came off, rather than a
            close-out.
          </div>
        ) : null}

        <label className="mt-3 block">
          <span className="text-[12px] opacity-70">Contracts</span>
          <input
            inputMode="numeric"
            value={qty}
            onChange={(e) => setQty(e.target.value)}
            className="mt-1 w-full rounded-md px-3 py-2 text-[13px] tabular-nums bg-[var(--bg-secondary)]"
            style={{
              border: qtyBad ? "1px solid rgb(var(--c-down))" : "1px solid var(--border-color)",
              color: "var(--text-color)",
            }}
          />
          <span className="mt-1 block text-[11px] opacity-60">
            Holding {held ?? "—"}.{" "}
            {isNum(notional)
              ? `Estimated ${fmtUSD(notional, 0)} at ${fmtUSD(price, 2)} × ${mult}.`
              : "No mark available — a notional is required, so this cannot be sent."}
          </span>
          {qtyBad ? (
            <span className="mt-1 block text-[11px] text-down">
              Must be between 1 and the {held ?? "held"} contracts on hand.
            </span>
          ) : trimIsWholePosition ? (
            <span className="mt-1 block text-[11px] text-down">
              That is the whole position — choose "Sell (full position)" so the ledger records a
              close-out rather than a trim.
            </span>
          ) : null}
        </label>

        <label className="mt-3 block">
          <span className="text-[12px] opacity-70">Reason recorded on the ticket</span>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={2}
            className="mt-1 w-full rounded-md px-3 py-2 text-[13px] bg-[var(--bg-secondary)]"
            style={{ border: "1px solid var(--border-color)", color: "var(--text-color)" }}
          />
        </label>

        <p className="mt-3 text-[11px] leading-5 opacity-70">
          This creates a <strong>recommendation only</strong>. No order is sent, nothing is
          executed, and the ticket still has to be dispositioned by hand on the Trade Desk after
          you trade it in TWS.
        </p>

        <div className="mt-4 flex items-center justify-end gap-2">
          <HelionButton size="sm" onClick={onClose} disabled={busy}>Cancel</HelionButton>
          <HelionButton
            size="sm"
            onClick={submit}
            disabled={!canSubmit}
            className="font-semibold"
            style={{ borderColor: "rgb(var(--c-accent))" }}
          >
            {busy ? "Creating…" : "Create ticket"}
          </HelionButton>
        </div>
      </div>
    </div>
  );
};

type WatchlistLineProps = {
  row: WatchlistRow;
  idx: number;
  thresholds: DecisionThresholds;
  policy: RiskHomeRunPolicy | null;
  fallbackSpot: number | null;
  fallbackSpotAsOf: string | null;
};

// React.FC per the house pattern (cf. PositionDetailCard in PortfolioView.tsx):
// @types/react is not installed, so a bare function component rejects `key` at the
// call site. Params are annotated explicitly to keep the props checked regardless.
const WatchlistLine: React.FC<WatchlistLineProps> = ({
  row,
  idx,
  thresholds,
  policy,
  fallbackSpot,
  fallbackSpotAsOf,
}: WatchlistLineProps) => {
  const [open, setOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [notice, setNotice] = useState<{ text: string; tone: "success" | "error" } | null>(null);
  const r = row;
  const label = watchlistContractLabel(r);
  const identified = hasFullContractIdentity(r);
  const explanations = explainFlags(r, thresholds, policy);
  const notTriggered = explainNonTriggers(r, thresholds);

  // Moneyness from the row's own underlying mark when the risk feed supplied one;
  // otherwise the vol-surface close, labelled as such. Never silently mixes the two.
  const spot = isNum(r.underlyingPrice) ? (r.underlyingPrice as number) : fallbackSpot;
  const spotIsFallback = !isNum(r.underlyingPrice) && isNum(fallbackSpot);
  const moneyness = isNum(spot) && isNum(r.strike) ? (spot as number) / (r.strike as number) - 1 : null;

  const rowBg = idx % 2 === 0 ? "bg-transparent" : "bg-[var(--table-row-alt-bg,rgba(255,255,255,0.02))]";

  return (
    <>
      <tr className={`border-b border-[var(--border-color)] ${rowBg}`}>
        <td className="p-3 align-top">
          <div className="font-medium">{label}</div>
          <div className="text-[11px] opacity-60">
            {isNum(r.quantity) ? `${r.quantity} ct` : ""}
            {r.contractId !== "—" ? ` • conid ${r.contractId}` : ""}
            {isNum(moneyness)
              ? ` • ${moneyness >= 0 ? "ITM" : "OTM"} ${fmtPct(Math.abs(moneyness), 1)}`
              : ""}
          </div>
          {!identified ? (
            <div className="text-[11px] mt-1 text-warn">
              Strike/right missing from the position feed — confirm the contract in TWS
              before acting.
            </div>
          ) : null}
        </td>
        <td className={`p-3 align-top font-semibold ${postureTone(r.posture)}`}>
          {r.posture || "—"}
          <div className="mt-1 flex flex-wrap gap-1">
            {r.hardExit ? (
              <span className="text-[9px] uppercase tracking-wide rounded-full px-2 py-0.5 border border-down/40 bg-down/10 text-down">
                Hard exit
              </span>
            ) : null}
            {r.isHomeRun ? (
              <span className="text-[9px] uppercase tracking-wide rounded-full px-2 py-0.5 border border-up/40 bg-up/10 text-up">
                Home run
              </span>
            ) : null}
            {r.likelyPeaked ? (
              <span className="text-[9px] uppercase tracking-wide rounded-full px-2 py-0.5 border border-warn/40 bg-warn/10 text-warn">
                Peaked
              </span>
            ) : null}
            {r.likelyMature ? (
              <span className="text-[9px] uppercase tracking-wide rounded-full px-2 py-0.5 border border-[var(--border-color)] bg-[var(--bg-secondary)] opacity-80">
                Mature
              </span>
            ) : null}
            {/* The harvest test did not run (no return, or the policy failed to load).
                Shown explicitly so an unevaluated line cannot pass for a clean one. */}
            {!r.homeRunEvaluated ? (
              <span
                className="text-[9px] uppercase tracking-wide rounded-full px-2 py-0.5 border border-warn/40 bg-warn/10 text-warn"
                title="The harvest check could not be evaluated for this position — its return or the harvest policy was unavailable. This is NOT the same as 'no harvest signal'."
              >
                Harvest not evaluated
              </span>
            ) : null}
          </div>
        </td>
        <td className="p-3 align-top max-w-[320px]">
          <div className="leading-5">
            {explanations.length
              ? explanations.map((e) => e.headline).slice(0, 3).join(" • ")
              : r.reason || "—"}
          </div>
          <div className="mt-1.5 flex flex-wrap items-center gap-2">
            {/* The primary action belongs on the row. It used to live only inside the
                expander below, so the ability to stage an exit read as absent. */}
            <HelionButton size="sm" onClick={() => setSending(true)}>
              Send to Trade Desk
            </HelionButton>
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              className="text-[11px] underline underline-offset-2 opacity-70 hover:opacity-100"
              aria-expanded={open}
            >
              {open ? "Hide why" : "Why this line?"}
            </button>
          </div>
          {/* The result of "Send to Trade Desk" — success AND failure — belongs on the
              row with the button. It used to render only inside the expander below,
              which defaults closed, so a ticket that was never created was
              indistinguishable from one that was. Same regression as the button itself
              (see the note above); do not move either back inside the expander. */}
          {notice ? (
            <div
              role="status"
              aria-live="polite"
              className={`mt-1.5 text-[11px] leading-5 ${
                notice.tone === "error" ? "text-down font-semibold" : "text-up"
              }`}
            >
              {notice.text}
              {notice.tone === "success" ? (
                <> <a className="underline" href="/trading-desk">Open the Trade Desk</a>.</>
              ) : null}
            </div>
          ) : null}
        </td>
        <td className="p-3 align-top text-right">{fmtNum(r.dte, 0)}</td>
        <td
          className={`p-3 align-top text-right ${
            isNum(r.returnPct) && (r.returnPct as number) >= 0 ? "text-up" : "text-down"
          }`}
        >
          {fmtPct(r.returnPct, 1)}
        </td>
        <td className="p-3 align-top text-right text-down">{fmtPct(r.ddFromPeak, 1)}</td>
        <td className="p-3 align-top text-right font-semibold">
          {isNum(r.exitHintPercentile) ? (
            <span className={exitRankTier(r.exitHintPercentile).cls}>
              {exitRankTier(r.exitHintPercentile).label} · {fmtNum(r.exitHintPercentile, 0)}
            </span>
          ) : (
            "—"
          )}
          {isNum(r.exitHintScore) ? (
            <div
              className="text-[11px] font-normal opacity-60"
              title="Absolute exit signal (rule+model blend), 0-100 — separate from the rank. A high rank with a low score = most exit-worthy of a calm book, not urgent."
            >
              score{" "}
              {fmtNum(
                (r.exitHintScore as number) <= 1.5
                  ? (r.exitHintScore as number) * 100
                  : (r.exitHintScore as number),
                0
              )}
            </div>
          ) : null}
        </td>
      </tr>

      {open ? (
        <tr className={`border-b border-[var(--border-color)] ${rowBg}`}>
          <td colSpan={7} className="p-3 pt-0">
            <div className="rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] p-3 space-y-3">
              {/* --- Contract, in full --- */}
              <div>
                <div className="text-[10px] uppercase tracking-wide opacity-60 mb-1">Contract</div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-1 text-[12px]">
                  <div>
                    <span className="opacity-60">Underlying </span>
                    <span className="font-medium">{r.symbol || "—"}</span>
                  </div>
                  <div>
                    <span className="opacity-60">Strike </span>
                    <span className="font-medium">{fmtStrike(r.strike)}</span>
                  </div>
                  <div>
                    <span className="opacity-60">Right </span>
                    <span className="font-medium">
                      {r.right ? (r.right.toUpperCase().startsWith("C") ? "Call" : "Put") : "—"}
                    </span>
                  </div>
                  <div>
                    <span className="opacity-60">Expiry </span>
                    <span className="font-medium">{r.expiration}</span>
                  </div>
                  <div>
                    <span className="opacity-60">Contracts </span>
                    <span className="font-medium">{isNum(r.quantity) ? r.quantity : "—"}</span>
                  </div>
                  <div>
                    <span className="opacity-60">Entry </span>
                    <span className="font-medium">{fmtUSD(r.entryPrice, 2)}</span>
                  </div>
                  <div>
                    <span className="opacity-60">Mark </span>
                    <span className="font-medium">{fmtUSD(r.markPrice, 2)}</span>
                  </div>
                  <div>
                    <span className="opacity-60">Market value </span>
                    <span className="font-medium">{fmtUSD(r.marketValue, 0)}</span>
                  </div>
                  <div>
                    <span className="opacity-60">Conid </span>
                    <span className="font-medium">{r.contractId}</span>
                  </div>
                  <div>
                    <span className="opacity-60">Held </span>
                    <span className="font-medium">{isNum(r.ageDays) ? `${r.ageDays}d` : "—"}</span>
                  </div>
                  <div>
                    <span className="opacity-60">Δ </span>
                    <span className="font-medium">{fmtNum(r.delta, 2)}</span>
                  </div>
                  <div>
                    <span className="opacity-60">IV </span>
                    <span className="font-medium">{fmtPct(r.impliedVol, 1)}</span>
                  </div>
                </div>
                {isNum(moneyness) ? (
                  <div className="text-[11px] opacity-60 mt-1">
                    {r.symbol} at {fmtNum(spot, 2)} vs {fmtStrike(r.strike)} strike ={" "}
                    {moneyness >= 0 ? "in the money by " : "out of the money by "}
                    {fmtPct(Math.abs(moneyness), 1)}
                    {spotIsFallback
                      ? ` (spot from the vol-surface close${fallbackSpotAsOf ? ` ${fallbackSpotAsOf}` : ""}, not this position's own mark)`
                      : ""}
                    .
                  </div>
                ) : null}
              </div>

              {/* --- Why it is on the list --- */}
              <div>
                <div className="text-[10px] uppercase tracking-wide opacity-60 mb-1">
                  Why it is on this list
                </div>
                {explanations.length ? (
                  <ul className="space-y-2">
                    {explanations.map((e) => (
                      <li key={e.key} className="text-[12px] leading-5">
                        <span className={`${chipClass(e.tone)} mr-2 align-middle`}>{e.headline}</span>
                        {e.detail ? <span className="opacity-80">{e.detail}</span> : null}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="text-[12px] opacity-70">
                    {r.reason ||
                      "No named trigger — this line is here on relative exit-rank alone."}
                  </div>
                )}
              </div>

              {!r.homeRunEvaluated ? (
                <div className="text-[11px] leading-5 text-warn">
                  The harvest check did not run for this position — its return or the harvest
                  policy was unavailable. Treat this as unknown, not as "nothing to do".
                </div>
              ) : null}

              {/* --- What was checked and did not fire --- */}
              {notTriggered.length ? (
                <div className="text-[11px] opacity-55 leading-5">
                  Checked and not triggered: {notTriggered.join("; ")}.
                </div>
              ) : null}

              {/* --- The engine's own sentence, verbatim --- */}
              {r.detail ? (
                <div className="text-[11px] opacity-55 leading-5">
                  Engine note: {r.detail}
                </div>
              ) : null}

              <div className="text-[11px] opacity-60 leading-5">
                "Send to Trade Desk" stages this as a SELL/TRIM recommendation in the Trade
                Desk's Decide tab, alongside anything the exit engine flags. You disposition it
                there after trading in TWS; it does not trade.
              </div>

              <div className="text-[11px] opacity-55 leading-5">
                Advisory posture only — nothing here is a trade instruction and nothing trades
                automatically. Confirm the contract, quote, and size in TWS before acting.
              </div>
            </div>
          </td>
        </tr>
      ) : null}

      {sending ? (
        <SendToDeskModal
          row={r}
          onClose={() => setSending(false)}
          onDone={(text, tone) => setNotice({ text, tone })}
        />
      ) : null}
    </>
  );
};

function ratioTone(ratio?: number | null) {
  if (!isNum(ratio)) return "text-[var(--text-color)]";
  if (ratio <= 0.85) return "text-up";
  if (ratio <= 1.1) return "text-warn";
  return "text-down";
}

function stanceTone(label?: string | null) {
  const value = (label || "").toLowerCase();
  if (
    ["favorable", "aligned", "lean in", "lean_in", "constructive"].includes(
      value
    )
  ) {
    return "text-up";
  }
  if (["neutral", "balanced", "mixed", "selective", "watch"].includes(value)) {
    return "text-warn";
  }
  if (["defensive", "cautious", "dislocated", "reduce"].includes(value)) {
    return "text-down";
  }
  return "text-[var(--text-color)]";
}

function stabilizationTone(state?: string | null) {
  const value = (state || "").toLowerCase();
  if (value === "improving") return "text-up";
  if (value === "deteriorating") return "text-down";
  if (value === "unstable") return "text-warn";
  return "text-[var(--text-color)]";
}

function stabilizationBadgeClass(state?: string | null) {
  const value = (state || "").toLowerCase();
  if (value === "improving") {
    return "border-up/35 bg-up/10 text-up";
  }
  if (value === "deteriorating") {
    return "border-down/35 bg-down/10 text-down";
  }
  return "border-warn/35 bg-warn/10 text-warn";
}

function surfaceValueFromCommentary(commentary?: string | null) {
  const text = (commentary || "").toLowerCase();
  if (text.includes("very rich")) return "Very Rich";
  if (text.includes(" rich")) return "Rich";
  if (text.includes("very cheap")) return "Very Cheap";
  if (text.includes(" cheap")) return "Cheap";
  return "Neutral";
}

function riskPostureLabel(
  ratio?: number | null,
  mismatchFlag?: boolean | null,
  deploymentStance?: string | null
) {
  const stance = (deploymentStance || "").toLowerCase();
  if (isNum(ratio) && ratio > 1.1) return "Defensive";
  if (mismatchFlag) return "Watch";
  if (
    stance.includes("lean") ||
    stance.includes("favorable") ||
    stance.includes("constructive")
  ) {
    return "Favorable";
  }
  if (stance.includes("selective") || stance.includes("watch")) {
    return "Watch";
  }
  return "Neutral";
}

function riskPostureTone(label?: string | null) {
  const value = (label || "").toLowerCase();
  if (value === "favorable") return "text-up";
  if (value === "watch" || value === "neutral") {
    return "text-warn";
  }
  if (value === "defensive") return "text-down";
  return "text-[var(--text-color)]";
}

function worstScenarioLabel(
  cell?: GridCell | null,
  axisMode: "vix" | "iv" = "vix"
) {
  if (!cell) return "—";
  const spotText = `${cell.spot > 0 ? "+" : ""}${cell.spot}% spot`;
  const volText =
    axisMode === "vix"
      ? `VIX ${fmtNum(cell.vol, 0)}`
      : `${cell.vol > 0 ? "+" : ""}${fmtNum(cell.vol, 0)} vol pts`;
  return `${spotText} / ${volText}`;
}

function surfaceCoverageTone(label?: string | null) {
  const value = (label || "").toLowerCase();
  if (value === "solid" || value === "exact") {
    return "text-up";
  }
  if (value === "partial" || value === "nearby") {
    return "text-warn";
  }
  if (value === "sparse") {
    return "text-down";
  }
  return "text-[var(--text-color)]";
}

function surfaceCoverageBadgeClass(label?: string | null) {
  const value = (label || "").toLowerCase();
  if (value === "solid" || value === "exact") {
    return "border-up/35 bg-up/10 text-up";
  }
  if (value === "partial" || value === "nearby") {
    return "border-warn/35 bg-warn/10 text-warn";
  }
  if (value === "sparse") {
    return "border-down/35 bg-down/10 text-down";
  }
  return "border-[var(--border-color)] bg-[var(--bg-secondary)] text-[var(--text-color)]";
}

// 28.9 -> "29th"; 1 -> "1st"; 12 -> "12th".
function ordinalPct(p: number) {
  const n = Math.round(p);
  const mod100 = n % 100;
  const suffix =
    mod100 >= 11 && mod100 <= 13 ? "th" : ({ 1: "st", 2: "nd", 3: "rd" } as Record<number, string>)[n % 10] ?? "th";
  return `${n}${suffix}`;
}

function fmtMonthLabel(ym: string | null | undefined) {
  if (!ym || ym.length < 7) return "—";
  const d = new Date(`${ym.slice(0, 7)}-01T00:00:00Z`);
  return d.toLocaleDateString("en-US", { month: "short", year: "numeric", timeZone: "UTC" });
}

function termLabel(key: VolSurfaceTermKey) {
  const map: Record<VolSurfaceTermKey, string> = {
    "1y": "1Y",
    "18m": "18M",
    "2y": "2Y",
    "30m": "30M",
    "3y": "3Y",
  };
  return map[key];
}

function skewLabel(key: VolSurfaceSkewKey) {
  const map: Record<VolSurfaceSkewKey, string> = {
    "95": "95%",
    "100": "100%",
    "105": "105%",
  };
  return map[key];
}

function StatCard({
  title,
  value,
  subtext,
  toneClassName,
  accentClassName,
}: StatCardProps) {
  return (
    <div
      className={`rounded-xl border shadow-sm p-3 bg-[var(--card-bg)] text-[var(--text-color)] ${
        accentClassName || "border-[var(--border-color)]"
      }`}
    >
      <div className="text-[10px] uppercase tracking-[0.18em] opacity-60">
        {title}
      </div>
      <div
        className={`text-[1.8rem] font-bold mt-1.5 leading-none ${
          toneClassName || ""
        }`}
      >
        {value}
      </div>
      {subtext ? (
        <div className="text-[11px] opacity-65 mt-1.5 leading-5">{subtext}</div>
      ) : null}
    </div>
  );
}

function SectionCard({
  title,
  subtitle,
  right,
  children,
  className,
}: {
  title: string;
  subtitle?: string;
  right?: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={`rounded-xl border shadow-sm p-3.5 bg-[var(--card-bg)] text-[var(--text-color)] border-[var(--border-color)] ${
        className || ""
      }`}
    >
      <div className="flex flex-col gap-2.5 mb-3 lg:flex-row lg:items-start lg:justify-between">
        <div className="space-y-1">
          <div className="font-semibold text-[15px] leading-none">{title}</div>
          {subtitle ? (
            <div className="text-[11px] opacity-60 leading-5">{subtitle}</div>
          ) : null}
        </div>
        {right ? <div className="shrink-0">{right}</div> : null}
      </div>
      {children}
    </div>
  );
}

// "Where do I stand on the cap" — funded/cost basis is the cap basis (CIO 2026-06-16);
// market-value weight is shown for context and can run above the cap without a breach.
function BookStanceCard({
  leapsMv,
  nav,
  fundedPct,
  marketPct,
  capPct,
  fundedDelta,
  marketDelta,
  deltaWindowLabel,
  lockout,
  remainingToDeploy,
}: {
  leapsMv: number | null;
  nav: number | null;
  fundedPct: number | null;
  marketPct: number | null;
  capPct: number | null;
  fundedDelta?: number | null;
  marketDelta?: number | null;
  deltaWindowLabel?: string;
  /** Shared harvest-lockout derivation; see lib/harvestLockout.ts. */
  lockout: HarvestLockout;
  /** Purchase headroom against the effective target, already netted of the lockout. */
  remainingToDeploy: DeployableFigure;
}) {
  const cap = isNum(capPct) ? capPct : 0.4;
  const ratio = isNum(fundedPct) && cap > 0 ? fundedPct / cap : null;
  const capTone: ChipTone =
    ratio == null ? "neutral" : ratio > 1.0 ? "danger" : ratio > 0.95 ? "warning" : "positive";
  const capLabel =
    ratio == null ? "cap status —" : ratio > 1.0 ? "Over cap" : ratio > 0.95 ? "At cap" : "Under cap";
  return (
    <SectionCard title="Book Stance" subtitle="Where the LEAPS sleeve sits against the funded cap.">
      <div className="text-[1.35rem] font-semibold leading-none">
        {fmtUSD(leapsMv)}{" "}
        <span className="opacity-50 text-[0.9rem] font-normal">LEAPS on {fmtUSD(nav)} NAV</span>
      </div>
      <div className="mt-3 flex flex-wrap items-start gap-x-8 gap-y-2">
        <div>
          <div className="text-[10px] uppercase tracking-[0.16em] opacity-60">
            Funded basis (cap basis)
            <InfoHint text="LEAPS funded with cost basis as a share of NAV. The 40% cap is measured on THIS, not market value." />
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <span className="text-[1.15rem] font-semibold">{fmtPct(fundedPct)}</span>
            <span className={chipClass(capTone)}>
              {capLabel} · cap {fmtPct(cap)}
            </span>
          </div>
          <div className="mt-1 text-[11px]">
            <DeltaRow delta={fundedDelta} format="pp" digits={1} hideZero windowLabel={deltaWindowLabel} />
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.16em] opacity-60">
            Market weight (informational)
            <InfoHint text="LEAPS market value as a share of NAV. Can run above the cap on appreciation without breaching it." />
          </div>
          <div className="mt-1 text-[1.15rem] font-semibold opacity-80">{fmtPct(marketPct)}</div>
          <div className="mt-1 text-[11px]">
            <DeltaRow delta={marketDelta} format="pp" digits={1} windowLabel={deltaWindowLabel} />
          </div>
        </div>
      </div>
      <div className="mt-3 text-[11px] opacity-65 leading-5">
        The cap is measured on funded / cost basis. Market value can run above it on appreciation
        without breaching the cap — trims require a funded-basis breach or a regime target drop.
      </div>

      {/* ROOM TO BUY, AND WHAT A HARVEST LOCKOUT TAKES OUT OF IT.
          The cap chip above says whether we MAY buy; it never said how much, and it could not
          show that the engine had already withheld locked harvest capital from the buy budget
          (strategy/rebalance_logic.py:947-950). A lockout that merely SHRANK the budget was
          silent everywhere in the app. Figures and wording come from the shared derivation, and
          the engine's own sentence inside it is reproduced verbatim from `buy_block_reason`. */}
      <div className="mt-3 border-t border-[var(--border-color)] pt-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="text-[10px] uppercase tracking-[0.16em] opacity-60">
            Remaining to deploy
            <InfoHint text="Effective target x NAV minus LEAPS market value, then less any harvest capital the lockout withholds from the buy budget. Advisory only — nothing here blocks a trade." />
          </div>
          <span className="text-[1.15rem] font-semibold">
            {remainingToDeploy.unknown ? "—" : fmtUSD(remainingToDeploy.net)}
          </span>
          {remainingToDeploy.suppression === "partial" || remainingToDeploy.suppression === "full" ? (
            <span className="text-[11px] opacity-70">of {fmtUSD(remainingToDeploy.gross)} before the lockout</span>
          ) : null}
          <span
            className={chipClass(
              !lockout.known ? "neutral" : lockout.status === "none" ? "positive" : "warning"
            )}
          >
            {lockout.known ? lockout.headline : "Lockout unknown"}
          </span>
        </div>
        <div className="mt-1.5 text-[11px] opacity-70 leading-5">{remainingToDeploy.disclosure}</div>
        {lockout.nextUnlockAtUTC && lockout.status !== "none" ? (
          <div className="mt-1 text-[11px] opacity-55 leading-5">
            Advisory: the lockout does not prevent a purchase. Unlock {fmtUnlockDate(lockout.nextUnlockAtUTC)}.
          </div>
        ) : null}
      </div>
    </SectionCard>
  );
}

// "What do I act on first" — surfaces the exit model's already-bucketed postures
// (Act now / Review today / Monitor) at the top. Review-only; nothing auto-trades.
function AttentionPane({
  watchlist,
  summary,
  heldLeapsCount,
  deltaNote,
  loaded,
}: {
  watchlist: WatchlistRow[];
  summary: { actNow: number; review: number; monitor: number };
  heldLeapsCount: number;
  deltaNote?: React.ReactNode;
  /** False until the held-position payload has actually arrived. An empty watchlist and
   *  a not-yet-loaded one are different facts and only one of them is reassuring. */
  loaded: boolean;
}) {
  const flagged = summary.actNow + summary.review + summary.monitor;
  const top = watchlist.slice(0, 3);
  const postureTone = (r: WatchlistRow): ChipTone => {
    const p = (r.posture || "").toLowerCase();
    if (r.hardExit || p.includes("act")) return "danger";
    if (p.includes("review")) return "elevated";
    if (p.includes("monitor")) return "warning";
    return "neutral";
  };
  return (
    <SectionCard
      title="Needs Attention First"
      subtitle="Review-only — nothing trades automatically. Full queue in the watchlist below."
      right={
        <div className="text-[11px] opacity-70">
          {flagged} of {heldLeapsCount} flagged
        </div>
      }
    >
      <div className="flex flex-wrap gap-2 mb-2">
        <span className={chipClass(summary.actNow > 0 ? "danger" : "neutral")}>Now {summary.actNow}</span>
        <span className={chipClass(summary.review > 0 ? "elevated" : "neutral")}>Today {summary.review}</span>
        <span className={chipClass(summary.monitor > 0 ? "warning" : "neutral")}>Monitor {summary.monitor}</span>
      </div>
      {deltaNote ? <div className="text-[11px] mb-3" style={{ color: "var(--text-muted)" }}>{deltaNote}</div> : null}
      {top.length ? (
        <div className="space-y-1.5">
          {top.map((r, i) => (
            <div
              key={`${r.contractId}-${i}`}
              className="flex items-center justify-between gap-3 text-[12px]"
            >
              <div className="min-w-0 truncate">
                {/* Full contract, not just the expiry — several strikes share an expiry. */}
                <span className="font-medium">{watchlistContractLabel(r)}</span>
                {r.triggers.length ? (
                  <span className="opacity-60"> · {r.triggers[0]}</span>
                ) : r.reason ? (
                  <span className="opacity-60"> · {r.reason}</span>
                ) : null}
              </div>
              <span className={`${chipClass(postureTone(r))} shrink-0`}>{r.posture || "—"}</span>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-[12px] opacity-65">
          {loaded
            ? "No positions flagged — the book is clean."
            : "Waiting for held positions — this is not yet a clean-book result."}
        </div>
      )}
    </SectionCard>
  );
}

function ExposureBars({
  rows,
  labelKey,
  valueKey,
  formatter,
}: {
  rows: SurfaceExposureRow[];
  labelKey: "tenor_bucket" | "label";
  valueKey: "market_value_pct" | "score";
  formatter?: (row: SurfaceExposureRow) => string;
}) {
  if (!rows.length) {
    return <div className="opacity-60 text-sm">No data</div>;
  }

  return (
    <div className="space-y-2.5">
      {rows.map((row) => {
        const rawLabel = row[labelKey];
        const displayLabel =
          labelKey === "tenor_bucket" ? bucketLabel(rawLabel) : titleize(rawLabel);
        const rawValue = row[valueKey];
        const widthPct = isNum(rawValue)
          ? Math.max(0, Math.min(100, rawValue * 100))
          : 0;

        return (
          <div key={`${labelKey}-${String(rawLabel)}`} className="space-y-1.5">
            <div className="flex items-center justify-between gap-3 text-[13px]">
              <div className="font-medium">{displayLabel}</div>
              <div className="opacity-70 text-right text-[11px] sm:text-[12px]">
                {formatter ? formatter(row) : fmtPct(row.market_value_pct ?? null)}
              </div>
            </div>
            <div className="h-2 rounded-full bg-[var(--bg-secondary)] border border-[var(--border-color)] overflow-hidden">
              <div
                className="h-full rounded-full bg-[var(--nav-hover)]"
                style={{ width: `${widthPct}%` }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function TogglePills<T extends string>({
  value,
  options,
  onChange,
}: {
  value: T;
  options: Array<{ value: T; label: string }>;
  onChange: (value: T) => void;
}) {
  return (
    <div className="inline-flex rounded-xl border border-[var(--border-color)] bg-[var(--bg-secondary)] p-1 gap-1">
      {options.map((option) => {
        const active = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            onClick={() => onChange(option.value)}
            className={`rounded-lg px-3 py-1.5 text-[12px] transition ${
              active
                ? "bg-[var(--card-bg)] shadow-sm border border-[var(--border-color)]"
                : "opacity-70 hover:opacity-100"
            }`}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

type MiniNarrativeCardProps = {
  label: string;
  value: string;
  subtext?: string;
  toneClassName?: string;
};

const MiniNarrativeCard: React.FC<MiniNarrativeCardProps> = ({
  label,
  value,
  subtext,
  toneClassName,
}) => {
  return (
    <div className="rounded-xl border border-[var(--border-color)] bg-[var(--bg-secondary)] p-3">
      <div className="text-[10px] uppercase tracking-[0.16em] opacity-60">
        {label}
      </div>
      <div className={`text-[1.2rem] font-semibold mt-1 ${toneClassName || ""}`}>
        {value}
      </div>
      {subtext ? (
        <div className="text-[11px] opacity-65 mt-1.5 leading-5">{subtext}</div>
      ) : null}
    </div>
  );
};

function RiskTakeawayBand({
  posture,
  surfaceValue,
  bestBucket,
  biggestRisk,
  commentary,
}: {
  posture: string;
  surfaceValue: string;
  bestBucket: string;
  biggestRisk: string;
  commentary?: string | null;
}) {
  return (
    <div className="rounded-2xl border border-[var(--border-color)] bg-[var(--card-bg)] shadow-sm overflow-hidden">
      <div className="grid grid-cols-1 xl:grid-cols-[1.35fr_1fr]">
        <div className="p-4 sm:p-5 border-b xl:border-b-0 xl:border-r border-[var(--border-color)]">
          <div className="text-[10px] uppercase tracking-[0.18em] opacity-60">
            Risk Takeaway
          </div>
          <div className="mt-2 text-[1.15rem] sm:text-[1.25rem] font-semibold leading-tight">
            {posture} posture with {surfaceValue.toLowerCase()} surface conditions
            and the best current deployment zone in {bestBucket}.
          </div>
          <div className="mt-2.5 text-[12px] opacity-75 leading-6">
            {commentary ||
              "Use the top-line read to frame the book before drilling into heatmap scenarios and bucket detail."}
          </div>
        </div>
        {/* Three short descriptive reads across the top, then the one cell that names
            something needing a decision on its own full-width row. It previously sat in
            a quarter cell at 12px/medium while its three siblings rendered at
            1.1rem/semibold — the smallest, lightest value in the band built to be read
            first. A span inside the old fixed grid-cols-2 would have left a hole, so the
            band is 3-up now and the risk row spans it. */}
        <div className="grid grid-cols-1 sm:grid-cols-3">
          <div className="p-4 border-b sm:border-r border-[var(--border-color)]">
            <div className="text-[10px] uppercase tracking-[0.16em] opacity-60">
              Current Posture
            </div>
            <div
              className={`mt-2 text-[1.1rem] font-semibold ${riskPostureTone(
                posture
              )}`}
            >
              {posture}
            </div>
          </div>
          <div className="p-4 border-b sm:border-r border-[var(--border-color)]">
            <div className="text-[10px] uppercase tracking-[0.16em] opacity-60">
              Surface Condition
            </div>
            <div className="mt-2 text-[1.1rem] font-semibold">{surfaceValue}</div>
          </div>
          <div className="p-4 border-b border-[var(--border-color)]">
            <div className="text-[10px] uppercase tracking-[0.16em] opacity-60">
              Best Deployment Zone
            </div>
            <div className="mt-2 text-[1.1rem] font-semibold">{bestBucket}</div>
          </div>
          <div className="p-4 sm:col-span-3">
            <div className="text-[10px] uppercase tracking-[0.16em] opacity-60">
              Biggest Active Risk
            </div>
            <div className="mt-2 text-[13px] font-semibold leading-6">
              {biggestRisk}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Heatmap({
  grid,
  cells,
  axisMode,
  axisLabel,
  metricMode,
  worstCell,
  baseCell,
}: {
  grid?: GridAxis | null;
  cells?: GridCell[] | null;
  axisMode: "vix" | "iv";
  axisLabel?: string;
  metricMode: HeatmapMetricMode;
  worstCell?: GridCell | null;
  baseCell?: GridCell | null;
}) {
  const axis: GridAxis = {
    spot: Array.isArray(grid?.spot) ? grid!.spot : [],
    vol: Array.isArray(grid?.vol) ? grid!.vol : [],
  };

  const data: GridCell[] = Array.isArray(cells) ? cells : [];

  const values = useMemo(() => {
    return data.map((c) => (metricMode === "pct" ? c.pnl_pct ?? 0 : c.pnl_usd));
  }, [data, metricMode]);

  // Intensity is normalized WITHIN a sign branch, on |value|, so that "darker means
  // more extreme" holds for losses and gains alike. Normalizing across the whole grid
  // (min..max) inverted the loss side: the largest loss sat at the range minimum and
  // therefore rendered palest, while a near-zero loss rendered a deeper red.
  const maxLoss = useMemo(
    () => values.reduce((m, v) => (v < 0 ? Math.max(m, Math.abs(v)) : m), 0),
    [values]
  );
  const maxGain = useMemo(
    () => values.reduce((m, v) => (v > 0 ? Math.max(m, v) : m), 0),
    [values]
  );

  const scale = (v: number) => {
    const bound = v >= 0 ? maxGain : maxLoss;
    if (!bound) return 0;
    const t = Math.abs(v) / bound;
    return Math.round(Math.max(0, Math.min(1, t)) * 100);
  };

  const lookup = (sp: number, vo: number) =>
    data.find((c) => c.spot === sp && c.vol === vo);

  const baseKey = baseCell ? `${baseCell.spot}:${baseCell.vol}` : null;
  const worstKey = worstCell ? `${worstCell.spot}:${worstCell.vol}` : null;

  if (!axis.spot.length || !axis.vol.length) {
    return <div className="opacity-60 text-sm">No data</div>;
  }

  return (
    <div className="space-y-3">
      {/* The key is painted from the same hsl() ramp the cells use, so a swatch and a
          cell of the same severity are the same colour. It previously used theme tokens
          (border-down / border-up), which resolve to different hues than the cells, and
          advertised an amber "Moderate" band that no cell can ever render — the cell hue
          is binary (145 gain / 3 loss). */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-[11px] opacity-80">
        <div className="flex items-center gap-2">
          <span>Loss</span>
          <span
            className="inline-block h-3 w-24 rounded-sm border border-[var(--border-color)]"
            style={{
              background: `linear-gradient(90deg, hsl(3, 58%, 50%) 0%, hsl(3, 58%, 94%) 100%)`,
            }}
          />
          <span className="opacity-70">worst → flat</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="opacity-70">flat → best</span>
          <span
            className="inline-block h-3 w-24 rounded-sm border border-[var(--border-color)]"
            style={{
              background: `linear-gradient(90deg, hsl(145, 58%, 94%) 0%, hsl(145, 58%, 50%) 100%)`,
            }}
          />
          <span>Gain</span>
        </div>
        <div className="flex items-center gap-2">
          <span
            className="inline-block h-3 w-3 rounded"
            style={{ border: "2px solid rgba(14,165,233,0.85)" }}
          />
          Base reference
        </div>
        <div className="flex items-center gap-2">
          <span
            className="inline-block h-3 w-3 rounded"
            style={{ border: "2px solid rgba(220,38,38,0.85)" }}
          />
          Worst cell
        </div>
        <div className="opacity-60">
          Darker = more extreme. Losses and gains are scaled separately, so the two sides
          are not comparable by shade alone — read the numbers.
        </div>
      </div>

      <div className="w-full overflow-auto">
        <table className="min-w-[980px] border-collapse text-[var(--text-color)]">
          <caption className="sr-only">
            Modeled LEAPS-sleeve profit and loss. Rows are volatility levels, columns are
            spot moves in percent. The fixed-income sleeve is held flat in this grid.
          </caption>
          <thead>
            <tr>
              <th scope="col" className="sticky left-0 z-10 text-left p-2 font-semibold bg-[var(--card-bg)] text-[13px]">
                {(axisLabel ||
                  (axisMode === "vix" ? "VIX Level" : "IV Δ (vol pts)"))}{" "}
                ↓ / Spot Δ (%) →
              </th>
              {axis.spot.map((sp) => (
                <th
                  key={sp}
                  scope="col"
                  className="p-2 text-right font-semibold text-[13px] text-[var(--text-color)] bg-[var(--card-bg)]"
                >
                  {sp > 0 ? `+${sp}%` : `${sp}%`}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {axis.vol
              .slice()
              .reverse()
              .map((vo) => (
                <tr key={vo}>
                  <th scope="row" className="sticky left-0 z-10 p-2 text-left font-semibold bg-[var(--card-bg)] text-[13px] text-[var(--text-color)]">
                    {axisMode === "vix" ? `${vo}` : vo > 0 ? `+${vo}` : `${vo}`}
                  </th>
                  {axis.spot.map((sp) => {
                    const c = lookup(sp, vo);
                    const pnlUsd = c?.pnl_usd ?? 0;
                    const pnlPct = c?.pnl_pct ?? null;
                    const primaryValue = metricMode === "pct" ? (pnlPct ?? 0) : pnlUsd;
                    const hue = primaryValue >= 0 ? 145 : 3;
                    const intensity = scale(primaryValue);
                    // Same slope on both branches so a loss and a gain of equal
                    // severity read at equal weight.
                    const light = 94 - intensity * 0.44;
                    const cellKey = `${sp}:${vo}`;
                    const isBase = cellKey === baseKey;
                    const isWorst = cellKey === worstKey;

                    return (
                      <td key={cellKey} className="p-1.5 align-top">
                        <div
                          className="rounded-lg p-2 text-right text-[12px] border shadow-[inset_0_1px_0_rgba(255,255,255,0.04)] min-h-[62px]"
                          style={{
                            backgroundColor: `hsl(${hue}, 58%, ${light}%)`,
                            borderColor: isWorst
                              ? "rgba(220,38,38,0.85)"
                              : isBase
                              ? "rgba(14,165,233,0.85)"
                              : primaryValue >= 0
                              ? "rgba(34,197,94,0.18)"
                              : "rgba(239,68,68,0.18)",
                            boxShadow: isWorst
                              ? "0 0 0 2px rgba(220,38,38,0.18)"
                              : isBase
                              ? "0 0 0 2px rgba(14,165,233,0.18)"
                              : undefined,
                          }}
                        >
                          <div className="flex items-start justify-between gap-2 mb-1">
                            <div
                              className="text-[9px] uppercase tracking-[0.12em]"
                              style={{ color: "var(--heatmap-cell-meta)" }}
>
                              {isWorst ? "Worst" : isBase ? "Base" : "Scenario"}
                            </div>
                            <div
                              className="text-[9px]"
                              style={{ color: "var(--heatmap-cell-meta)" }}
>
                              {sp > 0 ? `+${sp}` : sp}% /{" "}
                              {axisMode === "vix" ? vo : `${vo > 0 ? "+" : ""}${vo}`}
                            </div>
                          </div>
                          <div
                            className="font-semibold"
                            style={{ color: "var(--heatmap-cell-text)" }}
>
                            {metricMode === "pct"
                              ? fmtPct(pnlPct, 1)
                              : fmtUSD(pnlUsd)}
                          </div>
                          <div
                            className="text-[10px] mt-0.5"
                            style={{ color: "var(--heatmap-cell-subtext)" }}
>
                            {metricMode === "pct"
                              ? fmtUSD(pnlUsd)
                              : isNum(pnlPct)
                              ? fmtPct(pnlPct, 1)
                              : ""}
                          </div>
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SurfaceLineChart({
  points,
  valueFormatter,
  valueSuffix,
  lineColor = "var(--nav-hover)",
  dashedFromIndex,
  bars,
  barFormatter,
}: {
  // `suspect`: the backend's term_sanity flagged this node as impossible. It is drawn
  // as a hollow red marker OFF the line (the line joins its neighbours instead).
  points: Array<{ label: string; value: number | null | undefined; suspect?: boolean; note?: string | null }>;
  valueFormatter?: (value: number) => string;
  valueSuffix?: string;
  lineColor?: string;
  // Index at/after which the series is PROVISIONAL: drawn dashed with hollow
  // markers. For a tail that rests on materially thinner data than the head, so
  // it cannot read as equally solid. Omit for a uniform line.
  dashedFromIndex?: number;
  // Optional bars under the line, aligned with `points` (e.g. book vega by tenor). On
  // their own scale (tallest = 32% of the plot), labelled, drawn behind the line.
  bars?: Array<number | null>;
  barFormatter?: (value: number) => string;
}) {
  const indexed = points.map((point, index) => ({ ...point, index }));
  const valid = indexed.filter((point) => isNum(point.value) && !point.suspect);
  const suspectPts = indexed.filter((point) => isNum(point.value) && point.suspect);

  if (!valid.length) {
    return <div className="opacity-60 text-sm">No data</div>;
  }

  const width = 560;
  const height = 210;
  const left = 38;
  const right = 16;
  const top = 16;
  const bottom = 38;

  const values = [...valid, ...suspectPts].map((point) => point.value as number);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const spread = max - min || Math.max(Math.abs(max) * 0.08, 0.01);

  const xForIndex = (index: number) => {
    const usableWidth = width - left - right;
    const denom = Math.max(points.length - 1, 1);
    return left + (usableWidth * index) / denom;
  };

  const yForValue = (value: number) => {
    const usableHeight = height - top - bottom;
    const domainMin = min - spread * 0.12;
    const domainMax = max + spread * 0.12;
    const ratio = (value - domainMin) / (domainMax - domainMin || 1);
    return height - bottom - usableHeight * ratio;
  };

  const buildPath = (pts: typeof valid) =>
    pts
      .map((point, idx) => {
        const cmd = idx === 0 ? "M" : "L";
        return `${cmd} ${xForIndex(point.index)} ${yForValue(point.value as number)}`;
      })
      .join(" ");

  // Split the line at `dashedFromIndex`. With the prop omitted the split sits at
  // +Infinity, so `path` is identical to the previous single-path output and
  // `dashedPath` is empty -- the other call site renders exactly as before.
  const splitIndex = isNum(dashedFromIndex)
    ? dashedFromIndex
    : Number.POSITIVE_INFINITY;
  const solidPoints = valid.filter((point) => point.index < splitIndex);
  const tailPoints = valid.filter((point) => point.index >= splitIndex);
  // The boundary segment is drawn on the DASHED side (it repeats the last solid
  // point) so the join inherits the weaker of the two confidence levels.
  const dashedPoints = tailPoints.length
    ? [...solidPoints.slice(-1), ...tailPoints]
    : [];
  const path = buildPath(solidPoints);
  const dashedPath = buildPath(dashedPoints);

  const ticks = [max, (max + min) / 2, min];

  return (
    <div className="w-full overflow-auto">
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="w-full min-w-[420px] h-[220px]"
        role="img"
        aria-label="Surface chart"
      >
        <rect
          x={0}
          y={0}
          width={width}
          height={height}
          fill="transparent"
        />
        {ticks.map((tick, idx) => {
          const y = yForValue(tick);
          return (
            <g key={`tick-${idx}`}>
              <line
                x1={left}
                y1={y}
                x2={width - right}
                y2={y}
                stroke="var(--chart-grid-line)"
                strokeDasharray="4 4"
              />
              <text
                x={left - 8}
                y={y + 4}
                textAnchor="end"
                className="fill-[var(--chart-axis-text)] text-[10px]"
              >
                {valueFormatter
                  ? valueFormatter(tick)
                  : `${tick.toFixed(1)}${valueSuffix || ""}`}
              </text>
            </g>
          );
        })}

        {points.map((point, idx) => {
          const x = xForIndex(idx);
          return (
            <text
              key={`label-${idx}-${point.label}`}
              x={x}
              y={height - 12}
              textAnchor="middle"
              className="fill-[var(--chart-axis-text)] text-[10px]"
            >
              {point.label}
            </text>
          );
        })}

        {(() => {
          const barVals = (bars ?? []).map((b) => (isNum(b) && (b as number) > 0 ? (b as number) : 0));
          const barMax = Math.max(0, ...barVals);
          if (!bars || barMax <= 0) return null;
          const usableHeight = height - top - bottom;
          const barW = Math.min(34, ((width - left - right) / Math.max(points.length, 1)) * 0.5);
          return (
            <g data-testid="surface-vega-bars">
              {barVals.map((v, idx) => {
                if (v <= 0) return null;
                const h = (v / barMax) * usableHeight * 0.32;
                const x = xForIndex(idx) - barW / 2;
                const y = height - bottom - h;
                return (
                  <rect key={`bar-${idx}`} x={x} y={y} width={barW} height={h} rx={3} fill="var(--chart-axis-text)" opacity={0.22}>
                    <title>
                      {points[idx]?.label}: {barFormatter ? barFormatter(v) : v.toFixed(0)}
                    </title>
                  </rect>
                );
              })}
            </g>
          );
        })()}

        <path
          d={path}
          fill="none"
          stroke={lineColor}
          strokeWidth={3}
          strokeLinejoin="round"
          strokeLinecap="round"
        />
        {dashedPath ? (
          <path
            d={dashedPath}
            fill="none"
            stroke={lineColor}
            strokeWidth={3}
            strokeDasharray="6 5"
            strokeLinejoin="round"
            strokeLinecap="round"
            opacity={0.7}
          />
        ) : null}

        {valid.map((point) => {
          const x = xForIndex(point.index);
          const y = yForValue(point.value as number);
          const provisional = point.index >= splitIndex;
          return (
            <g key={`pt-${point.index}-${point.label}`}>
              <circle
                cx={x}
                cy={y}
                r={provisional ? 4.5 : 5}
                fill={provisional ? "var(--card-bg)" : lineColor}
                stroke={provisional ? lineColor : "none"}
                strokeWidth={provisional ? 2 : 0}
              />
              <circle cx={x} cy={y} r={10} fill="transparent">
                <title>
                  {point.label}:{" "}
                  {valueFormatter
                    ? valueFormatter(point.value as number)
                    : `${(point.value as number).toFixed(2)}${valueSuffix || ""}`}
                </title>
              </circle>
            </g>
          );
        })}

        {suspectPts.map((point) => {
          const x = xForIndex(point.index);
          const y = yForValue(point.value as number);
          return (
            <g key={`suspect-${point.index}-${point.label}`} data-testid="surface-suspect-point">
              <circle
                cx={x}
                cy={y}
                r={5}
                fill="var(--card-bg)"
                stroke="rgb(var(--c-down))"
                strokeWidth={2.5}
              />
              <circle cx={x} cy={y} r={10} fill="transparent">
                <title>
                  {point.label} SUSPECT:{" "}
                  {valueFormatter
                    ? valueFormatter(point.value as number)
                    : `${(point.value as number).toFixed(2)}${valueSuffix || ""}`}
                  {point.note ? ` — ${point.note}` : ""}
                </title>
              </circle>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

// "How cheap, and for how long" (item 3, display only). One tenor's ATM IV through the
// stored history, on a TIME axis (monthly month-ends to 2026-03, daily after -- so the
// daily block is honestly narrow on the full range), over its full-sample 20-80 band with
// the 30th and 50th marked. Suspect sessions are gaps, not points.
function CheapnessHistoryChart({
  views,
  tenor,
  range,
}: {
  views: SurfaceHistoryViews;
  tenor: HistoryTenor;
  range: "full" | "daily";
}) {
  const band = views.bands?.[tenor] ?? {};
  const dailySince = views.history?.daily_since ?? null;
  const all = (views.series ?? []).filter((p) => !(range === "daily" && dailySince && p.date < dailySince));
  const pts = all
    .map((p) => ({ t: Date.parse(`${p.date}T00:00:00Z`), v: p[tenor] ?? null, date: p.date }))
    .filter((p) => !Number.isNaN(p.t));
  const valued = pts.filter((p) => isNum(p.v));
  if (valued.length < 2 || !isNum(band.p50 ?? null)) {
    return <div className="opacity-60 text-sm">Not enough stored history</div>;
  }

  const width = 760;
  const height = 230;
  const left = 46;
  const right = 14;
  const top = 12;
  const bottom = 30;
  const vals = [...valued.map((p) => p.v as number), band.p20, band.p80].filter(isNum) as number[];
  const vMin = Math.min(...vals);
  const vMax = Math.max(...vals);
  const pad = (vMax - vMin) * 0.08 || 0.005;
  const t0 = pts[0].t;
  const t1 = pts[pts.length - 1].t;
  const x = (t: number) => left + ((width - left - right) * (t - t0)) / Math.max(t1 - t0, 1);
  const y = (v: number) => top + (height - top - bottom) * (1 - (v - (vMin - pad)) / (vMax - vMin + 2 * pad));

  // Break the line at gaps (suspect / missing sessions).
  const segments: string[] = [];
  let cur = "";
  for (const p of pts) {
    if (!isNum(p.v)) {
      if (cur) segments.push(cur);
      cur = "";
      continue;
    }
    cur += `${cur ? "L" : "M"} ${x(p.t).toFixed(1)} ${y(p.v as number).toFixed(1)} `;
  }
  if (cur) segments.push(cur);

  const last = valued[valued.length - 1];
  const yearTicks: number[] = [];
  if (range === "full") {
    const y0 = new Date(t0).getUTCFullYear();
    const y1 = new Date(t1).getUTCFullYear();
    const step = Math.max(1, Math.ceil((y1 - y0) / 7));
    for (let yr = y0 + 1; yr <= y1; yr += step) yearTicks.push(Date.UTC(yr, 0, 1));
  } else {
    const d = new Date(t0);
    for (let m = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 1)); m.getTime() <= t1; m = new Date(Date.UTC(m.getUTCFullYear(), m.getUTCMonth() + 1, 1))) {
      yearTicks.push(m.getTime());
    }
  }
  // Percentile lines carry no axis text (p20/p30/p50 sit a few tenths apart and the labels
  // collide); p50 is named at the right edge, the values are in the tiles and the legend.
  const pctLine = (v: number | null | undefined, dash: string, label?: string) =>
    isNum(v ?? null) ? (
      <g>
        <line x1={left} x2={width - right} y1={y(v as number)} y2={y(v as number)} stroke="rgb(245 158 11)" strokeDasharray={dash} opacity={0.75} />
        {label ? (
          <text x={width - right - 2} y={y(v as number) - 4} textAnchor="end" className="fill-[var(--chart-axis-text)] text-[9px]">
            {label}
          </text>
        ) : null}
      </g>
    ) : null;
  const yTicks = [vMin, (vMin + vMax) / 2, vMax];

  return (
    <div className="w-full overflow-auto">
      <svg viewBox={`0 0 ${width} ${height}`} className="w-full min-w-[480px] h-[240px]" role="img" aria-label={`${tenor} ATM IV history`} data-testid="cheapness-history-chart">
        {isNum(band.p20 ?? null) && isNum(band.p80 ?? null) ? (
          <rect
            x={left}
            width={width - left - right}
            y={y(band.p80 as number)}
            height={Math.max(0, y(band.p20 as number) - y(band.p80 as number))}
            fill="rgb(245 158 11)"
            opacity={0.1}
          />
        ) : null}
        {yTicks.map((t, i) => (
          <g key={`yt-${i}`}>
            <line x1={left} x2={width - right} y1={y(t)} y2={y(t)} stroke="var(--chart-grid-line)" strokeDasharray="4 4" />
            <text x={left - 6} y={y(t) + 3} textAnchor="end" className="fill-[var(--chart-axis-text)] text-[10px]">
              {(t * 100).toFixed(1)}%
            </text>
          </g>
        ))}
        {pctLine(band.p50, "6 4", "p50")}
        {pctLine(band.p30, "2 3")}
        {yearTicks.map((t) => (
          <text key={t} x={x(t)} y={height - 10} textAnchor="middle" className="fill-[var(--chart-axis-text)] text-[10px]">
            {range === "full"
              ? new Date(t).getUTCFullYear()
              : new Date(t).toLocaleDateString("en-US", { month: "short", timeZone: "UTC" })}
          </text>
        ))}
        {range === "full" && dailySince ? (
          <line
            x1={x(Date.parse(`${dailySince}T00:00:00Z`))}
            x2={x(Date.parse(`${dailySince}T00:00:00Z`))}
            y1={top}
            y2={height - bottom}
            stroke="var(--chart-grid-line)"
            strokeDasharray="3 3"
          />
        ) : null}
        {segments.map((d, i) => (
          <path key={i} d={d} fill="none" stroke="var(--nav-hover)" strokeWidth={1.8} strokeLinejoin="round" />
        ))}
        <circle cx={x(last.t)} cy={y(last.v as number)} r={4.5} fill="var(--nav-hover)">
          <title>
            {last.date}: {((last.v as number) * 100).toFixed(2)}%
          </title>
        </circle>
      </svg>
    </div>
  );
}

function SurfaceSkewBars({
  rows,
}: {
  rows: Array<{ label: string; value: number | null | undefined; percentile?: number | null }>;
}) {
  const valid = rows.filter((row) => isNum(row.value));
  if (!valid.length) {
    return <div className="opacity-60 text-sm">No data</div>;
  }

  const max = Math.max(...valid.map((row) => row.value as number));

  return (
    <div className="space-y-3">
      {rows.map((row) => {
        const value = row.value;
        const widthPct = isNum(value) && max > 0 ? ((value / max) * 100) : 0;

        return (
          <div key={row.label} className="space-y-1.5">
            <div className="flex items-center justify-between gap-3 text-[13px]">
              <div className="font-medium">{row.label}</div>
              <div className="opacity-75 text-[11px] sm:text-[12px]">
                {isNum(value) ? `${(value * 100).toFixed(2)}% IV` : "—"}
                {isNum(row.percentile) ? ` • pctl ${fmtNum(row.percentile, 1)}` : ""}
              </div>
            </div>
            <div className="h-2.5 rounded-full bg-[var(--bg-secondary)] border border-[var(--border-color)] overflow-hidden">
              <div
                className="h-full rounded-full bg-[var(--nav-hover)]"
                style={{ width: `${Math.max(0, Math.min(100, widthPct))}%` }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function SurfaceQualityBadge({
  label,
}: {
  label?: string | null;
}) {
  return (
    <div
      className={`inline-flex items-center rounded-full border px-2.5 py-1 text-[11px] font-semibold ${surfaceCoverageBadgeClass(
        label
      )}`}
    >
      {titleize(label || "Unavailable")}
    </div>
  );
}

const RiskDashboard: React.FC = () => {
  const [mode, setMode] = useState<"reprice" | "expansion">("reprice");
  const [axisMode, setAxisMode] = useState<"vix" | "iv">("vix");
  const [horizonMonths, setHorizonMonths] = useState<0 | 3 | 6 | 12>(0);
  // Rate shock on the stress grid (backend risk/stress_grid ?rate_shock_bp=, 2026-10-22). 0 sends
  // NO parameter, so the default request -- and the snapshot risk/kpis reads -- is untouched.
  const [rateShockBp, setRateShockBp] = useState<RateShockBp>(0);
  const [detailContracts, setDetailContracts] = useState(false);
  const [metricMode, setMetricMode] = useState<HeatmapMetricMode>("usd");
  const [methodologyOpen, setMethodologyOpen] = useState(false);

  const [greeks, setGreeks] = useState<GreeksResponse | null>(null);
  const [grid, setGrid] = useState<StressGridResponse | null>(null);
  const [kpis, setKpis] = useState<KPIsResponse | null>(null);
  const [surfaceOverview, setSurfaceOverview] =
    useState<SurfaceOverviewResponse | null>(null);
  const [volSurface, setVolSurface] = useState<VolSurfaceResponse | null>(null);
  const [cheapTenor, setCheapTenor] = useState<HistoryTenor>("3y");
  const [cheapRange, setCheapRange] = useState<"full" | "daily">("full");
  const [fiRate, setFiRate] = useState<FiRateResponse | null>(null);
  const [peakFreshness, setPeakFreshness] = useState<PeakFreshness | null>(null);
  const [portfolioView, setPortfolioView] =
    useState<RiskPortfolioViewResponse | null>(null);
  // When get_portfolio_view was last actually READ, and a slow tick to measure it against.
  // This page has no auto-refresh, so without the tick a lockout figure read at 07:00 would
  // still be asserted at 16:00. 60s is far below any rate this page could care about.
  const [portfolioViewFetchedAtMs, setPortfolioViewFetchedAtMs] = useState<number | null>(null);
  const [nowTickMs, setNowTickMs] = useState<number>(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNowTickMs(Date.now()), 60_000);
    return () => window.clearInterval(id);
  }, []);
  const [latestNav, setLatestNav] = useState<RiskLatestNavState | null>(null);
  const [snapDeltas, setSnapDeltas] = useState<SnapshotDeltasResponse | null>(null);
  const [longEndVol, setLongEndVol] = useState<LongEndVolResponse | null>(null);

  // Auth gate for the SignalR connect (mirror SignalsDashboard).
  const { loading: userLoading } = useUser();

  // SignalR live risk-deltas push (read-only telemetry). Updates the same snapDeltas
  // the page loads on open/refresh. The Risk page has no poll interval, so the on-load
  // + manual Refresh / ORATS-backfill fetch is the automatic fallback — nothing to
  // suppress; whichever delivered most recently is shown.
  useSignalR(
    "riskDeltas",
    (payload: any) => {
      if (payload && typeof payload === "object") {
        setSnapDeltas(payload && !payload.error ? (payload as SnapshotDeltasResponse) : null);
      }
    },
    { enabled: !userLoading }
  );

  const [loading, setLoading] = useState(false);
  const [surfaceLoading, setSurfaceLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [surfaceError, setSurfaceError] = useState<string | null>(null);
  const [expView, setExpView] = useState<string>("__PORTFOLIO__");

  const [oratsUpdating, setOratsUpdating] = useState(false);
  const [oratsStatus, setOratsStatus] = useState<{ text: string; tone: "live" | "warn" } | null>(
    null
  );

  // Shared with PortfolioView (lib/peakFreshness). "unknown" is never folded into
  // "current", and a run that refreshed only some lines reads as suspect.
  const { label: peakLabel, suspect: peakSuspect } = peakFreshnessView(peakFreshness);

  const navItems = useMemo(
    () => [
      // The nine sections the page is now built from, in page order. These point at the
      // <section> wrappers (risk-sec-*), not at the panel anchors inside them, so a jump
      // lands on the whole section rather than partway into it. The older panel ids
      // (#risk-overview, #risk-stress, …) are deliberately left in place: they are still
      // valid targets and the screenshot harness crops by them.
      { id: "risk-sec-trust", label: "Trust" },
      { id: "risk-sec-posture", label: "Posture" },
      { id: "risk-sec-nav", label: "NAV" },
      { id: "risk-sec-regime", label: "Regime" },
      { id: "risk-sec-book", label: "Sensitivities" },
      { id: "risk-sec-surface", label: "Surface" },
      { id: "risk-sec-stress", label: "Stress" },
      { id: "risk-sec-fi", label: "FI Rates" },
      { id: "risk-sec-data", label: "Data & Detail" },
    ],
    []
  );

  const fetchAll = async (withContracts: boolean) => {
    setLoading(true);
    setError(null);
    setSurfaceError(null);
    setSurfaceLoading(true);

    try {
      const qContracts = withContracts ? "&detail=contracts" : "";

      const [
        gkRes,
        sgRes,
        kpRes,
        soRes,
        pvRes,
        navRes,
        vsRes,
        frRes,
        sdRes,
        levRes,
      ] = await Promise.all([
        fetch(`${API_BASE_URL}/risk/greeks`),
        fetch(
          `${API_BASE_URL}/risk/stress_grid?mode=${mode}&axis_mode=${axisMode}&horizon_months=${horizonMonths}${qContracts}${
            rateShockBp !== 0 ? `&rate_shock_bp=${rateShockBp}` : ""
          }`
        ),
        fetch(`${API_BASE_URL}/risk/kpis?axis_mode=${axisMode}`),
        fetch(`${API_BASE_URL}/risk/surface_overview`),
        fetch(`${API_BASE_URL}/get_portfolio_view`),
        fetch(`${API_BASE_URL}/risk/nav_latest`),
        fetch(`${API_BASE_URL}/get_vol_surface_snapshot`),
        fetch(`${API_BASE_URL}/risk/fi_rate`),
        // Day-over-day deltas — additive/optional; never blocks the page.
        fetch(`${API_BASE_URL}/risk/snapshot_deltas`).catch(() => null),
        // Long-end vol & vega cockpit — additive/optional; never blocks the page.
        fetch(`${API_BASE_URL}/risk/long_end_vol`).catch(() => null),
      ]);

      if (!gkRes.ok || !sgRes.ok || !kpRes.ok || !soRes.ok || !pvRes.ok || !navRes.ok) {
        throw new Error("One or more risk services returned a non-success response.");
      }

      const [gk, sg, kp, so, pv, nav, vs, fr, sd, lev] = await Promise.all([
        gkRes.json(),
        sgRes.json(),
        kpRes.json(),
        soRes.json(),
        pvRes.json(),
        navRes.json(),
        vsRes.ok ? vsRes.json() : Promise.resolve(null),
        frRes.ok ? frRes.json() : Promise.resolve(null),
        sdRes && sdRes.ok ? sdRes.json() : Promise.resolve(null),
        levRes && levRes.ok ? levRes.json() : Promise.resolve(null),
      ]);

      if (gk?.error || sg?.error || kp?.error || so?.error) {
        throw new Error(
          gk?.error || sg?.error || kp?.error || so?.error || "Risk service error."
        );
      }

      setGreeks(gk ?? null);
      setGrid(sg ?? null);
      setKpis(kp ?? null);
      setSurfaceOverview(so ?? null);
      setPortfolioView(pv ?? null);
      setPortfolioViewFetchedAtMs(pv ? Date.now() : null);
      // Absent field => null => renders as "unknown", never as OK. Same rule as
      // PortfolioView: a missing freshness block must not read as a healthy one.
      setPeakFreshness(parsePeakFreshness((pv as any)?.peakDataFreshness));
      setLatestNav(normalizeRiskLatestNav(nav));
      setFiRate(fr && !fr?.error ? fr : null);
      setSnapDeltas(sd && !sd?.error ? (sd as SnapshotDeltasResponse) : null);
      setLongEndVol(lev && !lev?.error ? (lev as LongEndVolResponse) : null);
      setExpView("__PORTFOLIO__");

      if (vsRes.ok && vs && !vs?.error) {
        setVolSurface(vs);
        setSurfaceError(null);
      } else {
        setVolSurface(null);
        setSurfaceError(
          (vs && typeof vs?.error === "string" && vs.error) ||
            "Vol surface snapshot unavailable."
        );
      }
    } catch (e: unknown) {
      console.error(e);
      setError(e instanceof Error ? e.message : "Unknown error");
    } finally {
      setLoading(false);
      setSurfaceLoading(false);
    }
  };

  useEffect(() => {
    if (!userLoading) fetchAll(detailContracts);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, axisMode, horizonMonths, rateShockBp, detailContracts, userLoading]);

  // ---- Day-over-day deltas (session-over-session, 15:45 ET pre-close; telemetry) ----
  const deltasOk = !!snapDeltas && snapDeltas.reason === "ok" && !!snapDeltas.deltas;
  const deltaWindowLabel: string = (() => {
    if (!deltasOk || !snapDeltas) return "vs prior session";
    const sb = snapDeltas.sessions_between ?? null;
    const prior = snapDeltas.prior_as_of ?? null;
    const base =
      sb && sb > 1
        ? `vs ${prior ?? "prior"} (${sb} sessions)`
        : prior
        ? `vs prior session (${prior})`
        : "vs prior session";
    return snapDeltas.stale ? `${base} · stale` : base;
  })();
  /** Render a "▲ X  vs prior session" row for a snapshot column, or null.
   *  opts.window / opts.title override the default label/tooltip for tiles whose
   *  displayed value is on a slightly different basis than the snapshot column. */
  const renderDelta = (
    key: string,
    format: DeltaFormat,
    digits?: number,
    opts?: { window?: string; title?: string }
  ) => {
    if (!deltasOk || !snapDeltas?.deltas) return null;
    const d = snapDeltas.deltas[key];
    if (typeof d !== "number" || !Number.isFinite(d)) return null;
    // A greek or stress figure whose window straddles the switch to market-carry pricing:
    // part of the change is the new assumption, so the chip must not read as a market move.
    const basis = snapDeltas.basis_change;
    const basisHit = !!basis && (basis.fields ?? []).includes(key);
    const baseTitle =
      opts?.title ??
      `Change from the 15:45 ET pre-close reading on ${snapDeltas.prior_as_of ?? "the prior session"} to the one on ${snapDeltas.current_as_of ?? "the latest session"}`;
    return (
      <div className="mt-1">
        <DeltaRow
          delta={d}
          format={format}
          digits={digits}
          windowLabel={`${opts?.window ?? deltaWindowLabel}${basisHit ? " · pricing basis changed" : ""}`}
          title={basisHit ? `${baseTitle}. ${basis?.note ?? ""}` : baseTitle}
        />
      </div>
    );
  };
  // Watchlist movement since prior session: counts + which positions newly flagged.
  const watchlistDeltaNote: React.ReactNode = (() => {
    if (!deltasOk || !snapDeltas) return null;
    const wd = snapDeltas.watchlist_delta;
    const newly = wd?.newly_flagged ?? [];
    const cleared = wd?.no_longer_flagged ?? [];
    const heldDelta = snapDeltas.deltas?.HeldLeapsCount;
    const parts: string[] = [];
    if (newly.length) parts.push(`${newly.length} newly flagged`);
    if (cleared.length) parts.push(`${cleared.length} cleared`);
    if (typeof heldDelta === "number" && Number.isFinite(heldDelta) && heldDelta !== 0) {
      parts.push(`held LEAPS ${heldDelta > 0 ? "+" : ""}${heldDelta}`);
    }
    if (!parts.length) return null;
    const tip = newly.length
      ? "Newly flagged: " +
        newly
          .map((n) => {
            const sym = (n.symbol as string) ?? "";
            const strike = n.strike != null ? String(n.strike) : "";
            const act = (n.action as string) ?? (n.category as string) ?? "";
            return `${sym} ${strike}${act ? ` (${act})` : ""}`.trim();
          })
          .join(", ")
      : undefined;
    return (
      <span title={tip}>
        {parts.join(" · ")} {deltaWindowLabel}
      </span>
    );
  })();

  const expirations = useMemo<string[]>(() => {
    const list = grid?.by_expiration;
    return Array.isArray(list) ? list.map((x) => x.expiration) : [];
  }, [grid]);

  const heatmapCells: GridCell[] = useMemo(() => {
    if (!grid) return [];

    const sourceCells =
      expView === "__PORTFOLIO__"
        ? Array.isArray(grid.portfolio)
          ? grid.portfolio
          : []
        : Array.isArray(grid.by_expiration)
        ? grid.by_expiration.find((x) => x.expiration === expView)?.cells ?? []
        : [];

    const sleeveMarketValue = kpis?.summary?.leaps_market_value ?? null;

    return sourceCells.map((cell) => {
      const derivedPct =
        isNum(cell.pnl_pct)
          ? cell.pnl_pct
          : isNum(sleeveMarketValue) && sleeveMarketValue > 0
          ? cell.pnl_usd / sleeveMarketValue
          : null;

      return {
        ...cell,
        pnl_pct: derivedPct,
      };
    });
  }, [grid, expView, kpis?.summary?.leaps_market_value]);

  const worstContracts = useMemo(() => {
    const contracts = Array.isArray(grid?.contracts) ? grid!.contracts : [];
    if (!detailContracts || !contracts.length) return [];
    return contracts.slice(0, 12);
  }, [detailContracts, grid]);

  const gridAxis: GridAxis = {
    spot: grid?.grid?.spot ?? [],
    vol: grid?.grid?.vol ?? [],
  };

  const pg = greeks?.portfolio;
  const summary = kpis?.summary ?? null;
  const surfaceSummary = surfaceOverview?.surface_summary ?? null;
  const overview = portfolioView?.overview ?? null;

  const canonicalTotalNav = useMemo(() => {
    const latestTotal = latestNav?.total_nav;
    if (isNum(latestTotal) && latestTotal > 0) return latestTotal;
    const overviewTotal = overview?.portfolioNAV;
    if (isNum(overviewTotal) && overviewTotal > 0) return overviewTotal;
    return summary?.total_nav ?? null;
  }, [latestNav?.total_nav, overview?.portfolioNAV, summary?.total_nav]);

  const canonicalNavAsOf = useMemo(() => {
    return (
      latestNav?.as_of ||
      overview?.navUpdatedUTC ||
      overview?.navAsOfDate ||
      summary?.latest_nav_as_of ||
      null
    );
  }, [latestNav?.as_of, overview?.navUpdatedUTC, overview?.navAsOfDate, summary?.latest_nav_as_of]);

  const canonicalLeapsMarketValue = useMemo(() => {
    const notional = overview?.currentNotional;
    if (isNum(notional) && notional >= 0) return notional;
    return summary?.leaps_market_value ?? null;
  }, [overview?.currentNotional, summary?.leaps_market_value]);

  const canonicalLeapsPct = useMemo(() => {
    const currentPct = overview?.currentLeapsPct;
    if (isNum(currentPct)) return currentPct;
    if (isNum(canonicalLeapsMarketValue) && isNum(canonicalTotalNav) && canonicalTotalNav > 0) {
      return canonicalLeapsMarketValue / canonicalTotalNav;
    }
    return summary?.leaps_funded_pct ?? null;
  }, [overview?.currentLeapsPct, canonicalLeapsMarketValue, canonicalTotalNav, summary?.leaps_funded_pct]);

  // TRUE funded (cost-basis) LEAPS weight — the 40% cap basis. Matches Signals /
  // Portfolio (cost basis, NOT winner market value). NOTE: risk/kpis
  // summary.leaps_funded_pct is MARKET-based despite its name, so we must NOT use
  // it here. Prefer the engine's funded_weight from get_portfolio_view's rebalance
  // meta; fall back to summing held cost basis (entry x qty x mult) / NAV exactly
  // as Portfolio does, so the number ties out across pages.
  const fundedLeapsWeight = useMemo(() => {
    const engineFunded = portfolioView?.rebalance?.funded_weight;
    if (isNum(engineFunded)) return engineFunded;
    const rows = portfolioView?.heldLeaps ?? portfolioView?.heldLeapsRaw ?? [];
    if (!Array.isArray(rows) || !isNum(canonicalTotalNav) || canonicalTotalNav <= 0) return null;
    const cost = rows.reduce((sum: number, r: any) => {
      const entry = asNum(r?.entry_price);
      const qty = asNum(r?.quantity);
      const mult = asNum(r?.multiplier) ?? 100;
      if (entry == null || qty == null) return sum;
      return sum + entry * qty * (mult || 100);
    }, 0);
    return cost > 0 ? cost / canonicalTotalNav : null;
  }, [portfolioView, canonicalTotalNav]);

  const fundedCapPct = useMemo(() => {
    const raw = portfolioView?.rebalance?.caps?.funded_max;
    return isNum(raw) ? raw : 0.4;
  }, [portfolioView?.rebalance?.caps?.funded_max]);

  const canonicalFixedIncomeMarketValue = useMemo(() => {
    const fiNav = overview?.fixedIncomeNAV;
    if (isNum(fiNav) && fiNav >= 0) return fiNav;
    return summary?.fixed_income_market_value ?? null;
  }, [overview?.fixedIncomeNAV, summary?.fixed_income_market_value]);

  const canonicalFixedIncomePct = useMemo(() => {
    if (isNum(canonicalFixedIncomeMarketValue) && isNum(canonicalTotalNav) && canonicalTotalNav > 0) {
      return canonicalFixedIncomeMarketValue / canonicalTotalNav;
    }
    return summary?.fixed_income_pct ?? null;
  }, [canonicalFixedIncomeMarketValue, canonicalTotalNav, summary?.fixed_income_pct]);

  const volatilityStabilization = surfaceOverview?.volatility_stabilization ?? null;
  const bucketExposure = Array.isArray(surfaceOverview?.bucket_exposure)
    ? surfaceOverview!.bucket_exposure
    : [];
  const valueExposure = Array.isArray(surfaceOverview?.value_label_exposure)
    ? surfaceOverview!.value_label_exposure
    : [];
  const shapeExposure = Array.isArray(surfaceOverview?.shape_label_exposure)
    ? surfaceOverview!.shape_label_exposure
    : [];
  const riskFlags = Array.isArray(surfaceOverview?.risk_flags)
    ? surfaceOverview!.risk_flags
    : [];

  const volatilityDirectionSummary = useMemo(() => {
    const state = String(volatilityStabilization?.state || "unstable").toLowerCase();
    const confidence = titleize(volatilityStabilization?.confidence || "low");
    const streak =
      state === "improving"
        ? volatilityStabilization?.consecutive_improving_days ?? 0
        : state === "deteriorating"
        ? volatilityStabilization?.consecutive_deteriorating_days ?? 0
        : 0;

    const subtextParts = [
      volatilityStabilization?.detail_text || undefined,
      `Confidence ${confidence}`,
      streak > 0 ? `${streak} day streak` : undefined,
    ].filter(Boolean);

    return {
      headline:
        volatilityStabilization?.headline ||
        titleize(volatilityStabilization?.state || "unstable"),
      subtext: subtextParts.join(" • "),
      tone: stabilizationTone(volatilityStabilization?.state),
      badgeClass: stabilizationBadgeClass(volatilityStabilization?.state),
    };
  }, [volatilityStabilization]);

  const drawdownSubtext = useMemo(() => {
    const peakText = isNum(summary?.peak_nav ?? null)
      ? fmtUSD(summary?.peak_nav ?? null)
      : "—";
    const peakAsOfText = summary?.peak_nav_as_of
      ? fmtDateTime(summary.peak_nav_as_of)
      : "—";
    return `Peak ${peakText} • as of ${peakAsOfText}`;
  }, [summary?.peak_nav, summary?.peak_nav_as_of]);

  const bestBucketRow = useMemo(() => {
    return bucketExposure.find((row) => row.is_best_bucket) ?? null;
  }, [bucketExposure]);

  const currentVix =
    summary?.current_vix ?? surfaceSummary?.current_vix ?? grid?.current_vix ?? null;

  // ---- Exit / harvest watchlist (reuses the get_portfolio_view payload already fetched) ----
  const watchlist = useMemo(
    () => buildWatchlist(portfolioView?.heldLeaps ?? portfolioView?.heldLeapsRaw),
    [portfolioView]
  );
  const watchlistSummary = useMemo(() => {
    let actNow = 0, review = 0, monitor = 0;
    for (const r of watchlist) {
      const p = (r.posture || "").toLowerCase();
      if (r.hardExit || p.includes("act")) actNow += 1;
      else if (p.includes("review")) review += 1;
      else if (p.includes("monitor")) monitor += 1;
    }
    return { actNow, review, monitor };
  }, [watchlist]);
  const heldLeapsCount = useMemo(
    () => (portfolioView?.heldLeaps ?? portfolioView?.heldLeapsRaw ?? []).length,
    [portfolioView]
  );

  // The live harvest policy and the thresholds the exit model compared against,
  // both straight from get_portfolio_view. The watchlist explains a flag using
  // these numbers rather than a hardcoded copy, so a config change in
  // exit_signals.json shows up here without a frontend edit.
  const homeRunPolicy = useMemo<RiskHomeRunPolicy | null>(
    () => portfolioView?.homeRun?.policy ?? null,
    [portfolioView]
  );

  // HARVEST LOCKOUT. This page is the only one of the three that receives BOTH producers in
  // one payload: get_portfolio_view ships `homeRun` (build_home_run_dashboard_payload, whose
  // summary.lockedNotional is the RAW sum) and `rebalance` (the engine meta, whose
  // home_run_redeployment.lockedNotional is already policy-gated and is the literal input to
  // effective_buy_headroom). Both are handed to the shared module: it prefers the engine's
  // figures, borrows per-lock days from the harvest payload, and refuses to publish anything
  // at all if the two disagree.
  const harvestLockout = useMemo<HarvestLockout>(
    () =>
      deriveHarvestLockout({
        harvest: portfolioView?.homeRun ?? null,
        engine: portfolioView?.rebalance ?? null,
        fetchedAtMs: portfolioViewFetchedAtMs,
        nowMs: nowTickMs,
      }),
    [portfolioView, portfolioViewFetchedAtMs, nowTickMs]
  );

  // Cash the CIO reads as free to put to work. cash_override.deployable_cash knows nothing
  // about a redeployment lockout, and harvested proceeds sit in exactly this cash balance —
  // so an active lockout made this tile overstate what is free.
  const deployableCash = useMemo<DeployableFigure>(
    () => netDeployable(asNum(latestNav?.cash_override?.deployable_cash), harvestLockout),
    [latestNav, harvestLockout]
  );

  // Purchase headroom against the engine's effective target, GROSS of the lockout as
  // get_portfolio_view emits it. We net it here with the shared module rather than reading
  // overview.homeRunEffectiveRemainingToDeploy, because that backend field subtracts the RAW
  // lock sum (portfolio_view.py:2476-2477) instead of the policy-gated figure the engine
  // actually applies — the two diverge whenever apply_to_buy_budget is off, and only one
  // definition may appear on a screen.
  const remainingToDeploy = useMemo<DeployableFigure>(
    () => netDeployable(asNum(portfolioView?.overview?.remainingNotionalToDeploy), harvestLockout),
    [portfolioView, harvestLockout]
  );
  const decisionThresholds = useMemo<DecisionThresholds>(() => {
    const t = (portfolioView as any)?.decisionThresholds ?? {};
    const triggerMultiple =
      asNum(t.homeRunTriggerMultiple) ??
      (isNum(homeRunPolicy?.trigger_multiple) ? (homeRunPolicy?.trigger_multiple as number) : null);
    return {
      givebackFromPeak: asNum(t.givebackFromPeak),
      dteReviewDays: asNum(t.dteReviewDays),
      dteMonitorDays: asNum(t.dteMonitorDays),
      homeRunTriggerMultiple: triggerMultiple,
      homeRunTriggerReturn:
        asNum(t.homeRunTriggerReturn) ?? (isNum(triggerMultiple) ? triggerMultiple - 1 : null),
    };
  }, [portfolioView, homeRunPolicy]);

  // ---- Cash posture + NAV reconciliation (from /risk/nav_latest) ----
  const cashOverride = latestNav?.cash_override ?? null;
  const navRecon = latestNav?.reconciliation ?? null;

  // ---- Portfolio decay (theta) ----
  const portfolioThetaPerDay = pg?.theta_per_day ?? null;
  const portfolioThetaAnnual = pg?.theta ?? null;

  // ---- Honest data-age / staleness (data time, NOT compute time) ----
  const dataHealth = useMemo(() => {
    const navRaw = canonicalNavAsOf ?? null;
    const vixRaw = summary?.latest_vix_as_of ?? surfaceSummary?.latest_vix_as_of ?? null;
    const surfRaw = volSurface?.as_of_trade_date ?? null;
    const navMs = parseTsMs(navRaw);
    const vixMs = parseTsMs(vixRaw);
    const surfMs = parseTsMs(surfRaw);

    // Grace = trading sessions we tolerate before flagging stale. 2 sessions
    // covers normal next-morning publish lag + a holiday-adjacent weekend without
    // false-alarming; the surface gets +1 for its heavier ORATS publish/cache lag.
    // EOD feeds (VIX, Vol Surface) carry a bare date → shown as a session date +
    // session-age; NAV carries a real timestamp → shown as a local datetime.
    const items = [
      { key: "NAV", ms: navMs, dateOnly: isDateOnlyTs(navRaw), stale: sessionStale(navMs, 2) }, // broker NAV updates each session
      { key: "VIX", ms: vixMs, dateOnly: isDateOnlyTs(vixRaw), stale: sessionStale(vixMs, 2) },
      { key: "Vol Surface", ms: surfMs, dateOnly: isDateOnlyTs(surfRaw), stale: sessionStale(surfMs, 3) }, // ORATS EOD parquet
    ];

    const knownMs = items.map((i) => i.ms).filter((m): m is number => m !== null);
    const oldestMs = knownMs.length ? Math.min(...knownMs) : null;
    const oldestItem = oldestMs !== null ? items.find((i) => i.ms === oldestMs) ?? null : null;
    const staleCount = items.filter((i) => i.stale).length;
    const weekend = isWeekendNow();

    return {
      items,
      oldestMs,
      oldestLabel:
        oldestMs === null
          ? "unknown"
          : oldestItem?.dateOnly
          ? sessionAgeLabel(oldestMs)
          : humanizeAgeMs(Date.now() - oldestMs),
      staleCount,
      weekend,
      anyStale: staleCount > 0,
    };
  }, [
    canonicalNavAsOf,
    summary?.latest_vix_as_of,
    surfaceSummary?.latest_vix_as_of,
    volSurface?.as_of_trade_date,
  ]);

  const dataSources = useMemo(() => {
    const items = [
      { label: "Greeks", cache: greeks?.cache ?? null, asOf: greeks?.as_of ?? null },
      { label: "Stress", cache: grid?.cache ?? null, asOf: grid?.as_of ?? null },
      { label: "KPIs", cache: kpis?.cache ?? null, asOf: kpis?.as_of ?? null },
      { label: "Surface", cache: surfaceOverview?.cache ?? null, asOf: surfaceOverview?.as_of ?? null },
      { label: "Vol Snapshot", cache: volSurface?.cache ?? null, asOf: volSurface?.as_of_trade_date ?? null },
    ];
    return items.filter((item) => item.cache || item.asOf);
  }, [greeks?.as_of, greeks?.cache, grid?.as_of, grid?.cache, kpis?.as_of, kpis?.cache, surfaceOverview?.as_of, surfaceOverview?.cache, volSurface?.as_of_trade_date, volSurface?.cache]);

  const degradedSourceCount = useMemo(
    () =>
      dataSources.filter(
        (item) => item.cache?.stale || (item.cache?.source || "").toLowerCase() === "blob_fallback"
      ).length,
    [dataSources]
  );

  const primaryFreshness = useMemo(() => {
    const timestamps = dataSources
      .map((item) => item.cache?.generated_at || item.cache?.as_of || item.asOf || null)
      .filter((value): value is string => Boolean(value));
    if (!timestamps.length) return "—";
    const newest = timestamps.sort().slice(-1)[0];
    return fmtDateTime(newest);
  }, [dataSources]);

  // Greeks data health is a SEPARATE failure from a stale cache. The cache can be
  // perfectly fresh while the numbers inside it were computed from a fabricated IV.
  // Folded into Source Health so the panel can never report "All live" over
  // fabricated greeks -- it previously could, because it only looked at cache
  // staleness and blob fallbacks.
  const greeksHealth = greeks?.data_health ?? null;
  const fabricatedIvCount = greeksHealth?.iv_fabricated_count ?? 0;
  const droppedContractCount = greeksHealth?.dropped_no_inputs_count ?? 0;
  const greeksFabricated = Boolean(greeksHealth?.degraded) || fabricatedIvCount > 0 || droppedContractCount > 0;

  // Market session is the THIRD thing this panel has to know about, alongside cache
  // staleness and greeks health. Without it the panel asserted "All live" in green
  // every pre-open morning over numbers that were the last settled session's -- it was
  // structurally incapable of seeing an unresolved live quote, because market session
  // was not among its inputs. Same class of blind spot the greeks-health fold above
  // fixed, and the backend market-hours gate makes it a nightly certainty rather than
  // an occasional one.
  //
  // Ranked BELOW the fault states deliberately: fabricated greeks or a cache fallback
  // are problems whether or not the market is open, and must not be masked by a closed
  // market. A closed market is not a fault, so it is text-subtle -- neither green
  // (which claims live) nor amber (which claims something is wrong).
  const marketOpen = isMarketOpenAt(Date.now());

  // Vol-surface suspect nodes (backend term_sanity, 2026-10-27): an ATM term point whose
  // total variance kinks against its neighbours -- an impossible curve, i.e. a data
  // problem, so it belongs in this trust summary. Ranked below greeks/fallback faults.
  const surfaceSuspectLabels = volSurface?.term_sanity?.suspect_labels ?? [];
  const surfaceSuspectText = surfaceSuspectLabels
    .map((l) => (l === "5y" ? "5Y+" : l.toUpperCase()))
    .join(", ");
  const surfaceSuspectReason = surfaceSuspectLabels.length
    ? volSurface?.term_sanity?.nodes?.[surfaceSuspectLabels[0]]?.reason ?? null
    : null;

  const sourceHealthLabel = greeksFabricated
    ? fabricatedIvCount > 0
      ? `${fabricatedIvCount} greek${fabricatedIvCount === 1 ? "" : "s"} estimated`
      : `${droppedContractCount} contract${droppedContractCount === 1 ? "" : "s"} dropped`
    : degradedSourceCount > 0
      ? `${degradedSourceCount} fallback${degradedSourceCount === 1 ? "" : "s"}`
      : surfaceSuspectLabels.length > 0
        ? `Surface ${surfaceSuspectText} suspect`
        : marketOpen
          ? "All live"
          : "As of last close";

  // A failed refresh used to leave this tile reading "All live" in green directly above
  // figures frozen at the last successful load. The banner alone is not enough — it
  // scrolls away, and this strip is what the page offers as its trust summary.
  const sourceHealthTone =
    error || greeksFabricated || degradedSourceCount > 0 || surfaceSuspectLabels.length > 0
      ? "text-warn"
      : marketOpen
        ? "text-up"
        : "text-subtle";

  const sourceHealthSubtextBase = greeksFabricated
    ? greeksHealth?.note ||
      (fabricatedIvCount > 0
        ? `${fabricatedIvCount} of ${greeksHealth?.contracts_total ?? "?"} contracts had no observed IV and were priced at a placeholder ${
            greeksHealth?.fallback_iv_used != null
              ? `${(greeksHealth.fallback_iv_used * 100).toFixed(0)}%`
              : "20%"
          } vol — vega and stress loss are understated`
        : `${droppedContractCount} held contract(s) dropped for missing inputs — the held count below is short`)
    : degradedSourceCount > 0
      ? "One or more views are being served from preflight snapshot fallback"
      : marketOpen
        ? "Live services are responding across the risk stack"
        : "Market closed — book greeks and vol levels are the last settled session's, not a live tick";

  // A suspect surface node is reported even when a higher-ranked fault owns the label.
  const surfaceSuspectSuffix = surfaceSuspectLabels.length
    ? `Vol surface ${surfaceSuspectText} suspect${surfaceSuspectReason ? ` (${surfaceSuspectReason})` : ""}; flagged only — percentiles, regime and the screener still use it`
    : null;
  const sourceHealthSubtextWithSurface =
    surfaceSuspectSuffix && !greeksFabricated && degradedSourceCount === 0
      ? surfaceSuspectSuffix
      : surfaceSuspectSuffix
        ? `${sourceHealthSubtextBase} • ${surfaceSuspectSuffix}`
        : sourceHealthSubtextBase;

  const sourceHealthSubtext = error
    ? `${sourceHealthSubtextWithSurface} • last refresh failed`
    : sourceHealthSubtextWithSurface;

  const baseCell = useMemo(() => {
    if (!heatmapCells.length) return null;
    const baseVolTarget =
      axisMode === "vix"
        ? isNum(currentVix)
          ? currentVix
          : null
        : 0;

    return heatmapCells.reduce<GridCell | null>((best, cell) => {
      if (cell.spot !== 0) return best;
      if (baseVolTarget === null) return best ?? cell;
      if (!best) return cell;
      return Math.abs(cell.vol - baseVolTarget) < Math.abs(best.vol - baseVolTarget)
        ? cell
        : best;
    }, null);
  }, [axisMode, currentVix, heatmapCells]);

  const worstViewCell = useMemo(() => {
    if (!heatmapCells.length) return null;
    return heatmapCells.reduce((worst, cell) =>
      cell.pnl_usd < worst.pnl_usd ? cell : worst
    );
  }, [heatmapCells]);

  // The tiles read ONE stress-grid cell each, and "+5" lands on the nearest grid row
  // (about VIX +4; +4 vol pts on the IV axis). Label with the row the backend actually
  // used (kpis.scenario_cells); the nominal wording is only a fallback for older payloads.
  const scenarioCells = kpis?.scenario_cells ?? null;
  const scenarioVolText = (c: ScenarioCellCoord | null | undefined): string | null => {
    if (!c || !isNum(c.vol_level)) return null;
    if (scenarioCells?.axis_mode === "vix") {
      if (!isNum(c.vol_from)) return `VIX ${c.vol_level}`;
      if (Math.round(c.vol_from as number) === c.vol_level) return `VIX flat (${c.vol_level})`;
      return `VIX ${(c.vol_from as number).toFixed(1)} → ${c.vol_level}`;
    }
    const pts = c.vol_level as number;
    return pts === 0 ? "flat vol" : `${pts > 0 ? "+" : ""}${pts} vol pts`;
  };
  const cushionVol = scenarioVolText(scenarioCells?.vega_cushion);
  const tailFlatVol = scenarioVolText(scenarioCells?.right_tail_flat);
  const tailDownVol = scenarioVolText(scenarioCells?.right_tail_minus5);

  const vegaCushionLabel = cushionVol
    ? `Scenario: ${scenarioCells?.vega_cushion?.spot_pct ?? -10}% spot / ${cushionVol}`
    : axisMode === "vix"
      ? "Scenario: -10% spot / VIX +5"
      : "Scenario: -10% spot / +5 vol pts";

  const rightTailLabel =
    tailFlatVol && tailDownVol
      ? `+${scenarioCells?.right_tail_flat?.spot_pct ?? 20}% spot / ${tailFlatVol} vs ${tailDownVol}`
      : axisMode === "vix"
        ? "+20% spot / VIX flat vs VIX -5"
        : "+20% spot / flat vol vs -5 vol";

  const surfaceValue = useMemo(
    () => surfaceValueFromCommentary(surfaceOverview?.commentary),
    [surfaceOverview?.commentary]
  );

  const primaryBudgetRatio =
    kpis?.dynamic_vs_budget_ratio ?? kpis?.vs_budget_ratio ?? null;

  const posture = useMemo(
    () =>
      riskPostureLabel(
        primaryBudgetRatio,
        surfaceSummary?.mismatch_flag,
        surfaceSummary?.deployment_stance
      ),
    [
      primaryBudgetRatio,
      surfaceSummary?.deployment_stance,
      surfaceSummary?.mismatch_flag,
    ]
  );

  const biggestRisk = useMemo(() => {
    if (isNum(primaryBudgetRatio) && primaryBudgetRatio > 1.1) {
      return `${kpis?.dynamic_vs_budget_ratio != null ? "Dynamic" : "Worst-case"} fund impact is running ${fmtNum(
        primaryBudgetRatio,
        2
      )}x the drawdown budget.`;
    }
    if (surfaceSummary?.mismatch_flag) {
      return `Book is not centered in the current sweet spot—primary bucket ${bucketLabel(
        surfaceSummary.portfolio_primary_bucket
      )} vs best bucket ${bucketLabel(surfaceSummary.best_deployment_bucket)}.`;
    }
    if (riskFlags.length) {
      return riskFlags[0];
    }
    return "No acute book-level risk flags; focus shifts to scenario monitoring and bucket mix discipline.";
  }, [
    kpis?.dynamic_vs_budget_ratio,
    primaryBudgetRatio,
    riskFlags,
    surfaceSummary?.best_deployment_bucket,
    surfaceSummary?.mismatch_flag,
    surfaceSummary?.portfolio_primary_bucket,
  ]);

  const stressNarrative = useMemo(() => {
    const selectedWorst = worstViewCell;
    const scenario = worstScenarioLabel(selectedWorst, axisMode);
    const viewLabel = expView === "__PORTFOLIO__" ? "portfolio" : `expiry ${expView}`;
    const budgetText = fmtPct(kpis?.dd_budget_pct ?? null);

    const fundImpactSource =
      kpis?.expected_stress_drawdown_pct_nav ?? kpis?.worst_pct_of_nav ?? null;
    const fundImpactText = isNum(fundImpactSource)
      ? fmtPct(fundImpactSource, 1)
      : "—";

    const sleeveLossText = isNum(selectedWorst?.pnl_pct ?? null)
      ? fmtPct(selectedWorst?.pnl_pct ?? null, 1)
      : fmtPct(kpis?.worst_pct_of_sleeve ?? null, 1);

    return {
      scenario,
      viewLabel,
      estimatedLoss: fmtUSD(
        selectedWorst?.pnl_usd ?? kpis?.worst_cell?.pnl_usd ?? null
      ),
      sleeveLoss: sleeveLossText,
      fundImpact: fundImpactText,
      withinBudget:
        isNum(primaryBudgetRatio) && primaryBudgetRatio <= 1
          ? "Yes"
          : isNum(primaryBudgetRatio)
          ? "No"
          : "Unknown",
      mainDriver: surfaceSummary?.mismatch_flag
        ? `Surface mismatch—book bucket ${bucketLabel(
            surfaceSummary?.portfolio_primary_bucket
          )} is not the best live zone.`
        : `Primary exposure concentrated in ${bucketLabel(
            surfaceSummary?.portfolio_primary_bucket
          )} with ${surfaceValue.toLowerCase()} surface conditions.`,
      budgetText,
    };
  }, [
    axisMode,
    expView,
    kpis?.dd_budget_pct,
    kpis?.expected_stress_drawdown_pct_nav,
    kpis?.worst_cell?.pnl_usd,
    kpis?.worst_pct_of_nav,
    kpis?.worst_pct_of_sleeve,
    primaryBudgetRatio,
    surfaceSummary?.mismatch_flag,
    surfaceSummary?.portfolio_primary_bucket,
    surfaceValue,
    worstViewCell,
  ]);

  const topRiskCards: TopRiskCard[] = [
    {
      title: "Surface Regime (live)",
      value: titleize(surfaceSummary?.surface_regime),
      subtext: `As of ${fmtDateTime(surfaceOverview?.as_of ?? null)}`,
      tone: stanceTone(surfaceSummary?.surface_regime),
    },
    {
      title: "Best Deployment Bucket",
      value: bucketLabel(surfaceSummary?.best_deployment_bucket),
      subtext: `Primary book bucket ${bucketLabel(
        surfaceSummary?.portfolio_primary_bucket
      )}`,
      tone: qualityTone(bestBucketRow?.market_value_pct ?? null),
    },
    {
      title: "Surface Alignment",
      value: fmtPct(surfaceSummary?.surface_alignment_score ?? null),
      subtext: surfaceSummary?.mismatch_flag
        ? "Current book is off-center versus the sweet spot"
        : "Book placement is tracking the live sweet spot",
      tone: qualityTone(surfaceSummary?.surface_alignment_score ?? null),
    },
    {
      title:
        kpis?.dynamic_vs_budget_ratio != null
          ? "Dynamic Budget Utilization"
          : "Budget Utilization",
      value: isNum(primaryBudgetRatio) ? `${fmtNum(primaryBudgetRatio, 2)}x` : "—",
      subtext: `Budget ${fmtPct(kpis?.dd_budget_pct ?? null)} of NAV (from LEAPS target) • expected stress loss ${fmtPct(
        (kpis?.expected_stress_drawdown_pct_nav ?? kpis?.worst_pct_of_nav) ?? null
      )}`,
      tone: ratioTone(primaryBudgetRatio),
    },
  ];

  const showDynamicRiskCards =
    kpis?.expected_stress_drawdown_pct_nav != null ||
    kpis?.structural_risk_pct_nav != null ||
    kpis?.convexity_benefit_pct_nav != null ||
    kpis?.recovery_probability_12m != null;

  const volCoverage = volSurface?.atm_term_structure_details?._coverage;
  // Deep-tenor (4-5.5yr) extension — display-only, isolated from the screener.
  const deepNodes = (volSurface?.deep_term_structure ?? []).filter(
    (n): n is VolSurfaceDeepNode => n != null
  );
  const deepUsable = deepNodes.filter((n) => !!n.usable && isNum(n.atm_iv ?? null));
  const deepCoverageOk = !!volSurface?.deep_end_coverage_ok;
  // The 5y node is the data edge (real coverage runs out ~5.4yr / Dec-2031), so it
  // reads as "5Y+" rather than a precise 5-year point.
  const deepDisplayLabel = (n: VolSurfaceDeepNode) =>
    n.tenor_days === 1825 ? "5y+" : (n.label ?? `${n.tenor_days}d`);
  // Suspect nodes (backend term_sanity, FLAG ONLY): drawn off the IV line as a hollow red
  // marker and left out of the percentile curve (a rank of an impossible point is noise).
  // The backend percentiles and regime are unchanged -- this is display only.
  // Shape percentiles (backend history_views.shape): where today's slope / skew spread
  // sits in the stored history -- the same rank rule as the term cards.
  const historyViews = volSurface?.history_views?.available ? volSurface.history_views : null;
  const shapePctText = (key: string): string => {
    const p = historyViews?.shape?.[key]?.percentile;
    return isNum(p ?? null) ? ` • ${ordinalPct(p as number)} pctile` : "";
  };

  // Book vega by tenor (item 5, display only): each expiry's vega ($ per vol pt, from
  // risk/greeks by_expiration) is assigned to the NEAREST point on the ATM curve by days
  // to expiry, so the bars sit under the tenor whose vol actually moves them.
  const curveNodeDays: Record<string, number> = {
    "1Y": 365, "18M": 548, "2Y": 730, "30M": 913, "3Y": 1095, "4y": 1460, "5y+": 1825,
  };
  const termCurveVega = (labels: string[]): Array<number | null> => {
    const rows = greeks?.by_expiration ?? [];
    if (!rows.length) return labels.map(() => null);
    const nodes = labels
      .map((label, i) => ({ i, days: curveNodeDays[label.replace(/\*$/, "")] }))
      .filter((n) => isNum(n.days));
    if (!nodes.length) return labels.map(() => null);
    const sums: Array<number | null> = labels.map(() => 0);
    const today = Date.now();
    for (const r of rows) {
      const exp = Date.parse(`${r.expiration}T00:00:00Z`);
      if (!isNum(r.vega) || Number.isNaN(exp)) continue;
      const dte = (exp - today) / 86_400_000;
      const nearest = nodes.reduce((best, n) =>
        Math.abs(n.days - dte) < Math.abs(best.days - dte) ? n : best
      );
      sums[nearest.i] = (sums[nearest.i] ?? 0) + (r.vega as number);
    }
    return sums;
  };

  const termSanity = volSurface?.term_sanity ?? null;
  const suspectLabels = termSanity?.suspect_labels ?? [];
  const suspectNote = (label: string | null | undefined): string | null =>
    label && suspectLabels.includes(label) ? termSanity?.nodes?.[label]?.reason ?? "suspect" : null;
  const suspectDisplay = suspectLabels.map((l) =>
    l === "5y" ? "5Y+" : l.toUpperCase()
  );
  const termCurvePoints = [
    ...VOL_TERM_ORDER.map((key) => ({
      label: termLabel(key),
      value: volSurface?.atm_term_structure?.[key] ?? null,
      suspect: !!suspectNote(key),
      note: suspectNote(key),
    })),
    // Extend the IV curve out to the deep long end (4y, 5y+).
    ...deepUsable.map((n) => ({
      label: deepDisplayLabel(n),
      value: n.atm_iv ?? null,
      suspect: !!suspectNote(n.label),
      note: suspectNote(n.label),
    })),
  ];
  const termPercentilePoints = [
    ...VOL_TERM_ORDER.map((key) => ({
      label: suspectNote(key) ? `${termLabel(key)}*` : termLabel(key),
      value: suspectNote(key) ? null : volSurface?.term_percentiles?.[key] ?? null,
    })),
    // Extend the percentile curve to the same deep long end the IV curve shows, so
    // the two side-by-side charts share an x-axis. Gated on `deepUsable` (not on a
    // non-null percentile) precisely so the axes cannot drift apart: a usable node
    // whose rank is unavailable keeps its tick and renders as a gap.
    ...deepUsable.map((n) => ({
      label: suspectNote(n.label) ? `${deepDisplayLabel(n)}*` : deepDisplayLabel(n),
      value: suspectNote(n.label) ? null : n.percentile ?? null,
    })),
  ];
  // Honesty gauge for the deep ranks. An IV is an IV at any tenor, but a PERCENTILE
  // is only as meaningful as the history behind it. Backend ranks deep and front
  // tenors over the SAME basis, but most historical rows carry no deep IV at all
  // (real 4-5y coverage starts ~2021), so the deep nodes are ranked on a few hundred
  // recent observations while 1Y-3Y rank on the full sample. A 4Y "45th" is
  // therefore not the same statistic as a 1Y "30th", and the card says so rather
  // than letting one continuous line imply otherwise.
  const frontHistoryCounts = VOL_TERM_ORDER.map(
    (key) => volSurface?.term_history_counts?.[key] ?? null
  ).filter(isNum);
  const frontHistoryMin = frontHistoryCounts.length
    ? Math.min(...frontHistoryCounts)
    : null;
  const deepHistoryNote = deepUsable
    .map(
      (n) =>
        `${deepDisplayLabel(n)} ${
          isNum(n.history_count ?? null) ? n.history_count : "—"
        }`
    )
    .join(" • ");
  const skewRows = VOL_SKEW_ORDER.map((key) => ({
    label: skewLabel(key),
    value: volSurface?.near_atm_skew?.[key] ?? null,
    percentile: volSurface?.skew_percentiles?.[key] ?? null,
  }));

  // Manual ORATS backfill escape hatch: if the daily ingestion timer missed a session,
  // an operator can pull recent SPX sessions on demand. We ask the manual ingestion HTTP
  // trigger for each of the last several weekdays; existing days are skipped server-side
  // (overwrite=false) and weekend/holiday dates return no rows, so this is idempotent and
  // safe to re-run. It does NOT change any strategy, risk, or trading logic -- it only
  // refreshes the ORATS market-data blob the risk views read from.
  async function updateOrats() {
    setOratsUpdating(true);
    setOratsStatus(null);
    try {
      const LOOKBACK_CALENDAR_DAYS = 7;
      const now = new Date();
      const dates: string[] = [];
      for (let i = 1; i <= LOOKBACK_CALENDAR_DAYS; i++) {
        const d = new Date(now);
        d.setUTCDate(d.getUTCDate() - i);
        const dow = d.getUTCDay();
        if (dow === 0 || dow === 6) continue; // skip weekends (no session)
        dates.push(d.toISOString().slice(0, 10));
      }

      const backfilled: string[] = [];
      const failed: string[] = [];
      for (const date of dates) {
        try {
          const res = await fetch(`${API_BASE_URL}/run_orats_ingestion`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            cache: "no-store",
            body: JSON.stringify({ symbol: "SPX", trade_date: date }),
          });
          if (!res.ok) {
            failed.push(date);
            continue;
          }
          const payload = await res.json();
          const chunks = payload?.result?.written_chunks;
          const wroteNew =
            Array.isArray(chunks) && chunks.some((c: any) => c?.written === true);
          if (wroteNew) backfilled.push(date);
        } catch {
          failed.push(date);
        }
      }

      let msg =
        backfilled.length > 0
          ? `Backfilled ${backfilled.length} ORATS session(s): ${backfilled.join(", ")}.`
          : "ORATS data already current — no missing sessions in the last week.";
      if (failed.length > 0) {
        msg += ` Could not reach the ingestion service for: ${failed.join(", ")}.`;
      }
      // A partial failure is not a success. The two branches below are the only two
      // places the code knows ingestion did not fully land; both must read as warnings.
      setOratsStatus({ text: msg, tone: failed.length > 0 ? "warn" : "live" });

      // Refresh risk views so any ORATS-derived surface data reflects the new pull.
      await fetchAll(detailContracts);
    } catch (e: any) {
      setOratsStatus({
        text: `ORATS update failed: ${e?.message || "unknown error"}`,
        tone: "warn",
      });
    } finally {
      setOratsUpdating(false);
    }
  }

  return (
    <Layout>
      <div className="max-w-[1880px] mx-auto px-3 sm:px-4 xl:px-5 2xl:px-6 py-3 space-y-6 text-[13px] 2xl:text-[14px] text-[var(--text-color)]">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
          <div>
            <h1 className="text-[2.05rem] font-semibold tracking-tight leading-none">
              Risk Command Center
            </h1>
            <div className="text-[13px] opacity-65 mt-2 max-w-4xl leading-6">
              Institutional view of held convexity, surface alignment, sleeve sizing,
              and scenario stress. The page is organized to answer three questions
              fast—what posture the book is in, where the risk sits, and whether the
              current surface is helping or hurting deployment.
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <HelionButton
              size="sm"
              onClick={() => setMethodologyOpen(true)}
              title="How to read this board — what every tile means, how it is computed, and what it deliberately does not do."
            >
              Methodology
            </HelionButton>
            <HelionButton
              size="sm"
              onClick={() => fetchAll(detailContracts)}
              disabled={loading}
            >
              {loading ? "Refreshing..." : "Refresh"}
            </HelionButton>
            <HelionButton
              size="sm"
              onClick={() => updateOrats()}
              disabled={oratsUpdating}
              title="Manually pull recent SPX ORATS sessions into blob storage. Use if the daily ingestion timer missed a day. Idempotent."
            >
              {oratsUpdating ? "Updating ORATS..." : "Update ORATS"}
            </HelionButton>
          </div>
        </div>

        {/* A failed refresh does not clear any state — every figure below stays on screen
            and stays frozen. This used to render the raw exception string alone, with no
            heading and nothing saying the numbers underneath were stale, so the page read
            as fully populated and current. Deliberately NOT clearing the state: blanking
            the page on a transient failure is worse, and is a behaviour change. */}
        {error ? (
          <div className="rounded-xl border border-down/40 bg-down/10 px-4 py-3 text-[13px] text-down">
            <span className="font-semibold">Refresh failed.</span> The figures below are
            from the last successful load and have NOT been updated.
            <div className="mt-1 opacity-80">{error}</div>
          </div>
        ) : null}

        {dataHealth.anyStale || dataHealth.weekend ? (
          <div className="rounded-xl border border-warn/40 bg-warn/10 px-4 py-3 text-[13px] text-warn">
            <span className="font-semibold">Data recency notice:</span>{" "}
            {dataHealth.anyStale
              ? `${dataHealth.items
                  .filter((i) => i.stale)
                  .map((i) => i.key)
                  .join(", ")} not confirmed current. `
              : ""}
            {dataHealth.weekend
              ? "Weekend / off-session — figures reflect the last available data. "
              : ""}
            Headline timestamps show when the page was <em>computed</em>, not when the underlying
            data was sourced. Check the data dates in the Service Status panel below.
          </div>
        ) : null}

        {oratsStatus ? (
          <div
            className={
              oratsStatus.tone === "warn"
                ? "rounded-xl border border-warn/40 bg-warn/10 px-4 py-3 text-[13px] text-warn"
                : "rounded-xl border border-live/40 bg-live/10 px-4 py-3 text-[13px] text-live"
            }
          >
            {oratsStatus.tone === "warn" ? (
              <span className="font-semibold">ORATS ingestion incomplete. </span>
            ) : null}
            {oratsStatus.text}
          </div>
        ) : null}

        <QuickNav items={navItems} />

        <section id="risk-sec-trust" className="scroll-mt-20 space-y-6">
        <div id="risk-overview" className="scroll-mt-20 space-y-2">
        <div className="text-[11px] uppercase tracking-[0.16em] opacity-55">
          Input freshness and display settings
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
          <StatCard
            title="Data Age (oldest input)"
            value={dataHealth.oldestLabel}
            subtext={
              (dataHealth.anyStale
                ? `${dataHealth.staleCount} input${dataHealth.staleCount === 1 ? "" : "s"} stale`
                : "Inputs current") +
              (dataHealth.weekend ? " • weekend (no new session)" : "") +
              ` • computed ${primaryFreshness}`
            }
            toneClassName={
              dataHealth.anyStale
                ? "text-down"
                : "text-up"
            }
          />
          <StatCard
            title="Source Health"
            value={sourceHealthLabel}
            subtext={sourceHealthSubtext}
            toneClassName={sourceHealthTone}
          />
          <StatCard
            title="Render Mode"
            value={mode === "reprice" ? "Full Reprice" : "Greek Expansion"}
            subtext={`Axis ${axisMode === "vix" ? "VIX-aware" : "Raw IV"} • Horizon ${
              horizonMonths === 0 ? "Now" : `${horizonMonths}m`
            }${grid?.rate_shock && grid.rate_shock.bp !== 0 ? ` • ${grid.rate_shock.label}` : ""}`}
          />
          <StatCard
            title="Contract Drilldown"
            value={detailContracts ? "Enabled" : "Off"}
            subtext={detailContracts ? "Worst-cell contributors shown below" : "Cleaner executive view"}
            toneClassName={detailContracts ? "text-warn" : undefined}
          />
        </div>
        </div>
        </section>

        <section id="risk-sec-posture" className="scroll-mt-20 space-y-6">
        <RiskTakeawayBand
          posture={posture}
          surfaceValue={surfaceValue}
          bestBucket={bucketLabel(surfaceSummary?.best_deployment_bucket)}
          biggestRisk={biggestRisk}
          commentary={surfaceOverview?.commentary}
        />

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
          <BookStanceCard
            leapsMv={canonicalLeapsMarketValue}
            nav={canonicalTotalNav}
            fundedPct={fundedLeapsWeight}
            marketPct={canonicalLeapsPct}
            capPct={fundedCapPct}
            fundedDelta={deltasOk ? snapDeltas?.deltas?.LeapsFundedPct ?? null : null}
            marketDelta={deltasOk ? snapDeltas?.deltas?.LeapsMarketPct ?? null : null}
            deltaWindowLabel={deltaWindowLabel}
            lockout={harvestLockout}
            remainingToDeploy={remainingToDeploy}
          />
          <AttentionPane
            watchlist={watchlist}
            summary={watchlistSummary}
            heldLeapsCount={heldLeapsCount}
            deltaNote={watchlistDeltaNote}
            loaded={heldLeapsCount > 0}
          />
        </div>

        <div id="risk-watchlist" className="scroll-mt-20">
          <SectionCard
            title="Exit / Harvest Watchlist"
            subtitle="Positions the exit model flags for attention — by its posture call (Review today / Monitor closely / Act now) or a hard-exit, peak, or home-run signal. Healthy holds are hidden; ranked by priority. Review-only — nothing trades automatically."
            right={
              <div className="text-[11px] opacity-70 text-right leading-5">
                {watchlist.length} of {heldLeapsCount} flagged
                {watchlistSummary.actNow > 0 ? ` • ${watchlistSummary.actNow} act now` : ""}
                {watchlistSummary.review > 0 ? ` • ${watchlistSummary.review} review today` : ""}
                {watchlistSummary.monitor > 0 ? ` • ${watchlistSummary.monitor} monitor` : ""}
                {/* The "Off Peak" column below is written by a different job on a
                    different clock from the prices beside it. Say which. */}
                <div className={peakSuspect ? "text-warn" : "opacity-70"}>
                  Peak data {peakLabel}
                </div>
              </div>
            }
          >
            {watchlist.length ? (
              <div className="overflow-auto">
                <table className="min-w-[900px] w-full text-[13px] text-[var(--text-color)]">
                  <thead>
                    <tr className="border-b border-[var(--border-color)] bg-[var(--table-header-bg,var(--card-bg))]">
                      <th className="text-left p-3 font-semibold">Position</th>
                      <th className="text-left p-3 font-semibold">Posture</th>
                      <th className="text-left p-3 font-semibold">Why flagged</th>
                      <th className="text-right p-3 font-semibold">DTE</th>
                      <th className="text-right p-3 font-semibold">Return</th>
                      <th className="text-right p-3 font-semibold">Off Peak</th>
                      <th className="text-right p-3 font-semibold">
                        Exit Rank
                        <InfoHint text="Cross-sectional exit-rank (tier · 0-100) vs the rest of today's book — relative, NOT a probability of needing to sell. Combines the rule + model exit signals; the model mainly reflects drawdown/DTE and is least reliable in calm markets. 100 = worst-positioned right now." />
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {watchlist.slice(0, 12).map((r, idx) => (
                      <WatchlistLine
                        key={`${r.contractId}-${r.expiration}-${r.strike ?? "na"}-${idx}`}
                        row={r}
                        idx={idx}
                        thresholds={decisionThresholds}
                        policy={homeRunPolicy}
                        fallbackSpot={isNum(volSurface?.spot_px) ? (volSurface?.spot_px as number) : null}
                        fallbackSpotAsOf={volSurface?.as_of_trade_date ?? null}
                      />
                    ))}
                  </tbody>
                </table>
                {watchlist.length > 12 ? (
                  <div className="mt-2 text-[11px] opacity-60">
                    Showing top 12 of {watchlist.length} flagged. Full queue on the Portfolio page.
                  </div>
                ) : null}
              </div>
            ) : (
              <div className="opacity-60 text-sm">
                No positions currently flagged for exit or harvest review.
              </div>
            )}
          </SectionCard>
        </div>

        <details className="rounded-xl border border-[var(--border-color)] bg-[var(--bg-secondary)] overflow-hidden">
          <summary className="list-none cursor-pointer px-4 py-3 font-semibold flex items-center justify-between text-[13px]">
            <span>How to read this page</span>
            <span className="text-[11px] opacity-60">Open / close</span>
          </summary>
          <div className="px-4 pb-4 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 text-[12px] leading-6 opacity-85">
            <div><span className="font-semibold">Posture / Takeaway</span> — the one-line read on how the book is positioned right now.</div>
            <div><span className="font-semibold">Daily Decay (Theta)</span> — dollars the long-LEAPS book loses per day to time, all else equal.</div>
            <div><span className="font-semibold">Drawdown</span> — how far NAV is below its high-water mark.</div>
            <div><span className="font-semibold">Exit / Harvest Watchlist</span> — positions flagged for profit-take, trailing-stop, or maturity review. The most actionable list.</div>
            <div><span className="font-semibold">Stress Heatmap</span> — re-prices the <em>LEAPS sleeve only</em> across spot &amp; volatility moves. Fixed income is not shocked (see note on those tiles).</div>
            <div><span className="font-semibold">Expected / Worst stress</span> — modeled loss across a fixed set of scenarios, expressed as % of NAV. Directional, not a statistical VaR.</div>
            <div><span className="font-semibold">Surface</span> — whether long-dated index volatility is cheap or rich, and where convexity is most attractive to deploy.</div>
            <div><span className="font-semibold">Greeks</span> — delta (direction), vega (volatility), theta (decay), gamma/vanna/vomma (curvature). USD-scaled to the whole book.</div>
          </div>
        </details>
        </section>

        <section id="risk-sec-nav" className="scroll-mt-20 space-y-6">
        <SectionCard
          title="Net Asset Value & Reconciliation"
          subtitle="Live total = IBKR broker book + off-broker bank cash + net accruals. The administrator strikes the official NAV monthly; the cross-check ties that official figure to (broker + bank + accruals) at the strike date."
        >
        <div className="space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
          {/* Estimated return since the administrator's last official strike. Shared
              component (also on Portfolio + Signals) so one figure cannot disagree with
              itself across pages. Reads the reconciliation block already fetched above —
              no extra request, and notably no ?persist=1 write to dbo.PortfolioNAV. */}
          <PeriodReturnTile reconciliation={navRecon} />
          <StatCard
            title="Total NAV (now)"
            value={fmtUSD(navRecon?.daily?.estimated_nav_now ?? canonicalTotalNav)}
            subtext={
              <>
                {/* The "% since strike" here mirrors the PeriodReturnTile's flow-aware
                    basis (lib/navPerformance.flowAwareChangePct) so the two surfaces
                    cannot disagree. Withheld when the flow ledger cannot stand behind
                    a percentage; "incl. capital flows" only applies pre-ledger. */}
                {navRecon?.daily?.estimated_nav_now != null
                  ? navRecon.daily.overlay_reliable === false
                    ? `IBKR ${fmtUSD(navRecon.daily.ibkr_live_nav ?? null)} + off-broker overlay ${fmtUSD(
                        navRecon.daily.admin_overlay_usd ?? null
                      )} (bank & accruals pending administrator close) • ${
                        navRecon.daily.flow_state === "suppressed"
                          ? "Δ% withheld (capital flows)"
                          : fmtPct(flowAwareChangePct(navRecon.daily), 2)
                      } since the ${navRecon.daily.last_official_date ?? "—"} strike${
                        !navRecon.daily.flow_state && navRecon.daily.change_may_include_flows
                          ? ", incl. capital flows"
                          : ""
                      } (${fmtUSD(navRecon.daily.last_official_nav ?? null)})`
                    : `IBKR ${fmtUSD(navRecon.daily.ibkr_live_nav ?? null)} + bank ${fmtUSD(
                        navRecon.daily.bank_cash ?? null
                      )} + accruals ${fmtUSD(navRecon.daily.accruals_est_usd ?? null)} • ${
                        navRecon.daily.flow_state === "suppressed"
                          ? "Δ% withheld (capital flows)"
                          : fmtPct(flowAwareChangePct(navRecon.daily), 2)
                      } since the ${
                        navRecon.daily.last_official_date ?? "—"
                      } official strike (${fmtUSD(navRecon.daily.last_official_nav ?? null)})`
                  : `Broker NAV • as of ${fmtDateTime(canonicalNavAsOf)} (off-broker bank cash not loaded)`}
                {renderDelta("TotalNAV", "usd", undefined, {
                  window: "vs prior close · broker NAV",
                  title:
                    "Broker-aggregate NAV, close-over-close. Excludes intraday bank cash + accruals that the live headline above includes.",
                })}
              </>
            }
          />
          <StatCard
            title="IBKR NAV (live)"
            value={fmtUSD(navRecon?.daily?.ibkr_live_nav ?? canonicalTotalNav)}
            subtext={`Broker account — positions + IBKR cash • as of ${fmtDateTime(canonicalNavAsOf)}`}
            toneClassName="text-subtle"
          />
          <StatCard
            title="LEAPS Sleeve"
            value={fmtPct(canonicalLeapsPct)}
            subtext={<>{`MV ${fmtUSD(canonicalLeapsMarketValue)} · of IBKR NAV`}{renderDelta("LeapsMarketPct", "pp", 1)}</>}
          />
          <StatCard
            title="Fixed Income Sleeve"
            value={fmtPct(canonicalFixedIncomePct)}
            subtext={<>{`MV ${fmtUSD(canonicalFixedIncomeMarketValue)} · of IBKR NAV`}{renderDelta("FixedIncomePct", "pp", 1)}</>}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
          <StatCard
            title="Cash Posture"
            /* The HEADLINE has to agree with the disclosure beneath it. This tile used to
               read "Fully Deployable" in its big text while the line under it said a
               harvest lockout was withholding capital — the netted figure was right and
               the headline contradicted it, which is worse than not mentioning the lockout
               at all. One derivation now decides both (lib/harvestLockout.ts). */
            value={deployabilityHeadline(harvestLockout, {
              reserved: cashOverride?.mode === "dashboard_reserved_cash" || !!cashOverride?.enabled,
              reservedLabel: "Cash Reserved",
              freeLabel: "Fully Deployable",
            })}
            subtext={
              <>
                {/* "Deployable" is NET of an active harvest lockout. Harvested proceeds sit
                    in this same cash balance, and the engine withholds them from the buy
                    budget, so the gross figure overstated what is free. When the lockout
                    cannot be substantiated the gross figure is kept but explicitly labelled
                    as pre-lockout — never presented as confirmed. */}
                {isNum(cashOverride?.reserved_cash ?? null)
                  ? `Reserved ${fmtUSD(cashOverride?.reserved_cash ?? null)} • Deployable ${
                      deployableCash.unknown
                        ? `${fmtUSD(deployableCash.gross)} (pre-lockout)`
                        : fmtUSD(deployableCash.net)
                    }`
                  : "Cash override status from latest NAV"}
                {harvestLockout.status === "none" ? null : (
                  <div className="mt-1">{deployableCash.disclosure}</div>
                )}
              </>
            }
          />
          <StatCard
            title="Capital Deployed"
            value={fmtPct(cashOverride?.deployment_pct ?? null)}
            subtext={
              isNum(cashOverride?.deployed_capital ?? null)
                ? `${fmtUSD(cashOverride?.deployed_capital ?? null)} at risk`
                : "Share of NAV deployed vs reserved"
            }
          />
          <StatCard
            title="NAV Reconciliation"
            value={
              navRecon?.break_flag
                ? "Break"
                : navRecon?.components_available
                ? "In Tolerance"
                : "Components N/A"
            }
            subtext={
              navRecon?.break_flag && isNum(navRecon?.difference_usd ?? null)
                ? `Broker vs components differ ${fmtUSD(navRecon?.difference_usd ?? null)}`
                : navRecon?.components_available
                ? navRecon?.note || "Broker NAV vs sum-of-components"
                : "LEAPS/FI/cash not itemized on this row — total NAV is cross-checked vs the administrator (→)"
            }
            toneClassName={
              navRecon?.break_flag
                ? "text-down"
                : navRecon?.components_available
                ? "text-up"
                : "text-subtle"
            }
          />
          <StatCard
            title="Administrator Cross-Check"
            value={
              navRecon?.administrator
                ? navRecon.administrator.break_flag
                  ? "Break"
                  : navRecon.administrator.bank_cash_reliable === false
                  ? "Pending"
                  : "Reconciled"
                : navRecon?.administrator_crosscheck
                ? "Active"
                : "None"
            }
            subtext={
              navRecon?.administrator
                ? navRecon.administrator.bank_cash_reliable === false
                  ? `Official ${fmtUSD(navRecon.administrator.admin_nav ?? null)} (${
                      navRecon.administrator.admin_as_of ?? "—"
                    }) vs broker@strike ${fmtUSD(
                      navRecon.administrator.broker_nav_at_date ?? null
                    )} — off-broker overlay ${fmtUSD(
                      navRecon.administrator.difference_usd ?? null
                    )} (${fmtPct(Math.abs(navRecon.administrator.difference_pct ?? 0))} of NAV); bank/accruals split pending administrator close`
                  : `Official ${fmtUSD(navRecon.administrator.admin_nav ?? null)} (${
                      navRecon.administrator.admin_as_of ?? "—"
                    }) = broker@strike ${fmtUSD(
                      navRecon.administrator.broker_nav_at_date ?? null
                    )} + bank ${fmtUSD(navRecon.administrator.bank_cash ?? null)} + accruals ${fmtUSD(
                      navRecon.administrator.accruals_est_usd ?? null
                    )} (${fmtPct(Math.abs(navRecon.administrator.difference_pct ?? 0))} of NAV)`
                : "NAV Fund Services — broker NAV not yet cross-checked."
            }
            toneClassName={
              navRecon?.administrator?.break_flag
                ? "text-down"
                : navRecon?.administrator?.bank_cash_reliable === false
                ? "text-warn"
                : navRecon?.administrator
                ? "text-up"
                : "text-warn"
            }
          />
        </div>
        </div>
        </SectionCard>
        </section>

        <section id="risk-sec-regime" className="scroll-mt-20 space-y-6">
        <div className="space-y-2">
        <div className="text-[11px] uppercase tracking-[0.16em] opacity-55">
          Volatility regime and sleeve sizing
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
          <StatCard
            title="VIX"
            value={fmtNum(
              summary?.current_vix ?? surfaceSummary?.current_vix ?? null,
              2
            )}
            subtext={
              <>
                {`z-score ${fmtNum(
                  summary?.vix_zscore ?? surfaceSummary?.vix_zscore ?? null,
                  2
                )} • as of ${fmtDateTime(
                  summary?.latest_vix_as_of ?? surfaceSummary?.latest_vix_as_of ?? null
                )}`}
                {renderDelta("CurrentVIX", "num", 2)}
              </>
            }
          />
          <StatCard
            title="Base Regime"
            value={summary?.current_band || "—"}
            subtext={`Phase ${summary?.current_phase || "—"}`}
          />
          <StatCard
            title="Effective Target"
            value={fmtPct(
              summary?.effective_target_leaps_pct ?? summary?.target_leaps_pct ?? null
            )}
            subtext={`Base ${fmtPct(
              summary?.base_target_leaps_pct ?? null
            )} • Regime cap ${fmtPct(summary?.max_leaps_pct ?? null)}`}
          />
          <StatCard
            title="Drawdown"
            value={fmtPct(summary?.portfolio_drawdown_pct ?? null)}
            subtext={<>{drawdownSubtext}{renderDelta("PortfolioDrawdownPct", "pp", 1)}</>}
          />
          <StatCard
            title="Defense Overlay"
            // Lead with WHICH leg is driving, not a generic "Active". defense_overlay_label
            // is built backend-side and reads "Grind-Defense", "Grind-Defense (armed)",
            // the spike band name, or both joined.
            value={
              summary?.defense_overlay_active
                ? summary?.defense_overlay_label || summary?.effective_regime || "Active"
                : "Inactive"
            }
            subtext={(() => {
              const g = summary?.defense_grind;
              if (summary?.defense_grind_applied && g) {
                // The validated lever is driving — say so, and say why.
                // F2: pct_from_sma200 is ALREADY a percent — strategy/rebalance_logic.py:473
                // returns round((last/sma - 1) * 100, 2). The extra *100 here rendered
                // "-425.0% vs 200d" for a -4.25% distance. Matches the correct sibling render
                // on SignalsDashboard.tsx:2654/2731, including its Number.isFinite guard.
                const dist =
                  typeof g.pct_from_sma200 === "number" && Number.isFinite(g.pct_from_sma200)
                    ? `${g.pct_from_sma200.toFixed(1)}% vs 200d`
                    : null;
                const days =
                  typeof g.days_below_200_of_window === "number" && g.persist_window
                    ? `${g.days_below_200_of_window}/${g.persist_window}d below`
                    : null;
                const tgt =
                  typeof g.target_pct === "number" ? `target ${(g.target_pct * 100).toFixed(0)}%` : null;
                return [dist, days, tgt].filter(Boolean).join(" · ") || "Grind defense driving target";
              }
              if (summary?.defense_grind_active) return "Grind signal armed — not yet driving target";
              if (summary?.defense_spike_active)
                return `Block ${String(summary?.block_reason || "confirmed").replace(/_/g, " ")}`;
              // If the regime block came from the FALLBACK path the rebalance engine was
              // never consulted, so "Inactive" above is unknown, not verified-off.
              if (summary?.regime_context_source === "fallback")
                return "Regime from fallback — rebalance engine not consulted, defense state UNKNOWN";
              return `Target set by ${titleize(
                String(summary?.target_source || "base_regime").replace(/_/g, " ")
              )}`;
            })()}
          />
        </div>
        </div>

        <SectionCard
          title="Volatility Stabilization"
          subtitle="Informational only—smoothed 3-day versus 10-day VIX trend with whipsaw awareness. This does not drive any trading logic."
          right={
            <div
              className={`inline-flex items-center rounded-full border px-2.5 py-1 text-[11px] font-semibold ${volatilityDirectionSummary.badgeClass}`}
            >
              {volatilityDirectionSummary.headline}
            </div>
          }
        >
          <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_0.9fr] gap-3">
            <div className="space-y-3">
              <div className={`text-[1.35rem] font-semibold ${volatilityDirectionSummary.tone}`}>
                {volatilityDirectionSummary.headline}
              </div>
              <div className="text-[12px] opacity-75 leading-6">
                {volatilityStabilization?.message ||
                  "No stabilization commentary available."}
              </div>
              <div className="text-[11px] opacity-65 leading-5">
                {volatilityDirectionSummary.subtext || "No stabilization detail available."}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <MiniNarrativeCard
                label="Short vs Medium Trend"
                value={`${fmtNum(volatilityStabilization?.short_vix_avg ?? null, 2)} / ${fmtNum(
                  volatilityStabilization?.medium_vix_avg ?? null,
                  2
                )}`}
                subtext={`${volatilityStabilization?.short_window ?? 3}d vs ${
                  volatilityStabilization?.medium_window ?? 10
                }d average`}
                toneClassName={volatilityDirectionSummary.tone}
              />
              <MiniNarrativeCard
                label="Trend Spread"
                value={fmtPct(volatilityStabilization?.spread_pct ?? null, 1)}
                subtext={`z spread ${fmtNum(volatilityStabilization?.z_spread ?? null, 2)}`}
                toneClassName={volatilityDirectionSummary.tone}
              />
              <MiniNarrativeCard
                label="Whipsaw Risk"
                value={volatilityStabilization?.whipsaw_flag ? "Elevated" : "Contained"}
                subtext={`${volatilityStabilization?.signal_flips_recent ?? 0} recent flips`}
                toneClassName={
                  volatilityStabilization?.whipsaw_flag
                    ? "text-warn"
                    : "text-up"
                }
              />
              <MiniNarrativeCard
                label="Current VIX Context"
                value={`${fmtNum(volatilityStabilization?.current_vix ?? null, 2)} | z ${fmtNum(
                  volatilityStabilization?.current_vix_zscore ?? null,
                  2
                )}`}
                subtext={`As of ${fmtDateTime(volatilityStabilization?.latest_vix_as_of ?? null)}`}
              />
            </div>
          </div>
        </SectionCard>
        </section>

        <section id="risk-sec-book" className="scroll-mt-20 space-y-6">
        <div id="risk-long-end-vol" className="scroll-mt-20">
          <SectionCard
            title="Long-End Vol & Vega"
            subtitle="The vol the book actually lives on. LEAPS price off LONG-DATED vol (~VIX3M), not spot 30-day VIX — so the regime dial and the daily marks can move in opposite directions. Net vega is shown as $ per 1 vol point. Read-only; estimates only."
            right={
              <div className="text-[11px] opacity-70 text-right leading-5">
                {longEndVol?.cockpit?.day_over_day?.prior_as_of
                  ? `vs prior close (${longEndVol.cockpit.day_over_day.prior_as_of})`
                  : "live"}
                {longEndVol?.as_of ? (
                  <div className="opacity-60">{fmtDateTime(longEndVol.as_of)}</div>
                ) : null}
              </div>
            }
          >
            {(() => {
              const lev = longEndVol?.cockpit ?? null;
              const attr = longEndVol?.attribution ?? null;
              if (!lev) {
                return (
                  <div className="text-[12px] opacity-60 leading-5">
                    Long-end vol cockpit unavailable.
                    {longEndVol?.errors?.length
                      ? ` (${longEndVol.errors.join(", ")})`
                      : " Waiting on live greeks / VIX3M."}
                  </div>
                );
              }
              const dod = lev.day_over_day ?? null;
              const tsLabel = lev.term_structure_label
                ? lev.term_structure_label.replace(/_/g, " ")
                : "—";
              const pnlTone = (x?: number | null) =>
                !isNum(x)
                  ? "text-[var(--text-color)] opacity-60"
                  : x > 0
                  ? "text-up"
                  : x < 0
                  ? "text-down"
                  : "text-[var(--text-color)] opacity-70";
              const divLabel: Record<string, string> = {
                spot_up_long_down: "Spot up · long end down",
                spot_down_long_up: "Spot down · long end up",
                aligned: "Moving together",
                quiet: "Quiet",
              };
              const divTone: Record<string, string> = {
                spot_up_long_down:
                  "border-warn/40 bg-warn/10 text-warn",
                spot_down_long_up:
                  "border-warn/40 bg-warn/10 text-warn",
                aligned:
                  "border-[var(--border-color)] bg-[var(--bg-secondary)] opacity-80",
                quiet:
                  "border-[var(--border-color)] bg-[var(--bg-secondary)] opacity-70",
              };
              const Tile = ({
                label,
                value,
                sub,
                valueClass,
              }: {
                label: string;
                value: React.ReactNode;
                sub?: React.ReactNode;
                valueClass?: string;
              }) => (
                <div className="rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] p-2.5">
                  <div className="text-[10px] uppercase tracking-wide opacity-60 leading-4">
                    {label}
                  </div>
                  <div className={`text-[16px] font-semibold leading-tight mt-0.5 ${valueClass || ""}`}>
                    {value}
                  </div>
                  {sub ? <div className="text-[11px] opacity-60 leading-4 mt-0.5">{sub}</div> : null}
                </div>
              );
              return (
                <div className="space-y-3">
                  {/* Vol levels: spot vs long end (where the book lives) */}
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5">
                    <Tile label="Spot VIX (30d)" value={fmtNum(lev.spot_vix, 2)} sub="Regime dial basis" />
                    <Tile label="VIX3M (long end)" value={fmtNum(lev.vix3m, 2)} sub="Market 3-month vol" />
                    <Tile
                      label="Book ATM IV"
                      value={fmtNum(lev.book_iv, 2)}
                      sub={
                        isNum(lev.book_iv_minus_spot_vix)
                          ? `${fmtSignedNum(lev.book_iv_minus_spot_vix, 2)} vs spot VIX · vega-weighted`
                          : "Vega-weighted, the vol the book lives on"
                      }
                    />
                    <Tile
                      label="Term structure"
                      value={isNum(lev.term_structure_ratio) ? fmtNum(lev.term_structure_ratio, 3) : "—"}
                      sub={`spot / 3M · ${tsLabel}`}
                    />
                  </div>

                  {/* Book first-order $ sensitivities — SHARED component, same live source
                      (risk/long_end_vol) as the Signals Intraday Book Exposure card, so net
                      delta/vega/theta can never disagree across the two dashboards. */}
                  <BookExposureGreeks
                    data={{
                      deltaDollarPer1pct:
                        isNum(lev.net_delta) && isNum(lev.spot_level)
                          ? lev.net_delta * lev.spot_level * 0.01
                          : null,
                      vegaPerPoint: lev.net_vega_per_pt ?? null,
                      thetaPerDay: lev.net_theta_per_day ?? null,
                    }}
                    asOf={longEndVol?.as_of}
                    showFootnote={false}
                  />

                  {/* Day-over-day move vs prior close */}
                  <div className="grid grid-cols-3 gap-2.5">
                    <Tile
                      label="Spot VIX Δ"
                      value={fmtSignedNum(dod?.spot_vix_change, 2)}
                      sub={dod?.prior_as_of ? `since ${dod.prior_as_of}` : "vs prior close"}
                    />
                    <Tile
                      label="Long-end Δ"
                      value={fmtSignedNum(dod?.long_end_change, 2)}
                      sub={
                        dod?.divergence_flag ? (
                          <span
                            className={`inline-block text-[9px] uppercase tracking-wide rounded-full px-2 py-0.5 border ${
                              divTone[dod.divergence_flag] || divTone.aligned
                            }`}
                          >
                            {divLabel[dod.divergence_flag] || dod.divergence_flag}
                          </span>
                        ) : (
                          `${lev.long_end_basis === "vix3m" ? "VIX3M" : "book IV"} basis`
                        )
                      }
                    />
                    <Tile
                      label="Implied vega P&L"
                      value={fmtSignedUSD(dod?.implied_vega_pnl, 0)}
                      sub="net vega × long-end move"
                      valueClass={pnlTone(dod?.implied_vega_pnl)}
                    />
                  </div>

                  {/* Daily P&L attribution — the 2026-06-24 reconciliation, automated */}
                  {attr ? (
                    <div className="rounded-lg border border-[var(--border-color)] p-3">
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-[12px] font-semibold">
                          What moved the book{" "}
                          <span className="opacity-60 font-normal">
                            (estimate{attr.prior_as_of ? `, vs ${attr.prior_as_of} close` : ""})
                          </span>
                        </div>
                        <InfoHint text="Close-to-now greek decomposition of the book's move: delta = prior share-delta × index move; theta = prior daily decay × days; vega = prior net $/vol-pt × long-end vol move. Residual = actual brokerage day P&L − estimate, which absorbs the FI sleeve plus gamma/vanna/basis. Delta & vega terms light up once the migration adds the prior-close vol columns and one preflight writes them. Estimates only — NAV Fund Services is the official source." />
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 text-[13px]">
                        {[
                          { k: "Delta", v: attr.delta_pnl },
                          { k: "Theta", v: attr.theta_pnl },
                          { k: "Vega", v: attr.vega_pnl },
                          { k: "Est. total", v: attr.estimated_total },
                          { k: "Actual day P&L", v: attr.actual_day_pnl },
                          { k: "Residual (FI + basis)", v: attr.residual },
                        ].map((c) => (
                          <div key={c.k} className="rounded-md bg-[var(--bg-secondary)] p-2">
                            <div className="text-[10px] uppercase tracking-wide opacity-60 leading-4">
                              {c.k}
                            </div>
                            <div className={`text-[14px] font-semibold leading-tight ${pnlTone(c.v)}`}>
                              {isNum(c.v) ? fmtSignedUSD(c.v, 0) : "n/a"}
                            </div>
                          </div>
                        ))}
                      </div>
                      {longEndVol?.errors?.includes("intraday_pnl_deferred_for_fill_capture") ? (
                        <div className="text-[11px] opacity-60 mt-2 leading-4">
                          Actual day P&amp;L was not read this refresh: a trade-ledger fill capture held the
                          IBKR account selection. It returns on the next refresh.
                        </div>
                      ) : null}
                      {attr.theta_basis === "live" ? (
                        <div className="text-[11px] opacity-60 mt-2 leading-4">
                          Theta uses live greeks (the prior snapshot's theta/day was unavailable);
                          over one session this is near-identical to the close value.
                        </div>
                      ) : null}
                      {attr.missing_terms?.length ? (
                        <div className="text-[11px] opacity-60 mt-2 leading-4">
                          Pending: {attr.missing_terms.join(", ")} term(s) need the prior-close vol
                          columns (run <code>sql/add_long_end_vol_columns.sql</code>, then one
                          risk-preflight cycle).
                        </div>
                      ) : null}
                    </div>
                  ) : null}

                  <div className="text-[11px] opacity-55 leading-5">
                    Read-only risk visibility. Estimates only — NAV Fund Services remains the official
                    NAV source; intraday brokerage P&L is situational. "Long end" ={" "}
                    {lev.long_end_basis === "vix3m" ? "VIX3M" : "the book's vega-weighted ATM IV"}.
                  </div>
                </div>
              );
            })()}
          </SectionCard>
        </div>

        <div className="space-y-2">
        <div className="text-[11px] uppercase tracking-[0.16em] opacity-55">
          Book sensitivities — greeks, vega cushion, surface shape
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
          {/* Both subtexts already state the unit is USD, and the Theta tile beside them
              already formats with fmtUSD. Rendering these two as bare fmtNum strings
              (no separators, no $) made net vega read "286076" here and "$286,071" in the
              Long-End Vol panel — the same quantity from two endpoints, presented as if
              one of them were a different kind of number. Formatting only; the values and
              their sources are unchanged. */}
          <StatCard
            title="Portfolio Delta"
            value={isNum(pg?.delta) ? fmtUSD(pg?.delta as number) : "—"}
            subtext={<>{"Directional exposure (USD per 1 pt of index) · greeks service"}{renderDelta("PortfolioDelta", "usd")}</>}
          />
          <StatCard
            title="Portfolio Vega"
            value={isNum(pg?.vega) ? fmtUSD(pg?.vega as number) : "—"}
            subtext={<>{"USD gain per +1 vol point · greeks service"}{renderDelta("PortfolioVega", "usd")}</>}
          />
          <StatCard
            title="Daily Decay (Theta)"
            value={isNum(portfolioThetaPerDay) ? fmtUSD(portfolioThetaPerDay) : "—"}
            subtext={
              <>
                {isNum(portfolioThetaAnnual)
                  ? `${fmtUSD(portfolioThetaAnnual)} / yr • time cost of holding LEAPS`
                  : "USD lost per day to time decay"}
                {renderDelta("PortfolioThetaPerDay", "usd")}
              </>
            }
            toneClassName={
              isNum(portfolioThetaPerDay) && (portfolioThetaPerDay as number) < 0
                ? "text-warn"
                : undefined
            }
          />
          <StatCard
            title="Vega Cushion"
            value={fmtUSD(kpis?.vega_cushion_usd ?? null)}
            subtext={<>{vegaCushionLabel}{renderDelta("VegaCushionUsd", "usd")}</>}
          />
          <StatCard
            title="Surface Distortion"
            value={fmtPct(surfaceSummary?.surface_distortion_score ?? null)}
            subtext="Higher = more elevated / dislocated shape"
            toneClassName={scoreTone(surfaceSummary?.surface_distortion_score ?? null)}
          />
        </div>
        </div>
        </section>

        <section id="risk-sec-surface" className="scroll-mt-20 space-y-6">
        <div className="space-y-2">
        <div className="text-[11px] uppercase tracking-[0.16em] opacity-55">
          Surface backdrop and deployment budget
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
          {topRiskCards.map((card) => (
            <React.Fragment key={card.title}>
              <StatCard
                title={card.title}
                value={card.value}
                subtext={card.subtext}
                toneClassName={card.tone}
              />
            </React.Fragment>
          ))}
        </div>
        </div>

        <div id="risk-surface" className="scroll-mt-20">
        <SectionCard
          title="Vol Surface Snapshot"
          subtitle="Current long-end SPX surface panel from the latest daily ORATS EOD snapshot with bucket quality and fallback transparency."
          right={
            <div className="flex flex-wrap items-center gap-2">
              {surfaceLoading ? (
                <div className="text-[11px] opacity-60">Loading surface…</div>
              ) : null}
              <SurfaceQualityBadge
                label={
                  volSurface?.term_structure_quality ||
                  volCoverage?.term_structure_quality ||
                  "unavailable"
                }
              />
            </div>
          }
        >
          {surfaceError ? (
            <div className="rounded-xl border border-warn/30 bg-warn/10 px-4 py-3 text-[12px] text-warn">
              {surfaceError}
            </div>
          ) : null}

          {volSurface ? (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
                <StatCard
                  title="Surface Regime (EOD snapshot)"
                  value={titleize(volSurface.surface_regime)}
                  subtext={
                    termSanity?.front_suspect
                      ? `Trade date ${volSurface.as_of_trade_date || "—"} • includes suspect ${suspectDisplay
                          .filter((l) => !["4Y", "5Y+"].includes(l))
                          .join(", ")}`
                      : `Trade date ${volSurface.as_of_trade_date || "—"}`
                  }
                  toneClassName={stanceTone(volSurface.surface_regime)}
                />
                <StatCard
                  title="Coverage"
                  value={titleize(
                    volSurface.term_structure_quality ||
                      volCoverage?.term_structure_quality
                  )}
                  subtext={`Distinct buckets ${
                    volCoverage?.distinct_non_null_count ?? "—"
                  } / ${volCoverage?.non_null_count ?? "—"}`}
                  toneClassName={surfaceCoverageTone(
                    volSurface.term_structure_quality ||
                      volCoverage?.term_structure_quality
                  )}
                />
                <StatCard
                  title="Long-End Coverage"
                  value={volSurface.long_end_coverage_ok ? "OK" : "Watch"}
                  subtext={`Exact / solid ${
                    volCoverage?.exact_or_solid_count ?? "—"
                  } • Min gap ${
                    volCoverage?.distinct_dte_min_gap_days ?? "—"
                  }d`}
                  toneClassName={
                    volSurface.long_end_coverage_ok
                      ? "text-up"
                      : "text-warn"
                  }
                />
                <StatCard
                  title="Deep-End (4–5Y+)"
                  value={
                    deepNodes.length === 0
                      ? "—"
                      : deepCoverageOk
                      ? "Covered"
                      : "Thin"
                  }
                  subtext={`Usable ${deepUsable.length} / ${deepNodes.length} • display-only`}
                  toneClassName={
                    deepNodes.length === 0
                      ? "text-[var(--text-color)]"
                      : deepCoverageOk
                      ? "text-up"
                      : "text-warn"
                  }
                />
                <StatCard
                  title="Spot"
                  value={fmtNum(volSurface.spot_px ?? null, 2)}
                  subtext={`${(volSurface.symbol || "SPX").toUpperCase()} • rows ${
                    volSurface.row_count ?? "—"
                  }`}
                />
                <StatCard
                  title="History Window"
                  value={titleize(volSurface.history_window?.frequency)}
                  subtext={`${volSurface.history_window?.start || "—"} → ${
                    volSurface.history_window?.end || "—"
                  }`}
                />
              </div>

              <div className="grid grid-cols-1 xl:grid-cols-[1.15fr_0.85fr] gap-3">
                <SectionCard
                  title="ATM Term Structure"
                  subtitle={
                    deepUsable.length > 0
                      ? "1Y through 3Y from the surface panel, extended to the deep 4–5Y+ long end the book actually holds."
                      : "1Y through 3Y near-ATM IV levels from the long-end surface panel."
                  }
                  className="shadow-none"
                >
                  <SurfaceLineChart
                    points={termCurvePoints}
                    valueFormatter={(value) => `${(value * 100).toFixed(2)}%`}
                    bars={termCurveVega(termCurvePoints.map((p) => p.label))}
                    barFormatter={(v) => `$${Math.round(v / 1000)}k`}
                  />
                  {(greeks?.by_expiration ?? []).length > 0 ? (
                    <div className="text-[10px] leading-4 mt-1" style={{ color: "var(--text-muted)" }}>
                      <span className="font-medium" data-testid="surface-vega-readout">
                        Book vega by tenor:{" "}
                        {(() => {
                          const labels = termCurvePoints.map((p) => p.label);
                          const vega = termCurveVega(labels);
                          return labels
                            .map((l, i) => (isNum(vega[i]) && (vega[i] as number) > 0 ? `${l} $${Math.round((vega[i] as number) / 1000)}k` : null))
                            .filter(Boolean)
                            .join(" · ");
                        })()}
                      </span>{" "}
                      (grey bars; $ per vol point from Portfolio Greeks by expiry). Each expiry is assigned
                      to its nearest curve point by days to expiry, so a bar shows how much of the book moves
                      with that tenor's vol. Display only.
                    </div>
                  ) : null}
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-2">
                    {VOL_TERM_ORDER.map((key) => (
                      <MiniNarrativeCard
                        key={`term-${key}`}
                        label={termLabel(key)}
                        value={
                          isNum(volSurface.atm_term_structure?.[key] ?? null)
                            ? `${((volSurface.atm_term_structure?.[key] ?? 0) * 100).toFixed(2)}%`
                            : "—"
                        }
                        subtext={
                          suspectNote(key)
                            ? "Suspect — see Source Health"
                            : `Pctile ${fmtNum(volSurface.term_percentiles?.[key] ?? null, 1)}`
                        }
                        toneClassName={
                          suspectNote(key)
                            ? "text-down"
                            : scoreTone(
                                isNum(volSurface.term_percentiles?.[key] ?? null)
                                  ? (volSurface.term_percentiles?.[key] ?? 0) / 100
                                  : null
                              )
                        }
                      />
                    ))}
                  </div>

                  {deepNodes.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-[var(--border-color)]">
                      <div className="flex items-center gap-2 mb-2 text-xs" style={{ color: "var(--text-muted)" }}>
                        <span className="font-medium">Deep long end (4–5Y+)</span>
                        <span className={`px-1.5 py-0.5 rounded border text-[10px] ${surfaceCoverageBadgeClass(deepCoverageOk ? "solid" : "sparse")}`}>
                          {deepCoverageOk ? "covered" : "thin"}
                        </span>
                        <span className="text-[10px]">modeled per-tenor; display-only (not used in screener sizing)</span>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        {deepNodes.map((n) => (
                          <MiniNarrativeCard
                            key={`deep-${n.tenor_days}`}
                            label={deepDisplayLabel(n)}
                            value={isNum(n.atm_iv ?? null) ? `${((n.atm_iv ?? 0) * 100).toFixed(2)}%` : "—"}
                            subtext={
                              suspectNote(n.label)
                                ? "Suspect — see Source Health"
                                : n.usable
                                ? `${
                                    isNum(n.percentile ?? null)
                                      ? `Pctile ${fmtNum(n.percentile ?? null, 1)}`
                                      : "Pctile —"
                                  } • ${titleize(n.quality)}${
                                    isNum(n.history_count ?? null) ? ` • hist ${n.history_count}` : ""
                                  }`
                                : "no data"
                            }
                            toneClassName={suspectNote(n.label) ? "text-down" : surfaceCoverageTone(n.quality)}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </SectionCard>

                <div className="grid grid-cols-1 gap-3">
                  <SectionCard
                    title="Term Percentile Curve"
                    subtitle={
                      deepUsable.length > 0
                        ? "Historical percentile rank for each tenor point, extended to the deep 4–5Y+ long end."
                        : "Historical percentile rank for each tenor point."
                    }
                    className="shadow-none"
                  >
                    <SurfaceLineChart
                      points={termPercentilePoints}
                      valueFormatter={(value) => `${value.toFixed(1)}`}
                      lineColor="rgb(245 158 11)"
                      dashedFromIndex={
                        deepUsable.length > 0 ? VOL_TERM_ORDER.length : undefined
                      }
                    />
                    {deepUsable.length > 0 ? (
                      <div
                        className="mt-2 pt-2 border-t border-[var(--border-color)] text-[10px] leading-4"
                        style={{ color: "var(--text-muted)" }}
                      >
                        <span className="font-medium">Dashed = deep long end.</span>{" "}
                        Ranked against a shorter post-2021 history ({deepHistoryNote}{" "}
                        obs) vs{" "}
                        {isNum(frontHistoryMin)
                          ? `${frontHistoryMin}+`
                          : "the full sample"}{" "}
                        for 1Y–3Y — read the deep level on its own, not as a
                        cross-tenor comparison.
                      </div>
                    ) : null}
                  </SectionCard>

                  <SectionCard
                    title="Near-ATM Skew"
                    subtitle="Long-end skew snapshot at 95 / 100 / 105 moneyness."
                    className="shadow-none"
                  >
                    <SurfaceSkewBars rows={skewRows} />
                  </SectionCard>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3">
                <MiniNarrativeCard
                  label="1Y → 2Y Slope"
                  value={fmtNum(volSurface.term_slopes?.["1y_to_2y"] ?? null, 4)}
                  subtext={`Negative means the longer tenor is cheaper${shapePctText("1y_to_2y")}`}
                  toneClassName={
                    isNum(volSurface.term_slopes?.["1y_to_2y"] ?? null) &&
                    (volSurface.term_slopes?.["1y_to_2y"] ?? 0) < 0
                      ? "text-up"
                      : "text-warn"
                  }
                />
                <MiniNarrativeCard
                  label="2Y → 3Y Slope"
                  value={fmtNum(volSurface.term_slopes?.["2y_to_3y"] ?? null, 4)}
                  subtext={`Shows long-end steepness or flattening${shapePctText("2y_to_3y")}`}
                  toneClassName={
                    isNum(volSurface.term_slopes?.["2y_to_3y"] ?? null) &&
                    (volSurface.term_slopes?.["2y_to_3y"] ?? 0) < 0
                      ? "text-up"
                      : "text-warn"
                  }
                />
                {deepNodes.length > 0 && (
                  <MiniNarrativeCard
                    label="3Y → 5Y+ Slope"
                    value={fmtNum(volSurface.term_slopes?.["3y_to_5y"] ?? null, 4)}
                    subtext={`Deep long-end steepness (thin; display-only)${shapePctText("3y_to_5y")}`}
                    toneClassName={
                      isNum(volSurface.term_slopes?.["3y_to_5y"] ?? null) &&
                      (volSurface.term_slopes?.["3y_to_5y"] ?? 0) < 0
                        ? "text-up"
                        : "text-warn"
                    }
                  />
                )}
                <MiniNarrativeCard
                  label="95 vs 100"
                  value={
                    isNum(volSurface.near_atm_skew?.["95"] ?? null) &&
                    isNum(volSurface.near_atm_skew?.["100"] ?? null)
                      ? `${(
                          ((volSurface.near_atm_skew?.["95"] ?? 0) -
                            (volSurface.near_atm_skew?.["100"] ?? 0)) *
                          100
                        ).toFixed(2)} vol pts`
                      : "—"
                  }
                  subtext={`Downside skew premium${shapePctText("skew_95_100")}`}
                />
                <MiniNarrativeCard
                  label="100 vs 105"
                  value={
                    isNum(volSurface.near_atm_skew?.["100"] ?? null) &&
                    isNum(volSurface.near_atm_skew?.["105"] ?? null)
                      ? `${(
                          ((volSurface.near_atm_skew?.["100"] ?? 0) -
                            (volSurface.near_atm_skew?.["105"] ?? 0)) *
                          100
                        ).toFixed(2)} vol pts`
                      : "—"
                  }
                  subtext={`Upside convexity discount${shapePctText("skew_100_105")}`}
                />
              </div>

              {historyViews ? (
                <div id="risk-cheapness">
                <SectionCard
                  title="How Cheap, and for How Long"
                  subtitle={`${cheapTenor.toUpperCase()} ATM IV through the stored history (${historyViews.history?.start ?? "—"} → ${historyViews.history?.end ?? "—"}) against its own full-history percentile bands.`}
                  className="shadow-none"
                >
                  <div className="flex flex-wrap items-center gap-3 mb-2 text-[12px]">
                    <select
                      aria-label="Tenor"
                      className="border rounded-lg px-2 py-1 bg-[var(--card-bg)] border-[var(--border-color)] text-[var(--text-color)]"
                      value={cheapTenor}
                      onChange={(e) => setCheapTenor(e.target.value as HistoryTenor)}
                    >
                      <option value="2y">2Y</option>
                      <option value="3y">3Y</option>
                    </select>
                    <select
                      aria-label="Range"
                      className="border rounded-lg px-2 py-1 bg-[var(--card-bg)] border-[var(--border-color)] text-[var(--text-color)]"
                      value={cheapRange}
                      onChange={(e) => setCheapRange(e.target.value as "full" | "daily")}
                    >
                      <option value="full">Full history</option>
                      <option value="daily" disabled={!historyViews.history?.daily_since}>
                        Daily block{historyViews.history?.daily_since ? ` (since ${historyViews.history.daily_since})` : ""}
                      </option>
                    </select>
                  </div>
                  <div className="grid grid-cols-1 xl:grid-cols-[1.6fr_1fr] gap-3">
                    <CheapnessHistoryChart views={historyViews} tenor={cheapTenor} range={cheapRange} />
                    <div className="grid grid-cols-1 gap-3 content-start">
                      {(() => {
                        const latest = historyViews.latest?.[cheapTenor];
                        return (
                          <MiniNarrativeCard
                            label={`${cheapTenor.toUpperCase()} today`}
                            value={isNum(latest?.iv ?? null) ? `${((latest?.iv as number) * 100).toFixed(2)}%` : "—"}
                            subtext={
                              isNum(latest?.percentile ?? null)
                                ? `${ordinalPct(latest?.percentile as number)} percentile of ${historyViews.bands?.[cheapTenor]?.n ?? "—"} stored sessions`
                                : "Percentile unavailable"
                            }
                            toneClassName={scoreTone(isNum(latest?.percentile ?? null) ? (latest?.percentile as number) / 100 : null)}
                          />
                        );
                      })()}
                      {([50, 30] as const).map((lvl) => {
                        const s = historyViews.streaks?.[cheapTenor]?.[`below_p${lvl}` as "below_p50" | "below_p30"];
                        const months = s?.month_ends ?? 0;
                        const sessions = s?.sessions;
                        return (
                          <MiniNarrativeCard
                            key={`streak-${lvl}`}
                            label={`At or below its ${lvl}th percentile`}
                            value={months > 0 ? `${months} month-end${months === 1 ? "" : "s"} running` : "Not now"}
                            subtext={[
                              months > 0 ? `since ${fmtMonthLabel(s?.since_month)}` : null,
                              isNum(sessions ?? null)
                                ? `${s?.sessions_capped ? "≥ " : ""}${sessions} daily session${sessions === 1 ? "" : "s"} in a row`
                                : null,
                              isNum(s?.share_of_daily ?? null)
                                ? `${Math.round((s?.share_of_daily as number) * 100)}% of daily sessions`
                                : null,
                              isNum(s?.threshold_iv ?? null) ? `p${lvl} = ${((s?.threshold_iv as number) * 100).toFixed(2)}%` : null,
                            ]
                              .filter(Boolean)
                              .join(" • ")}
                            toneClassName={months > 0 ? "text-up" : undefined}
                          />
                        );
                      })}
                    </div>
                  </div>
                  {volSurface.implied_vs_realized?.available ? (
                    <div className="mt-3 pt-3 border-t border-[var(--border-color)]" data-testid="implied-vs-realized">
                      <div className="text-xs font-medium mb-2" style={{ color: "var(--text-muted)" }}>
                        Against what SPX actually delivered — 2Y ATM implied vs trailing realized vol
                        (stored SPX closes through {volSurface.implied_vs_realized.latest?.spx_close_through ?? "—"})
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {(["1y", "2y"] as const).map((w) => {
                          const lat = volSurface.implied_vs_realized?.latest;
                          const row = lat?.[w];
                          const spread = row?.spread;
                          return (
                            <MiniNarrativeCard
                              key={`ivr-${w}`}
                              label={`2Y implied vs ${w.toUpperCase()} realized`}
                              value={
                                isNum(spread ?? null)
                                  ? `${(spread as number) >= 0 ? "+" : ""}${((spread as number) * 100).toFixed(2)} vol pts`
                                  : "—"
                              }
                              subtext={[
                                isNum(lat?.implied ?? null) && isNum(row?.realized ?? null)
                                  ? `${((lat?.implied as number) * 100).toFixed(2)}% vs ${((row?.realized as number) * 100).toFixed(2)}%`
                                  : null,
                                isNum(row?.spread_percentile ?? null)
                                  ? `premium at the ${ordinalPct(row?.spread_percentile as number)} pctile of ${row?.n ?? "—"} sessions`
                                  : null,
                                isNum(row?.spread_median ?? null)
                                  ? `median ${((row?.spread_median as number) * 100).toFixed(2)}`
                                  : null,
                              ]
                                .filter(Boolean)
                                .join(" • ")}
                              toneClassName={scoreTone(
                                isNum(row?.spread_percentile ?? null) ? (row?.spread_percentile as number) / 100 : null
                              )}
                            />
                          );
                        })}
                      </div>
                    </div>
                  ) : null}
                  <div className="mt-2 pt-2 border-t border-[var(--border-color)] text-[10px] leading-4" style={{ color: "var(--text-muted)" }}>
                    Implied-vs-realized: realized = trailing 252 / 504-day SPX log-return volatility
                    ×√252, from the stored close history; a low premium percentile means long-dated vol is
                    cheap against what the market has been delivering, not just against its own past.{" "}
                    Shaded = 20th–80th percentile; dashed lines = 30th and 50th. Bands use the whole stored
                    history, so on past dates they include hindsight. History is one session per month to
                    {historyViews.history?.daily_since ? ` ${historyViews.history.daily_since}` : " 2026-03"} and daily after
                    (the dotted divider); month-end streaks compare across the full history, session streaks
                    only cover the daily block ("≥" when the run reaches its start). Suspect sessions are gaps.
                    Same history and percentile rule as the term cards above. Display only.
                  </div>
                </SectionCard>
                </div>
              ) : null}

              <details
                className="rounded-xl border border-[var(--border-color)] bg-[var(--bg-secondary)] overflow-hidden"
              >
                <summary className="list-none cursor-pointer px-4 py-3.5 font-semibold flex items-center justify-between text-[14px]">
                  <span>Tenor Selection Detail</span>
                  <span className="text-[11px] opacity-60">Open / close</span>
                </summary>
                <div className="px-4 pb-4 overflow-auto">
                  <table className="min-w-[1040px] w-full text-[13px] text-[var(--text-color)]">
                    <thead>
                      <tr className="border-b border-[var(--border-color)] bg-[var(--table-header-bg,var(--card-bg))]">
                        <th className="text-left p-3 font-semibold">Tenor</th>
                        <th className="text-right p-3 font-semibold">IV</th>
                        <th className="text-right p-3 font-semibold">Pctile</th>
                        <th className="text-right p-3 font-semibold">Quality</th>
                        <th className="text-right p-3 font-semibold">Method</th>
                        <th className="text-right p-3 font-semibold">Median DTE</th>
                        <th className="text-right p-3 font-semibold">DTE Gap</th>
                        <th className="text-right p-3 font-semibold">Rows</th>
                        <th className="text-right p-3 font-semibold">Expiries</th>
                        <th className="text-right p-3 font-semibold">Distinct</th>
                      </tr>
                    </thead>
                    <tbody>
                      {VOL_TERM_ORDER.map((key, idx) => {
                        const detail = volSurface.atm_term_structure_details?.[key];
                        const quality = detail?.quality || "unavailable";
                        return (
                          <tr
                            key={`detail-${key}`}
                            className={`border-b border-[var(--border-color)] ${
                              idx % 2 === 0
                                ? "bg-transparent"
                                : "bg-[var(--table-row-alt-bg,rgba(255,255,255,0.02))]"
                            }`}
                          >
                            <td className="p-3 font-medium">{termLabel(key)}</td>
                            <td className="p-3 text-right">
                              {isNum(volSurface.atm_term_structure?.[key] ?? null)
                                ? `${((volSurface.atm_term_structure?.[key] ?? 0) * 100).toFixed(3)}%`
                                : "—"}
                            </td>
                            <td className="p-3 text-right">
                              {fmtNum(volSurface.term_percentiles?.[key] ?? null, 1)}
                            </td>
                            <td
                              className={`p-3 text-right font-semibold ${surfaceCoverageTone(
                                quality
                              )}`}
                            >
                              {titleize(quality)}
                            </td>
                            <td className="p-3 text-right">
                              {titleize(detail?.method)}
                            </td>
                            <td className="p-3 text-right">
                              {fmtNum(detail?.median_dte_used ?? null, 0)}
                            </td>
                            <td className="p-3 text-right">
                              {fmtNum(detail?.dte_distance_days ?? null, 0)}
                            </td>
                            <td className="p-3 text-right">
                              {fmtNum(detail?.count ?? null, 0)}
                            </td>
                            <td className="p-3 text-right">
                              {fmtNum(detail?.expiration_count ?? null, 0)}
                            </td>
                            <td className="p-3 text-right">
                              {detail?.distinct_cluster === true
                                ? "Yes"
                                : detail?.distinct_cluster === false
                                ? "No"
                                : "—"}
                            </td>
                          </tr>
                        );
                      })}
                      {deepNodes.map((n, di) => {
                        const idx = VOL_TERM_ORDER.length + di;
                        const quality = n.quality || "unavailable";
                        return (
                          <tr
                            key={`detail-deep-${n.tenor_days}`}
                            className={`border-b border-[var(--border-color)] ${
                              idx % 2 === 0
                                ? "bg-transparent"
                                : "bg-[var(--table-row-alt-bg,rgba(255,255,255,0.02))]"
                            }`}
                          >
                            <td className="p-3 font-medium">{deepDisplayLabel(n)}</td>
                            <td className="p-3 text-right">
                              {isNum(n.atm_iv ?? null)
                                ? `${((n.atm_iv ?? 0) * 100).toFixed(3)}%`
                                : "—"}
                            </td>
                            <td className="p-3 text-right">{fmtNum(n.percentile ?? null, 1)}</td>
                            <td
                              className={`p-3 text-right font-semibold ${surfaceCoverageTone(quality)}`}
                            >
                              {titleize(quality)}
                            </td>
                            <td className="p-3 text-right">{titleize(n.method)}</td>
                            <td className="p-3 text-right">{fmtNum(n.median_dte ?? null, 0)}</td>
                            <td className="p-3 text-right">{fmtNum(n.dte_distance_days ?? null, 0)}</td>
                            <td className="p-3 text-right">{fmtNum(n.count ?? null, 0)}</td>
                            <td className="p-3 text-right">—</td>
                            <td className="p-3 text-right">—</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </details>
            </div>
          ) : (
            <div className="opacity-60 text-sm">No surface snapshot available.</div>
          )}
        </SectionCard>
        </div>

        <div id="risk-exposure" className="scroll-mt-20 grid grid-cols-1 lg:grid-cols-3 gap-3">
          <SectionCard
            title="Held LEAPS by Tenor Bucket"
            subtitle="Every held line, shortest to longest — the four buckets cover 100% of the LEAPS book"
          >
            <ExposureBars
              rows={bucketExposure}
              labelKey="tenor_bucket"
              valueKey="market_value_pct"
              formatter={(row) =>
                `${fmtPct(row.market_value_pct ?? null)} • ${fmtUSD(
                  row.market_value ?? null
                )}`
              }
            />
          </SectionCard>

          <SectionCard
            title="Exposure by Surface Value"
            subtitle="Where the current book sits on the cheap-to-rich continuum"
          >
            <ExposureBars
              rows={valueExposure}
              labelKey="label"
              valueKey="market_value_pct"
              formatter={(row) =>
                `${fmtPct(row.market_value_pct ?? null)} • ${row.position_count ?? 0} lines`
              }
            />
          </SectionCard>

          <SectionCard
            title="Exposure by Surface Shape"
            subtitle="Normal, flat, elevated, and dislocated conditions across the held book"
          >
            <ExposureBars
              rows={shapeExposure}
              labelKey="label"
              valueKey="market_value_pct"
              formatter={(row) =>
                `${fmtPct(row.market_value_pct ?? null)} • ${row.position_count ?? 0} lines`
              }
            />
          </SectionCard>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-[1.15fr_0.85fr] gap-3">
          <SectionCard
            title="Deployment Readout"
            subtitle="Straight CIO framing of where the book lives versus where the surface says convexity is most attractive."
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
              <MiniNarrativeCard
                label="Primary Book Bucket"
                value={bucketLabel(surfaceSummary?.portfolio_primary_bucket)}
              />
              <MiniNarrativeCard
                label="Best Current Bucket"
                value={bucketLabel(surfaceSummary?.best_deployment_bucket)}
                toneClassName={qualityTone(bestBucketRow?.market_value_pct ?? null)}
              />
              <MiniNarrativeCard
                label="Sweet-Spot Share"
                value={fmtPct(bestBucketRow?.market_value_pct ?? null)}
                toneClassName={qualityTone(bestBucketRow?.market_value_pct ?? null)}
              />
              <MiniNarrativeCard
                label="Deployment Stance"
                value={titleize(surfaceSummary?.deployment_stance)}
                toneClassName={stanceTone(surfaceSummary?.deployment_stance)}
              />
            </div>
            {/* The same surfaceOverview.commentary string used to render here verbatim,
                a second time — it is already the closing paragraph of the Risk Takeaway
                band at the top of the page. Pointer, not a repeat. */}
            <div className="text-[11px] opacity-55 leading-6">
              The full surface commentary is in the Risk Takeaway band at the top of this
              page.
            </div>
          </SectionCard>

          <SectionCard
            title="Risk Flags"
            subtitle="Book-level alerts generated from the surface-aware mapping and scenario diagnostics"
          >
            {riskFlags.length ? (
              <div className="space-y-3">
                {riskFlags.map((flag, idx) => (
                  <div
                    key={`${idx}-${flag}`}
                    className="rounded-xl border border-[var(--border-color)] px-3 py-3 bg-[var(--bg-secondary)] text-[12px] leading-6"
                  >
                    {flag}
                  </div>
                ))}
              </div>
            ) : (
              <div className="opacity-60 text-sm">No current risk flags.</div>
            )}
          </SectionCard>
        </div>

        <details
          className="rounded-xl border border-[var(--border-color)] bg-[var(--card-bg)] shadow-sm overflow-hidden"
        >
          <summary className="list-none cursor-pointer px-4 py-3.5 font-semibold flex items-center justify-between text-[14px]">
            <span>Bucket Exposure Detail</span>
            <span className="text-[11px] opacity-60">Open / close</span>
          </summary>
          <div className="px-4 pb-4 overflow-auto">
            <table className="min-w-[760px] w-full text-[13px] text-[var(--text-color)]">
              <thead>
                <tr className="border-b border-[var(--border-color)] bg-[var(--table-header-bg,var(--card-bg))]">
                  <th className="text-left p-3 font-semibold">Bucket</th>
                  <th className="text-right p-3 font-semibold">Lines</th>
                  <th className="text-right p-3 font-semibold">MV</th>
                  <th className="text-right p-3 font-semibold">MV %</th>
                  <th className="text-right p-3 font-semibold">Delta</th>
                  <th className="text-right p-3 font-semibold">Vega</th>
                  <th className="text-right p-3 font-semibold">Surface Score</th>
                </tr>
              </thead>
              <tbody>
                {bucketExposure.map((row, idx) => (
                  <tr
                    key={row.tenor_bucket || idx}
                    className={`border-b border-[var(--border-color)] ${
                      idx % 2 === 0
                        ? "bg-transparent"
                        : "bg-[var(--table-row-alt-bg,rgba(255,255,255,0.02))]"
                    }`}
                  >
                    <td className="p-3 font-medium">
                      <div className="flex items-center gap-2">
                        <span>{bucketLabel(row.tenor_bucket)}</span>
                        {row.is_best_bucket ? (
                          <span className="text-[9px] uppercase tracking-wide rounded-full px-2 py-0.5 border border-up/40 bg-up/10 text-up">
                            Market Best
                          </span>
                        ) : null}
                      </div>
                    </td>
                    <td className="p-3 text-right">{row.position_count ?? 0}</td>
                    <td className="p-3 text-right">{fmtUSD(row.market_value ?? null)}</td>
                    <td className="p-3 text-right">{fmtPct(row.market_value_pct ?? null)}</td>
                    <td className="p-3 text-right">
                      {fmtNum(row.delta_exposure ?? null, 0)}
                    </td>
                    <td className="p-3 text-right">
                      {fmtNum(row.vega_exposure ?? null, 0)}
                    </td>
                    <td
                      className={`p-3 text-right font-semibold ${qualityTone(
                        row.avg_surface_score ?? null
                      )}`}
                    >
                      {fmtPct(row.avg_surface_score ?? null)}
                    </td>
                  </tr>
                ))}
                {!bucketExposure.length ? (
                  <tr>
                    <td className="p-3 opacity-60" colSpan={7}>
                      No surface bucket exposure data available.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </details>
        </section>

        <section id="risk-sec-stress" className="scroll-mt-20 space-y-6">
        <SectionCard
          title="Scenario Controls"
          subtitle="Tune the stress lens without leaving the page. Reprice uses full Black-Scholes; expansion is the faster Greek approximation."
          // Changing any control refetches ten endpoints. Until now the only feedback
          // was the Refresh button's label in the page header — and the reorder moved
          // these controls four sections below it. Without this, the PREVIOUS
          // scenario's numbers sit under the new setting looking authoritative.
          right={
            loading ? (
              <span className="text-[11px] text-warn font-semibold">
                Recomputing — figures below are the previous scenario
              </span>
            ) : null
          }
        >
          <div className="grid grid-cols-1 xl:grid-cols-[1.1fr_1fr] gap-3">
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
              <label className="space-y-1.5 text-[13px]">
                <div className="opacity-70">Mode</div>
                <select
                  className="w-full border rounded-xl px-3 py-2 bg-[var(--card-bg)] border-[var(--border-color)] text-[var(--text-color)] text-[13px]"
                  value={mode}
                  onChange={(e) => {
                    const next = e.target.value as "reprice" | "expansion";
                    // The backend prices a rate shock by full repricing only (expansion has
                    // no rho term and refuses it), so leaving Reprice clears the shock.
                    if (next === "expansion") setRateShockBp(0);
                    setMode(next);
                  }}
                >
                  <option value="reprice">Reprice (Black-Scholes)</option>
                  <option value="expansion">Taylor Expansion</option>
                </select>
              </label>

              <label className="space-y-1.5 text-[13px]">
                <div className="opacity-70">Heatmap Axis</div>
                <select
                  className="w-full border rounded-xl px-3 py-2 bg-[var(--card-bg)] border-[var(--border-color)] text-[var(--text-color)] text-[13px]"
                  value={axisMode}
                  onChange={(e) => setAxisMode(e.target.value as "vix" | "iv")}
                >
                  <option value="vix">VIX-aware</option>
                  <option value="iv">Raw IV shock</option>
                </select>
              </label>

              <label className="space-y-1.5 text-[13px]">
                <div className="opacity-70">Horizon</div>
                <select
                  className="w-full border rounded-xl px-3 py-2 bg-[var(--card-bg)] border-[var(--border-color)] text-[var(--text-color)] text-[13px]"
                  value={String(horizonMonths)}
                  onChange={(e) =>
                    setHorizonMonths(Number(e.target.value) as 0 | 3 | 6 | 12)
                  }
                >
                  <option value="0">Now</option>
                  <option value="3">3 Months</option>
                  <option value="6">6 Months</option>
                  <option value="12">12 Months</option>
                </select>
              </label>

              <label
                className="space-y-1.5 text-[13px]"
                title={
                  mode === "expansion"
                    ? "Rate shocks are priced by full repricing only. Switch Mode to Reprice."
                    : "Parallel shift of the interest rate at every expiry; dividend yields held."
                }
              >
                <div className="opacity-70">Rates</div>
                <select
                  className="w-full border rounded-xl px-3 py-2 bg-[var(--card-bg)] border-[var(--border-color)] text-[var(--text-color)] text-[13px] disabled:opacity-50"
                  value={String(rateShockBp)}
                  disabled={mode === "expansion"}
                  onChange={(e) => setRateShockBp(Number(e.target.value) as RateShockBp)}
                >
                  {RATE_SHOCK_OPTIONS.map((bp) => (
                    <option key={bp} value={String(bp)}>
                      {rateShockLabel(bp)}
                    </option>
                  ))}
                </select>
              </label>

              <label className="space-y-1.5 text-[13px]">
                <div className="opacity-70">Heatmap View</div>
                <select
                  className="w-full border rounded-xl px-3 py-2 bg-[var(--card-bg)] border-[var(--border-color)] text-[var(--text-color)] text-[13px]"
                  value={expView}
                  onChange={(e) => setExpView(e.target.value)}
                >
                  <option value="__PORTFOLIO__">Portfolio (All)</option>
                  {expirations.map((exp) => (
                    <option key={exp} value={exp}>
                      {exp}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <div className="flex flex-col gap-3 rounded-xl border border-[var(--border-color)] bg-[var(--bg-secondary)] p-3.5">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div className="text-[13px] font-medium">Heatmap Metric</div>
                  <div className="text-[11px] opacity-60">
                    Flip between absolute dollars and sleeve percent.
                  </div>
                </div>
                <TogglePills<HeatmapMetricMode>
                  value={metricMode}
                  onChange={setMetricMode}
                  options={[
                    { value: "usd", label: "Dollar P&L" },
                    { value: "pct", label: "% of Sleeve" },
                  ]}
                />
              </div>
              <label className="text-[13px] flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={detailContracts}
                  onChange={(e) => setDetailContracts(e.target.checked)}
                />
                Show worst-cell contract contributors
              </label>
            </div>
          </div>
        </SectionCard>

        {/* The heatmap declares min-w-[980px]. Pairing it with the readout in a
            1.5fr/0.85fr split gave it ~876px at a 1500px viewport, so it scrolled
            sideways and clipped the outer spot columns on every screen between roughly
            1280px and 1663px. It now takes the full row and the readout sits beneath it. */}
        <div id="risk-stress" className="scroll-mt-20 space-y-3">
          <div>
          <SectionCard
            title="Stress Heatmap"
            subtitle={`Mode ${grid?.mode_used ?? mode} • Axis ${
              grid?.axis_label ?? (axisMode === "vix" ? "VIX Level" : "IV Δ (vol pts)")
            } • Horizon ${grid?.horizon_label ?? "Now"}${
              grid?.rate_shock && grid.rate_shock.bp !== 0 ? ` • ${grid.rate_shock.label}` : ""
            } • View ${expView === "__PORTFOLIO__" ? "Portfolio" : expView}`}
            right={
              <div className="text-[11px] opacity-65 max-w-xs text-right leading-5">
                {axisMode === "vix"
                  ? "VIX-aware heatmap using calibrated tenor-sensitive IV translation from the current surface state."
                  : "Direct IV-shock map for pure option-risk diagnostics without the VIX translation layer."}
              </div>
            }
          >
            {grid?.rate_shock && grid.rate_shock.bp !== 0 ? (
              <div className="mb-2.5 rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] px-3 py-2 text-[12px] leading-5">
                <span className="font-semibold text-warn">{grid.rate_shock.label} scenario.</span>{" "}
                Every cell below includes the rate shock: the interest rate moves at every expiry and dividend
                yields are held, so net carry moves by the full shock. Today&apos;s marks are the base. The risk
                tiles elsewhere on this page are not shocked.
              </div>
            ) : rateShockBp !== 0 && grid && !loading ? (
              <div className="mb-2.5 rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] px-3 py-2 text-[12px] leading-5">
                <span className="font-semibold text-warn">{rateShockLabel(rateShockBp)} was requested but the grid below is not shocked.</span>{" "}
                The service did not confirm the shock; read this grid as rates unchanged.
              </div>
            ) : null}
            <Heatmap
              grid={gridAxis}
              cells={heatmapCells}
              axisMode={axisMode}
              axisLabel={grid?.axis_label}
              metricMode={metricMode}
              worstCell={worstViewCell}
              baseCell={baseCell}
            />
            {(grid?.shape_scenarios ?? [])
              .filter((s) => s.key === "twist_0624" && !s.error && isNum(s.pnl_usd ?? null))
              .map((s) => {
                const front = s.legs?.under_30m;
                const back = s.legs?.["30m_plus"];
                return (
                  <div
                    key={s.key}
                    data-testid="shape-twist-0624"
                    className="mt-2.5 rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] px-3 py-2 text-[12px] leading-5"
                  >
                    <span className="font-semibold">6/24 twist</span>{" "}
                    <span className="opacity-70">
                      (VIX {fmtNum(s.vix_from ?? null, 1)} → {fmtNum(s.vix_to ?? null, 1)} for options under 30 months;
                      30 months and longer −1 vol pt; spot flat{grid?.horizon_label && grid.horizon_label !== "Now" ? `; ${grid.horizon_label}` : ""}
                      {grid?.rate_shock && grid.rate_shock.bp !== 0 ? `; ${grid.rate_shock.label}` : ""}):
                    </span>{" "}
                    <span className={`font-semibold ${(s.pnl_usd as number) < 0 ? "text-down" : "text-up"}`}>
                      {fmtSignedUSD(s.pnl_usd as number)}
                    </span>
                    {isNum(s.pnl_pct_of_sleeve ?? null) ? (
                      <span className="opacity-70"> ({((s.pnl_pct_of_sleeve as number) * 100).toFixed(1)}% of sleeve)</span>
                    ) : null}
                    <span className="opacity-70">
                      {" "}= under 30m {isNum(front?.pnl_usd ?? null) ? fmtSignedUSD(front?.pnl_usd as number) : "—"} ({front?.contracts ?? 0} lines)
                      {" "}+ 30m and longer {isNum(back?.pnl_usd ?? null) ? fmtSignedUSD(back?.pnl_usd as number) : "—"} ({back?.contracts ?? 0} lines).
                      The grid moves every tenor with VIX; this is the day the front rises and the long end falls. Display only.
                    </span>
                  </div>
                );
              })}
            <div className="mt-2.5 text-[11px] opacity-65 leading-5">
              Read left-to-right for spot shock, top-to-bottom for volatility
              stress. The blue outline anchors the current/base reference cell and
              the red outline marks the most adverse scenario in the displayed view.{" "}
              <span className="text-warn">
                This reprices the LEAPS sleeve only — the fixed-income sleeve is held
                flat here. See the Fixed Income Rate Sensitivity panel for the rates shock
                on the FI book.
              </span>
            </div>
          </SectionCard>
          </div>

          <SectionCard
            title="Scenario Readout"
            subtitle="Plain-English translation of the displayed heatmap so you can move from model output to portfolio judgment quickly."
          >
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
              <MiniNarrativeCard
                label="Worst Scenario in View"
                value={stressNarrative.scenario}
                subtext={`Current selection: ${stressNarrative.viewLabel}`}
                toneClassName="text-down"
              />
              <MiniNarrativeCard
                label="Expected Stress Loss (vs NAV)"
                value={stressNarrative.fundImpact}
                subtext={`Fixed scenario set — does not follow the controls above. Worst cell in the current view: ${stressNarrative.estimatedLoss} · sleeve ${stressNarrative.sleeveLoss} · FI not shocked`}
                toneClassName="text-down"
              />
              <MiniNarrativeCard
                label="Within Budget?"
                value={stressNarrative.withinBudget}
                subtext={`Budget ${stressNarrative.budgetText}`}
                toneClassName={
                  stressNarrative.withinBudget === "Yes"
                    ? "text-up"
                    : "text-down"
                }
              />
              <MiniNarrativeCard
                label="Main Driver"
                value={surfaceSummary?.mismatch_flag ? "Surface Mismatch" : "Bucket Mix"}
                subtext={stressNarrative.mainDriver}
              />
            </div>
          </SectionCard>
        </div>

        {showDynamicRiskCards ? (
          <div className="space-y-2">
            <div className="text-[11px] uppercase tracking-[0.16em] opacity-55">
              Modeled stress &amp; convexity — LEAPS sleeve only, % of NAV
              <InfoHint text="These reprice the LEAPS options across a fixed set of spot/vol scenarios and divide by total fund NAV. Fixed income is NOT shocked here — see the Fixed Income Rate Sensitivity panel below for the rates shock. Directional estimates, not a statistical VaR." />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
              <StatCard
                title="Expected Stress Loss"
                value={fmtPct(kpis?.expected_stress_drawdown_pct_nav ?? null)}
                subtext={
                  <>
                    {`Worst weighted ${fmtPct(
                      kpis?.p95_path_drawdown_pct_nav ?? null
                    )} • Static worst ${fmtPct(kpis?.worst_pct_of_nav ?? null)}`}
                    {renderDelta("ExpectedStressDdPctNav", "pp", 1)}
                  </>
                }
                toneClassName={ratioTone(kpis?.dynamic_vs_budget_ratio ?? null)}
              />
              <StatCard
                title="Structural Risk Score"
                value={fmtPct(kpis?.structural_risk_pct_nav ?? null)}
                subtext="Concentration & aging score (expiry clustering, stale/underwater share) — not a loss estimate"
              />
              <StatCard
                title="Convexity Benefit"
                value={fmtPct(kpis?.convexity_benefit_pct_nav ?? null)}
                subtext={`Modeled ${fmtPct(
                  kpis?.expected_stress_drawdown_pct_nav ?? null
                )} vs linear ${fmtPct(
                  kpis?.expected_linear_drawdown_pct_nav ?? null
                )} — positive means convexity softened the loss`}
                toneClassName={qualityTone(kpis?.convexity_benefit_pct_nav ?? null)}
              />
              <StatCard
                title="Recovery Indicator (12-month)"
                value={fmtPct(kpis?.recovery_probability_12m ?? null)}
                subtext={`Heuristic score, NOT a calibrated probability • 6-month reads ${fmtPct(
                  kpis?.recovery_probability_6m ?? null
                )}`}
                toneClassName={qualityTone(kpis?.recovery_probability_12m ?? null)}
              />
            </div>
          </div>
        ) : null}

        {/* Right-Tail needs its own labelled group. Until the reorder, the Fixed Income
            panel physically separated this grid from the "Modeled stress & convexity —
            LEAPS sleeve only, % of NAV" label above it. With FI moved to §8 the two
            grids sit 24px apart carrying identical column classes, so a USD tile read as
            a fifth tile under a header asserting "% of NAV". The label above is left
            exactly as it is; this one is additive, and it also gives the tile a scope
            note in the branch where showDynamicRiskCards is false and that header does
            not render at all. */}
        <div className="space-y-2">
        <div className="text-[11px] uppercase tracking-[0.16em] opacity-55">
          Modeled upside scenario — LEAPS sleeve only, USD
          <InfoHint
            text={`Reprices the LEAPS options at the +20% spot node (${rightTailLabel}). Fixed income is not shocked. Directional estimate, not a statistical VaR.`}
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
          <StatCard
            title="Right-Tail Convexity"
            value={fmtUSD(kpis?.right_tail_convexity?.flat_vol ?? null)}
            subtext={`${rightTailLabel}: ${fmtUSD(
              kpis?.right_tail_convexity?.minus5_vol ?? null
            )}`}
          />
        </div>
        </div>
        </section>

        <section id="risk-sec-fi" className="scroll-mt-20 space-y-6">
        <div id="risk-fi-rate" className="scroll-mt-20">
          <SectionCard
            title="Fixed Income Rate Sensitivity"
            subtitle="Real measured effective durations (Morningstar) applied to live FI market values — the rate shock the LEAPS heatmap omits. Display/risk only; does not drive trading."
            right={
              <div className="text-[11px] opacity-70 text-right leading-5">
                {fiRate?.profile_source ? "Source: Morningstar" : ""}
                {fiRate?.profile_as_of ? ` • durations as of ${fiRate.profile_as_of}` : ""}
                {fiRate?.profile_staleness?.stale && (
                  <span className="ml-1 font-semibold text-warn" data-testid="fi-durations-stale" title={stalenessTitle(fiRate.profile_staleness)}>
                    • STALE
                  </span>
                )}
              </div>
            }
          >
            {fiRate?.sleeve ? (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
                  <StatCard
                    title="Sleeve Duration"
                    value={fmtNum(fiRate.sleeve.mv_weighted_effective_duration ?? null, 2)}
                    subtext={`MV-weighted yrs • FI MV ${fmtUSD(fiRate.sleeve.total_mv ?? null)}`}
                  />
                  <StatCard
                    title="DV01 (per +1bp)"
                    value={fmtUSD(fiRate.sleeve.dv01_usd ?? null)}
                    subtext="Dollar loss if yields rise 1bp"
                  />
                  <StatCard
                    title="Rates +100bp"
                    value={fmtUSD(fiRate.sleeve.dmv_usd?.["+100bp"] ?? null)}
                    subtext={
                      isNum(canonicalTotalNav) &&
                      isNum(fiRate.sleeve.dmv_usd?.["+100bp"] ?? null)
                        ? `${fmtPct(
                            (fiRate.sleeve.dmv_usd!["+100bp"] as number) / (canonicalTotalNav as number),
                            2
                          )} of NAV • parallel shift`
                        : "Parallel curve shift"
                    }
                    toneClassName="text-down"
                  />
                  <StatCard
                    title="Rates -100bp"
                    value={fmtUSD(fiRate.sleeve.dmv_usd?.["-100bp"] ?? null)}
                    subtext="Parallel curve rally"
                    toneClassName="text-up"
                  />
                </div>

                {fiRate.coverage && fiRate.coverage.all_covered === false ? (
                  <div className="rounded-xl border border-warn/30 bg-warn/10 px-3 py-2 text-[12px] text-warn">
                    {fiRate.coverage.note}
                  </div>
                ) : null}

                <div className="overflow-auto">
                  <table className="min-w-[760px] w-full text-[13px] text-[var(--text-color)]">
                    <thead>
                      <tr className="border-b border-[var(--border-color)] bg-[var(--table-header-bg,var(--card-bg))]">
                        <th className="text-left p-3 font-semibold">Holding</th>
                        <th className="text-right p-3 font-semibold">Market Value</th>
                        <th className="text-right p-3 font-semibold">
                          Eff. Duration
                          <InfoHint text="Real measured interest-rate sensitivity of the fund (Morningstar). Longer = more rate risk." />
                        </th>
                        <th className="text-right p-3 font-semibold">DV01 (+1bp)</th>
                        <th className="text-right p-3 font-semibold">+100bp</th>
                        <th className="text-right p-3 font-semibold">+200bp</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(fiRate.positions ?? []).map((p, idx) => (
                        <tr
                          key={`${p.ticker}-${idx}`}
                          className={`border-b border-[var(--border-color)] ${
                            idx % 2 === 0
                              ? "bg-transparent"
                              : "bg-[var(--table-row-alt-bg,rgba(255,255,255,0.02))]"
                          }`}
                        >
                          <td className="p-3">
                            <div className="font-medium">{p.ticker}</div>
                            {p.name ? (
                              <div className="text-[11px] opacity-60">{p.name}</div>
                            ) : null}
                          </td>
                          <td className="p-3 text-right">{fmtUSD(p.market_value)}</td>
                          <td className="p-3 text-right">
                            {p.covered ? (
                              fmtNum(p.effective_duration, 2)
                            ) : (
                              <span className="text-warn">no profile</span>
                            )}
                          </td>
                          <td className="p-3 text-right">{p.covered ? fmtUSD(p.dv01_usd) : "—"}</td>
                          <td className="p-3 text-right text-down">
                            {fmtUSD(p.dmv_usd?.["+100bp"] ?? null)}
                          </td>
                          <td className="p-3 text-right text-down">
                            {fmtUSD(p.dmv_usd?.["+200bp"] ?? null)}
                          </td>
                        </tr>
                      ))}
                      {!(fiRate.positions ?? []).length ? (
                        <tr>
                          <td className="p-3 opacity-60" colSpan={6}>
                            No fixed-income holdings returned.
                          </td>
                        </tr>
                      ) : null}
                    </tbody>
                  </table>
                </div>
                <div className="text-[11px] opacity-60 leading-5">
                  First-order parallel-shift estimate (dMV = −duration × Δyield × market value).
                  Convexity (curvature on large moves) not included. TIPS (STIP) shown at real-rate
                  duration. Durations refresh on a monthly cadence.
                </div>
              </div>
            ) : (
              <div className="opacity-60 text-sm">
                {fiRate?.error ||
                  fiRate?.warning ||
                  "Fixed-income rate sensitivity unavailable."}
              </div>
            )}
          </SectionCard>
        </div>
        </section>

        <section id="risk-sec-data" className="scroll-mt-20 space-y-6">
        {dataSources.length ? (
          <SectionCard
            title="Service Status"
            subtitle="Backend source transparency for the live risk payloads. Snapshot fallback is acceptable for resiliency, but it should stand out when it is happening."
          >
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
              {dataSources.map((item) => (
                <MiniNarrativeCard
                  key={item.label}
                  label={item.label}
                  value={sourceLabel(item.cache?.source)}
                  subtext={`Computed ${freshnessLabel(item.cache, item.asOf)}${
                    item.cache?.stale ? " • cache stale" : ""
                  }`}
                  toneClassName={sourceTone(item.cache?.source, item.cache?.stale)}
                />
              ))}
            </div>

            <div className="mt-3 pt-3 border-t border-[var(--border-color)]">
              <div className="text-[11px] uppercase tracking-[0.16em] opacity-55 mb-2">
                Data recency — age of the actual underlying data (not when the page ran)
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {dataHealth.items.map((item) => (
                  <MiniNarrativeCard
                    key={item.key}
                    label={item.key}
                    value={
                      item.ms === null
                        ? "unknown"
                        : item.dateOnly
                        ? sessionAgeLabel(item.ms)
                        : humanizeAgeMs(Date.now() - item.ms)
                    }
                    subtext={
                      item.ms !== null
                        ? `As of ${
                            item.dateOnly
                              ? fmtUtcDate(item.ms)
                              : fmtDateTime(new Date(item.ms).toISOString())
                          }${
                            item.stale
                              ? ` • data ${sessionsBehind(item.ms, Date.now())} session${
                                  sessionsBehind(item.ms, Date.now()) === 1 ? "" : "s"
                                } old`
                              : ""
                          }`
                        : "No data timestamp reported"
                    }
                    toneClassName={
                      item.stale
                        ? "text-down"
                        : "text-up"
                    }
                  />
                ))}
              </div>
            </div>
          </SectionCard>
        ) : null}

        <details
          id="risk-detail"
          className="scroll-mt-20 rounded-xl border border-[var(--border-color)] bg-[var(--card-bg)] shadow-sm overflow-hidden"
        >
          <summary className="list-none cursor-pointer px-4 py-3.5 font-semibold flex items-center justify-between text-[14px]">
            <span>Portfolio Greeks and Expiration Detail</span>
            <span className="text-[11px] opacity-60">Open / close</span>
          </summary>
          <div className="px-4 pb-4 space-y-4">
            <SectionCard
              title="Portfolio Greeks"
              subtitle={`as of ${greeks?.as_of ?? "—"} • ${summarizeCarry(greeks?.carry).label}`}
              className="shadow-none p-0 border-0 bg-transparent"
            >
              {greeks ? (
                <div
                  className={`text-[11px] mb-3 ${summarizeCarry(greeks.carry).warn ? "text-warn" : "opacity-60"}`}
                  title={summarizeCarry(greeks.carry).detail}
                >
                  {summarizeCarry(greeks.carry).detail}
                </div>
              ) : null}
              {greeks?.status === "degraded" ? (
                <div className="text-[11px] text-warn mb-3">
                  Greeks service degraded: {greeks?.error ?? "partial data"}
                </div>
              ) : null}
              <div className="grid grid-cols-2 md:grid-cols-6 gap-3 text-[13px]">
                <div className="rounded-xl border border-[var(--border-color)] p-3">
                  <div className="text-[10px] opacity-60 uppercase">Delta</div>
                  <div className="text-[1.1rem] font-semibold">
                    {fmtNum(pg?.delta ?? null, 0)}
                  </div>
                </div>
                <div className="rounded-xl border border-[var(--border-color)] p-3">
                  <div className="text-[10px] opacity-60 uppercase">Gamma</div>
                  <div className="text-[1.1rem] font-semibold">
                    {fmtNum(pg?.gamma ?? null, 4)}
                  </div>
                </div>
                <div className="rounded-xl border border-[var(--border-color)] p-3">
                  <div className="text-[10px] opacity-60 uppercase">Vega</div>
                  <div className="text-[1.1rem] font-semibold">
                    {fmtNum(pg?.vega ?? null, 0)}
                  </div>
                </div>
                <div className="rounded-xl border border-[var(--border-color)] p-3">
                  <div className="text-[10px] opacity-60 uppercase">
                    Theta / day
                    <InfoHint text="Dollars of value the book loses per day to time decay, all else equal." />
                  </div>
                  <div className="text-[1.1rem] font-semibold">
                    {fmtUSD(pg?.theta_per_day ?? null)}
                  </div>
                </div>
                <div className="rounded-xl border border-[var(--border-color)] p-3">
                  <div className="text-[10px] opacity-60 uppercase">Vanna</div>
                  <div className="text-[1.1rem] font-semibold">
                    {fmtNum(pg?.vanna ?? null, 0)}
                  </div>
                </div>
                <div className="rounded-xl border border-[var(--border-color)] p-3">
                  <div className="text-[10px] opacity-60 uppercase">Vomma</div>
                  <div className="text-[1.1rem] font-semibold">
                    {fmtNum(pg?.vomma ?? null, 0)}
                  </div>
                </div>
              </div>
            </SectionCard>

            <div className="overflow-auto">
              <table className="min-w-[760px] w-full text-[13px] text-[var(--text-color)]">
                <thead>
                  <tr className="border-b border-[var(--border-color)] bg-[var(--table-header-bg,var(--card-bg))]">
                    <th className="text-left p-3 font-semibold text-[var(--text-color)]">
                      Expiration
                    </th>
                    <th className="text-right p-3 font-semibold text-[var(--text-color)]">
                      Delta
                    </th>
                    <th className="text-right p-3 font-semibold text-[var(--text-color)]">
                      Gamma
                    </th>
                    <th className="text-right p-3 font-semibold text-[var(--text-color)]">
                      Vega
                    </th>
                    <th className="text-right p-3 font-semibold text-[var(--text-color)]">
                      Theta / day
                    </th>
                    <th className="text-right p-3 font-semibold text-[var(--text-color)]">
                      Vanna
                    </th>
                    <th className="text-right p-3 font-semibold text-[var(--text-color)]">
                      Vomma
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {(greeks?.by_expiration ?? []).map((row, idx) => (
                    <tr
                      key={row.expiration}
                      className={`border-b border-[var(--border-color)] ${
                        idx % 2 === 0
                          ? "bg-transparent"
                          : "bg-[var(--table-row-alt-bg,rgba(255,255,255,0.02))]"
                      }`}
                    >
                      <td className="p-3 text-[var(--text-color)]">{row.expiration}</td>
                      <td className="p-3 text-right text-[var(--text-color)]">
                        {fmtNum(row.delta, 0)}
                      </td>
                      <td className="p-3 text-right text-[var(--text-color)]">
                        {fmtNum(row.gamma, 4)}
                      </td>
                      <td className="p-3 text-right text-[var(--text-color)]">
                        {fmtNum(row.vega, 0)}
                      </td>
                      <td className="p-3 text-right text-[var(--text-color)]">
                        {fmtUSD(row.theta_per_day ?? null)}
                      </td>
                      <td className="p-3 text-right text-[var(--text-color)]">
                        {fmtNum(row.vanna, 0)}
                      </td>
                      <td className="p-3 text-right text-[var(--text-color)]">
                        {fmtNum(row.vomma, 0)}
                      </td>
                    </tr>
                  ))}
                  {!(greeks?.by_expiration ?? []).length && (
                    <tr>
                      <td
                        className="p-3 opacity-60 text-[var(--text-color)]"
                        colSpan={7}
                      >
                        No expiration-level greek data available.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </details>

        {detailContracts && (
          <details
            className="rounded-xl border border-[var(--border-color)] bg-[var(--card-bg)] shadow-sm overflow-hidden"
            open
          >
            <summary className="list-none cursor-pointer px-4 py-3.5 font-semibold flex items-center justify-between text-[14px]">
              <span>Worst-Cell Contract Contributors</span>
              <span className="text-[11px] opacity-60">Open / close</span>
            </summary>
            <div className="px-4 pb-4 overflow-auto">
              <table className="min-w-[760px] w-full text-[13px] text-[var(--text-color)]">
                <thead>
                  <tr className="border-b border-[var(--border-color)] bg-[var(--table-header-bg,var(--card-bg))]">
                    <th className="text-left p-3 font-semibold text-[var(--text-color)]">
                      Contract
                    </th>
                    <th className="text-left p-3 font-semibold text-[var(--text-color)]">
                      Symbol
                    </th>
                    <th className="text-right p-3 font-semibold text-[var(--text-color)]">
                      Expiration
                    </th>
                    <th className="text-right p-3 font-semibold text-[var(--text-color)]">
                      Scenario
                    </th>
                    <th className="text-right p-3 font-semibold text-[var(--text-color)]">
                      ΔP&L
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {worstContracts.map((row, idx) => (
                    <tr
                      key={`${row.contract_id}-${row.expiration}-${row.spot}-${row.vol}`}
                      className={`border-b border-[var(--border-color)] ${
                        idx % 2 === 0
                          ? "bg-transparent"
                          : "bg-[var(--table-row-alt-bg,rgba(255,255,255,0.02))]"
                      }`}
                    >
                      <td className="p-3 text-[var(--text-color)]">{row.contract_id}</td>
                      <td className="p-3 text-[var(--text-color)]">{row.symbol || "—"}</td>
                      <td className="p-3 text-right text-[var(--text-color)]">
                        {row.expiration}
                      </td>
                      <td className="p-3 text-right text-[var(--text-color)]">
                        {`${row.spot > 0 ? "+" : ""}${row.spot}% / ${
                          axisMode === "vix"
                            ? `VIX ${row.vol}`
                            : `${row.vol > 0 ? "+" : ""}${row.vol}`
                        }`}
                      </td>
                      <td className="p-3 text-right text-[var(--text-color)]">
                        {fmtUSD(row.pnl_usd)}
                      </td>
                    </tr>
                  ))}
                  {!worstContracts.length && (
                    <tr>
                      <td
                        className="p-3 opacity-60 text-[var(--text-color)]"
                        colSpan={5}
                      >
                        No contract-level detail available.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </details>
        )}
        </section>

        <RiskMethodologyModal
          open={methodologyOpen}
          onClose={() => setMethodologyOpen(false)}
        />
      </div>
    </Layout>
  );
};

export default RiskDashboard;